import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { useAdminPanel } from '../../contexts/AdminPanelContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { MobileSectionMenu } from '../../components/shared/MobileSectionMenu';
import { PaginationFooter, usePersistedPageSize } from '../../components/shared/PaginationFooter';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { NumberInput } from '../../components/ui/number-input';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { ScrollArea } from '../../components/ui/scroll-area';
import { SyncedTableScroll } from '../../components/admin/SyncedTableScroll';
import { toast } from 'sonner';
import { formatDT, formatDateOnly, getFileTimestamp } from '../../utils/timezone';
import {
  Settings,
  MessageSquare,
  Mail,
  CreditCard,
  Map,
  Key,
  Fuel,
  Smartphone,
  DollarSign,
  Wrench,
  Database,
  Shield,
  Loader2,
  Save,
  Eye,
  EyeOff,
  Plus,
  Trash2,
  Pencil,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  X,
  Download,
  Upload,
  Search,
  Copy,
  MapPin,
  Clock,
  CalendarIcon,
  XCircle,
  Send,
  Wifi,
  Zap,
  Gauge,
  Globe,
  History,
} from 'lucide-react';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { TimezoneSelector } from '../../components/TimezoneSelector';
import { SystemHealth } from './SystemHealth';

// Normalise FastAPI/Pydantic error payloads to a plain string — rendering the raw
// detail object in a toast crashes React (minified error #31 / blank screen).
const getApiErrorMessage = (error) => {
  const d = error?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map((x) => (x && x.msg ? `${(x.loc || []).slice(1).join('.')}: ${x.msg}` : String(x))).join('; ');
  }
  if (d && typeof d === 'object') return d.msg || JSON.stringify(d);
  return null;
};

const simSyncTimeAgo = (iso) => {
  if (!iso) return null;
  const secs = (Date.now() - new Date(iso).getTime()) / 1000;
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)} min ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)} h ago`;
  return `${Math.floor(secs / 86400)} d ago`;
};

// Single source of truth for a provider's most recent sync timestamp — used by BOTH
// the "Synced X ago" chip and the date line under it so they can never disagree.
const simLastSyncISO = (p) => [p.usage_last_synced, p.api_last_sync].filter(Boolean).sort().pop() || null;

const SimSyncStatusChip = ({ provider }) => {
  if (provider.provider_type === 'manual' || !provider.api_enabled) {
    return <Badge variant="outline" className="text-[10px] text-muted-foreground" data-testid={`sync-chip-${provider.id}`}>Manual — no sync</Badge>;
  }
  if (provider.usage_sync_status === 'in_progress') {
    return (
      <Badge className="text-[10px] gap-1 bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300 hover:bg-amber-100" data-testid={`sync-chip-${provider.id}`}>
        <Loader2 className="w-2.5 h-2.5 animate-spin" />
        Sync in progress…
      </Badge>
    );
  }
  const last = simLastSyncISO(provider);
  const ago = simSyncTimeAgo(last);
  if (provider.usage_sync_status === 'failed') {
    return (
      <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300 hover:bg-red-100" title={provider.usage_sync_error || ''} data-testid={`sync-chip-${provider.id}`}>
        Sync failed{ago ? ` — last synced ${ago}` : ''}
      </Badge>
    );
  }
  if (!last) {
    return <Badge variant="outline" className="text-[10px] text-muted-foreground" data-testid={`sync-chip-${provider.id}`}>Never synced</Badge>;
  }
  const fresh = Date.now() - new Date(last).getTime() < 26 * 3600 * 1000;
  return (
    <Badge
      className={`text-[10px] ${fresh ? 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300 hover:bg-green-100' : 'bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-200'}`}
      data-testid={`sync-chip-${provider.id}`}
    >
      Synced {ago}
    </Badge>
  );
};

export const GlobalSettings = () => {
  const { api } = useAuth();
  const { isSubpageEnabled } = useFeatureFlags();
  const { persistedSystemSettingsTab, setPersistedSystemSettingsTab } = useAdminPanel();

  // Safe render: converts objects to strings to prevent "Objects are not valid as React child" errors
  // 1NCE API returns nested objects like {id, description} for status, product, etc.
  const safe = (val) => {
    if (val === null || val === undefined) return '-';
    if (typeof val === 'object' && !Array.isArray(val)) return val.description || val.name || val.label || JSON.stringify(val);
    return val;
  };

  // Deep sanitize: flatten all nested objects in a data record
  const sanitizeRecord = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    const result = {};
    for (const [k, v] of Object.entries(obj)) {
      if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
        result[k] = v.description || v.name || v.label || v.text || v.value || JSON.stringify(v);
      } else if (Array.isArray(v)) {
        result[k] = v.map(item => (item !== null && typeof item === 'object') ? sanitizeRecord(item) : item);
      } else {
        result[k] = v;
      }
    }
    return result;
  };
  
  // Check which subpages are enabled
  const hasCommsProviders = isSubpageEnabled('global_comms_providers') !== false;
  const hasBillingPlans = isSubpageEnabled('global_billing_plans') !== false;
  const hasMapsGeocoding = isSubpageEnabled('global_maps_geocoding') !== false;
  const hasLocalization = isSubpageEnabled('global_localization') !== false;
  const hasApiWebhooks = isSubpageEnabled('global_api_webhooks') !== false;
  const hasFuelCards = isSubpageEnabled('global_fuel_cards') !== false;
  const hasSimProvider = isSubpageEnabled('global_sim_provider') !== false;
  const hasAccounting = isSubpageEnabled('global_accounting') !== false;
  const hasMaintenance = isSubpageEnabled('global_maintenance') !== false;
  const hasDataRetention = isSubpageEnabled('global_data_retention') !== false;
  const hasSecurity = isSubpageEnabled('global_security') !== false;
  
  const getDefaultTab = () => {
    if (hasCommsProviders) return 'comms';
    if (hasBillingPlans) return 'billing';
    if (hasMapsGeocoding) return 'maps';
    return 'comms';
  };

  // Allowed-tab map used for both initial hydration and feature-gate enforcement.
  const allowedSystemTabs = {
    comms: hasCommsProviders,
    billing: hasBillingPlans,
    maps: hasMapsGeocoding,
    api: hasApiWebhooks,
    fuel: hasFuelCards,
    sim: hasSimProvider,
    accounting: hasAccounting,
    maintenance: hasMaintenance,
    retention: hasDataRetention,
    security: hasSecurity,
  };

  const [activeTab, setActiveTab] = useState(() => {
    // Deep-link support (e.g. SIM-removal alert links to ?tab=system&section=sim).
    const urlSection = new URLSearchParams(window.location.search).get('section');
    if (urlSection && allowedSystemTabs[urlSection]) {
      return urlSection;
    }
    // Restore persisted nested tab if still enabled, otherwise fall back to default.
    if (persistedSystemSettingsTab && allowedSystemTabs[persistedSystemSettingsTab]) {
      return persistedSystemSettingsTab;
    }
    return getDefaultTab();
  });

  // Mirror activeTab back into persistence whenever it changes (and is allowed).
  useEffect(() => {
    if (activeTab && activeTab !== persistedSystemSettingsTab) {
      setPersistedSystemSettingsTab(activeTab);
    }
  }, [activeTab, persistedSystemSettingsTab, setPersistedSystemSettingsTab]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Communications Providers state
  const [smsProvider, setSmsProvider] = useState({
    primary_provider: 'twilio',
    account_sid: '',
    auth_token: '',
    from_number: '',
    api_key: '',
    sender_id: '',
    fallback_enabled: false,
    fallback_provider: '',
  });
  
  const [emailProvider, setEmailProvider] = useState({
    provider: 'sendgrid',
    api_key: '',
    sender_email: '',
    sender_name: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
  });

  // Account Invitation reminder settings
  const [inviteReminder, setInviteReminder] = useState({ enabled: true, days: 3, max_reminders: 2 });
  const [inviteReminderSaving, setInviteReminderSaving] = useState(false);
  const [runningReminders, setRunningReminders] = useState(false);

  // Billing & Plans state
  const [plans, setPlans] = useState([]);
  
  // OSRM Admin state
  const [osrmStatus, setOsrmStatus] = useState(null);
  const [osrmConfig, setOsrmConfig] = useState(null);
  const [osrmLoading, setOsrmLoading] = useState(false);
  const [osrmSaving, setOsrmSaving] = useState(false);
  const [osrmAddOpen, setOsrmAddOpen] = useState(false);
  const [osrmSetupCommands, setOsrmSetupCommands] = useState('');
  const [osrmShowCommands, setOsrmShowCommands] = useState(false);

  // Overpass Admin state
  const [overpassStatus, setOverpassStatus] = useState(null);
  const [overpassConfig, setOverpassConfig] = useState(null);
  const [overpassLoading, setOverpassLoading] = useState(false);
  const [overpassSaving, setOverpassSaving] = useState(false);

  // Speed Limit Test Tool state
  const [testLat, setTestLat] = useState('');
  const [testLng, setTestLng] = useState('');
  const [testAddressValue, setTestAddressValue] = useState('');
  const [testResults, setTestResults] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  // Overpass update commands
  const [overpassSetupCommands, setOverpassSetupCommands] = useState('');
  const [overpassShowCommands, setOverpassShowCommands] = useState(false);

  // Nominatim state
  const [nominatimStatus, setNominatimStatus] = useState(null);
  const [nominatimConfig, setNominatimConfig] = useState(null);
  const [nominatimLoading, setNominatimLoading] = useState(false);
  const [nominatimSaving, setNominatimSaving] = useState(false);
  const [nominatimAddOpen, setNominatimAddOpen] = useState(false);
  const [nominatimSetupCommands, setNominatimSetupCommands] = useState('');
  const [nominatimShowCommands, setNominatimShowCommands] = useState(false);
  const [nominatimTestQuery, setNominatimTestQuery] = useState('');
  const [nominatimTestResults, setNominatimTestResults] = useState(null);
  const [nominatimTestLoading, setNominatimTestLoading] = useState(false);
  const [nominatimTestMode, setNominatimTestMode] = useState('address');

  // OSRM Speed Profile Overrides state
  const [osrmOverrides, setOsrmOverrides] = useState(null);
  const [osrmOverridesSaving, setOsrmOverridesSaving] = useState(false);
  const [overrideCountry, setOverrideCountry] = useState('_default');
  const [addCountryOpen, setAddCountryOpen] = useState(false);
  const [addCountryName, setAddCountryName] = useState('');

  // Speed Limit Refresh Mode state
  const [speedLimitRefreshConfig, setSpeedLimitRefreshConfig] = useState(null);
  const [speedLimitRefreshSaving, setSpeedLimitRefreshSaving] = useState(false);

  // Localization state
  // API & Webhooks state
  const [apiConfig, setApiConfig] = useState({
    rate_limit_per_tenant: 1000,
    rate_limit_per_key: 100,
    enable_request_logging: true,
    api_version: 'v1',
  });

  // Fuel Cards state
  const [fuelCardProviders, setFuelCardProviders] = useState([]);

  // SIM Provider state (old - for API integration)
  const [simProvider, setSimProvider] = useState({
    provider: '',
    api_endpoint: '',
    api_key: '',
    auto_sync_enabled: false,
  });
  
  // SIM Providers List state (new - for provider management)
  const [simProviders, setSimProviders] = useState([]);
  const [simProviderSearch, setSimProviderSearch] = useState('');
  const [isAddSimProviderOpen, setIsAddSimProviderOpen] = useState(false);
  const [isEditSimProviderOpen, setIsEditSimProviderOpen] = useState(false);
  const [selectedSimProvider, setSelectedSimProvider] = useState(null);
  const [simProviderForm, setSimProviderForm] = useState({
    name: '',
    description: '',
    contact_email: '',
    contact_phone: '',
    is_active: true,
    provider_type: '',
    // API Configuration
    api_enabled: false,
    api_endpoint: '',
    api_key: '',
    api_auth_type: 'bearer',
    auto_sync_enabled: false,
    // 1NCE SMS Source Address
    sms_source_address: '',
    // 1NCE Data Streamer
    data_streamer_enabled: false,
    data_streamer_username: '',
    data_streamer_password: '',
    // Sync Schedule
    usage_sync_enabled: false,
    usage_sync_frequency: 'daily',
    usage_sync_time: '00:00',
    // Usage Spike Alerts
    spike_alert_enabled: false,
    spike_mode: 'fixed',
    spike_threshold: 5,
    // Prepaid Expiry Warnings
    expiry_warning_enabled: false,
    expiry_warn_days_1: 30,
    expiry_warn_days_2: 7,
  });
  const [activeSimProviderTab, setActiveSimProviderTab] = useState('details');
  const [dataStreamerEvents, setDataStreamerEvents] = useState([]);
  const [loadingStreamerEvents, setLoadingStreamerEvents] = useState(false);
  const [streamerDiagnostics, setStreamerDiagnostics] = useState(null);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [locatingSimCard, setLocatingSimCard] = useState(false);
  const [connectivityData, setConnectivityData] = useState(null);
  const [loadingConnectivity, setLoadingConnectivity] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  
  // SIM Cards state
  const [simCards, setSimCards] = useState([]);
  const [loadingSimCards, setLoadingSimCards] = useState(false);
  const [isAddSimCardOpen, setIsAddSimCardOpen] = useState(false);
  const [editingSimCard, setEditingSimCard] = useState(null);
  // Snapshot of the SIM card form taken when the dialog opens — unsaved-changes
  // warning compares actual form data instead of DOM input events.
  const simCardSnapshotRef = useRef('');
  const [showSimCardUnsavedWarning, setShowSimCardUnsavedWarning] = useState(false);
  const [isViewSimCardsOpen, setIsViewSimCardsOpen] = useState(false);
  const [selectedProviderForSimCards, setSelectedProviderForSimCards] = useState(null);
  const [simCardSearchTerm, setSimCardSearchTerm] = useState('');
  const [simCardStatusFilter, setSimCardStatusFilter] = useState('');
  const [selectedSimCardIds, setSelectedSimCardIds] = useState([]);  // For bulk export
  const [sortBy, setSortBy] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // SIM Detail Dialog state
  const [isSimDetailOpen, setIsSimDetailOpen] = useState(false);
  const [selectedSim, setSelectedSim] = useState(null);
  const [simDetailLoading, setSimDetailLoading] = useState(false);
  const [simDetailData, setSimDetailData] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [tempStatus, setTempStatus] = useState(''); // Temporary status until Save clicked
  
  // Import ICCIDs state
  const [isImportICCIDsOpen, setIsImportICCIDsOpen] = useState(false);
  const [importICCIDText, setImportICCIDText] = useState('');
  const [importStep, setImportStep] = useState('input'); // 'input' | 'preview' | 'importing' | 'done'
  const [importStats, setImportStats] = useState(null);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, synced: 0, failed: 0 });

  // Scheduled changes state
  const [scheduledChanges, setScheduledChanges] = useState([]);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [scheduleStatus, setScheduleStatus] = useState('');
  
  // Update limits state
  const [newExpiryDate, setNewExpiryDate] = useState(null);
  const [newDataLimit, setNewDataLimit] = useState('');

  // SMS state
  const [smsMessage, setSmsMessage] = useState('');
  const [simDetailDirty, setSimDetailDirty] = useState(false);
  const [showSimUnsavedWarning, setShowSimUnsavedWarning] = useState(false);
  const [smsHistory, setSmsHistory] = useState([]);
  const [sendingSms, setSendingSms] = useState(false);
  const [showAllSms, setShowAllSms] = useState(false);
  const [totalSmsCount, setTotalSmsCount] = useState({});
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [simTotal, setSimTotal] = useState(0);
  
  const [simCardForm, setSimCardForm] = useState({
    iccid: '',
    mobile_number: '',
    imsi: '',
    imei: '',
    status: 'activated',
    plan_name: '',
    data_limit_mb: 0,
    billing_cycle: 'monthly',
    cost: '',
    notes: '',
  });
  useEffect(() => {
    if (isAddSimCardOpen) simCardSnapshotRef.current = JSON.stringify(simCardForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddSimCardOpen]);
  const simCardFormHasChanges = () => JSON.stringify(simCardForm) !== simCardSnapshotRef.current;

  // Accounting state
  const [accountingConfig, setAccountingConfig] = useState({
    provider: '',
    api_key: '',
    sync_enabled: false,
  });

  // Maintenance state
  const [maintenanceMode, setMaintenanceMode] = useState({
    enabled: false,
    message: '',
    allow_admin_access: true,
  });

  // Polling configuration state
  const [pollingConfig, setPollingConfig] = useState({
    enabled: false,
    interval_seconds: 60,
  });
  const [savingPolling, setSavingPolling] = useState(false);

  // Data Retention state
  // Session audit (Option A) — configurable inactivity window
  const [sessionAudit, setSessionAudit] = useState({ inactivity_minutes: 30 });
  const [sessionAuditSaving, setSessionAuditSaving] = useState(false);
  useEffect(() => {
    api.get('/admin/global-settings/session-audit')
      .then((res) => setSessionAudit((prev) => ({ ...prev, ...res.data })))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleSaveSessionAudit = async () => {
    const mins = Number(sessionAudit.inactivity_minutes);
    if (!Number.isFinite(mins) || mins < 15 || mins > 120) {
      toast.error('Inactivity window must be between 15 and 120 minutes');
      return;
    }
    setSessionAuditSaving(true);
    try {
      const res = await api.put('/admin/global-settings/session-audit', { inactivity_minutes: mins });
      setSessionAudit(res.data);
      toast.success('Session audit settings saved');
    } catch (e) {
      toast.error(getApiErrorMessage(e) || 'Failed to save session audit settings');
    } finally {
      setSessionAuditSaving(false);
    }
  };

  // Audit Alerts — instant security alerts for sensitive account actions
  const AUDIT_ALERT_ROWS = [
    { id: 'password_changed', label: 'Password Changed', hint: 'Any account password change' },
    { id: 'impersonation_started', label: 'Account Impersonated', hint: 'An admin uses Login As on an account' },
    { id: 'role_changed', label: 'Role Changed', hint: 'An account role is modified' },
    { id: 'user_blocked', label: 'User Blocked / Unblocked', hint: 'An account is blocked or re-activated' },
    { id: 'suspicious_login', label: 'Suspicious Login', hint: 'A sign-in from a new IP address, new device, or outside a user\u2019s home region (geofence)' },
    { id: 'sim_removal', label: 'SIM Cards Removed by Sync', hint: 'A SIM provider sync deletes SIMs no longer in the provider account (e.g. transferred)', noAffected: true },
    { id: 'sim_usage_spike', label: 'SIM Usage Spike', hint: 'A SIM exceeds its daily usage threshold (configured per provider in the SIM Provider edit dialog)', noAffected: true },
    { id: 'sim_expiry', label: 'SIM Plan Expiry', hint: 'A prepaid SIM is approaching its plan expiry date (lead times configured per provider)', noAffected: true },
  ];
  const [auditAlerts, setAuditAlerts] = useState({ enabled: false, four_eyes: false, types: {} });
  const [auditAlertsSaving, setAuditAlertsSaving] = useState(false);
  useEffect(() => {
    api.get('/admin/global-settings/audit-alerts')
      .then((res) => setAuditAlerts(res.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleSaveAuditAlerts = async () => {
    setAuditAlertsSaving(true);
    try {
      const res = await api.put('/admin/global-settings/audit-alerts', auditAlerts);
      setAuditAlerts(res.data);
      toast.success('Audit alert settings saved');
    } catch (e) {
      toast.error(getApiErrorMessage(e) || 'Failed to save audit alert settings');
    } finally {
      setAuditAlertsSaving(false);
    }
  };

  // Audit archive (compliance) settings — saved separately from retention days
  const [auditArchive, setAuditArchive] = useState({ enabled: false, formats: ['csv', 'json'], retention_years: 0 });
  const [auditArchiveSaving, setAuditArchiveSaving] = useState(false);
  useEffect(() => {
    api.get('/admin/global-settings/audit-archive')
      .then((res) => setAuditArchive((prev) => ({ ...prev, ...res.data })))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleSaveAuditArchive = async () => {
    setAuditArchiveSaving(true);
    try {
      const res = await api.put('/admin/global-settings/audit-archive', auditArchive);
      setAuditArchive(res.data);
      toast.success('Audit archive settings saved');
    } catch (e) {
      toast.error(getApiErrorMessage(e) || 'Failed to save audit archive settings');
    } finally {
      setAuditArchiveSaving(false);
    }
  };

  const [retentionPolicies, setRetentionPolicies] = useState({
    operational_history_days: 365,
    video_metadata_days: 90,
    audit_logs_days: 730,
    chat_messages_days: 90,
    webhook_logs_days: 90,
    idle_session_days: 30,
  });

  // Security state
  const [securityConfig, setSecurityConfig] = useState({
    mfa_enforced: false,
    session_timeout: 480,
    password_min_length: 8,
    ip_whitelist_enabled: false,
  });
  const [pendingGlobalAction, setPendingGlobalAction] = useState(null); // { title, message, action, confirmLabel, variant }

  // TCP Server Security state
  const [tcpSecurityData, setTcpSecurityData] = useState(null);
  const [blacklistedIps, setBlacklistedIps] = useState({});
  const [trustedIps, setTrustedIps] = useState([]);
  const [trustedIpsPage, setTrustedIpsPage] = useState(1);
  const [trustedIpsTotal, setTrustedIpsTotal] = useState(0);
  const [trustedIpsPageSize, setTrustedIpsPageSize] = usePersistedPageSize('trusted-ips', 25);
  const [trustedIpCount, setTrustedIpCount] = useState(0);
  const [securityEvents, setSecurityEvents] = useState([]);
  const [unregisteredDevices, setUnregisteredDevices] = useState([]);
  const [loadingTcpSecurity, setLoadingTcpSecurity] = useState(false);

  const fetchTcpSecurity = useCallback(async (ipsPage = trustedIpsPage, ipsSize = trustedIpsPageSize) => {
    setLoadingTcpSecurity(true);
    try {
      const [secRes, blRes, wlRes, evRes, unregRes] = await Promise.all([
        api.get('/parsing/security'),
        api.get('/parsing/security/blacklist'),
        api.get(`/parsing/security/whitelist?skip=${(ipsPage - 1) * ipsSize}&limit=${ipsSize}`),
        api.get('/parsing/security/events?limit=20'),
        api.get('/parsing/security/unregistered-devices'),
      ]);
      setTcpSecurityData(secRes.data.security);
      setTrustedIpCount(secRes.data.trusted_ip_count || 0);
      setTrustedIps(wlRes.data.trusted_ips || []);
      setTrustedIpsTotal(wlRes.data.total || 0);
      setBlacklistedIps(blRes.data.blacklisted || {});
      setSecurityEvents(evRes.data.events || []);
      setUnregisteredDevices(unregRes.data.devices || []);
    } catch (e) {
      console.error('Failed to fetch TCP security data:', e);
    } finally {
      setLoadingTcpSecurity(false);
    }
  }, [api, trustedIpsPage, trustedIpsPageSize]);

  // Server-side pagination for the Trusted IPs table (sorted by last_seen desc on the server).
  const fetchTrustedIpsPage = useCallback(async (ipsPage, ipsSize) => {
    try {
      const res = await api.get(`/parsing/security/whitelist?skip=${(ipsPage - 1) * ipsSize}&limit=${ipsSize}`);
      setTrustedIps(res.data.trusted_ips || []);
      setTrustedIpsTotal(res.data.total || 0);
    } catch (e) {
      console.error('Failed to fetch trusted IPs page:', e);
    }
  }, [api]);

  useEffect(() => {
    fetchTrustedIpsPage(trustedIpsPage, trustedIpsPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trustedIpsPage, trustedIpsPageSize]);

  const [manualTrustIp, setManualTrustIp] = useState('');
  const [manualTrustNote, setManualTrustNote] = useState('');

  const handleClearBlacklistIp = async (ip) => {
    try {
      await api.delete(`/parsing/security/blacklist/${encodeURIComponent(ip)}`);
      toast.success(`Blacklist cleared for ${ip}`);
      fetchTcpSecurity();
    } catch (e) {
      toast.error(getApiErrorMessage(e) || 'Failed to clear blacklist');
    }
  };

  const handleClearAllBlacklists = async () => {
    try {
      await api.delete('/parsing/security/blacklist');
      toast.success('All blacklists cleared');
      fetchTcpSecurity();
    } catch (e) {
      toast.error('Failed to clear blacklists');
    }
  };

  const handleManualTrustIp = async () => {
    if (!manualTrustIp.trim()) { toast.error('Please enter an IP address'); return; }
    try {
      await api.post('/parsing/security/whitelist', { ip: manualTrustIp.trim(), note: manualTrustNote.trim() || null });
      toast.success(`IP ${manualTrustIp.trim()} added to trusted list`);
      setManualTrustIp('');
      setManualTrustNote('');
      fetchTcpSecurity();
    } catch (e) {
      toast.error(getApiErrorMessage(e) || 'Failed to add trusted IP');
    }
  };

  const handleRemoveTrustedIp = async (ip) => {
    setPendingGlobalAction({
      title: 'Remove Trusted IP',
      message: `Remove ${ip} from the trusted list? This IP will be subject to rate limiting again.`,
      confirmLabel: 'Remove',
      variant: 'destructive',
      action: async () => {
        try {
          await api.delete(`/parsing/security/whitelist/${encodeURIComponent(ip)}`);
          toast.success(`IP ${ip} removed from trusted list`);
          fetchTcpSecurity();
        } catch (e) {
          toast.error(getApiErrorMessage(e) || 'Failed to remove trusted IP');
        }
      },
    });
  };

  // Mock data
  const mockPlans = [
    {
      id: '1',
      name: 'Basic Plan',
      max_devices: 10,
      max_storage_gb: 50,
      history_days: 30,
      features: ['dashboard', 'live_tracking', 'basic_reports'],
      price: 49,
    },
    {
      id: '2',
      name: 'Professional Plan',
      max_devices: 50,
      max_storage_gb: 200,
      history_days: 90,
      features: ['dashboard', 'live_tracking', 'analytics', 'geofencing', 'video'],
      price: 149,
    },
    {
      id: '3',
      name: 'Enterprise Plan',
      max_devices: -1,
      max_storage_gb: -1,
      history_days: 365,
      features: ['all'],
      price: 499,
    },
  ];

  const mockFuelCardProviders = [
    { id: '1', name: 'BP Fuel Cards', status: 'active', last_sync: '2026-01-21 12:00' },
    { id: '2', name: 'Shell Fleet', status: 'active', last_sync: '2026-01-21 11:30' },
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all global settings
      const responses = await Promise.all([
        api.get('/admin/commands/communications/sms').catch(() => ({ data: null })),
        api.get('/admin/commands/communications/email').catch(() => ({ data: null })),
        api.get('/admin/global-settings/data-retention').catch(() => ({ data: retentionPolicies })),
        api.get('/admin/sim-providers').catch(() => ({ data: [] })),
      ]);
      
      // Load SMS settings from API
      if (responses[0]?.data) {
        setSmsProvider(prev => ({ ...prev, ...responses[0].data }));
      }
      // Load Email settings from API
      if (responses[1]?.data) {
        setEmailProvider(prev => ({ ...prev, ...responses[1].data }));
      }
      
      // Billing plans have no backend endpoint — static fallback only (avoids 404 noise)
      setPlans(mockPlans);
      setFuelCardProviders(mockFuelCardProviders);
      
      // Load retention policies from API
      if (responses[2]?.data) {
        setRetentionPolicies(responses[2].data);
      }
      
      // Load SIM providers from API
      if (responses[3]?.data) {
        setSimProviders((responses[3].data || []).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      }
    } catch (error) {
      console.error('Failed to fetch global settings:', error);
      setPlans(mockPlans);
      setFuelCardProviders(mockFuelCardProviders);
    } finally {
      setLoading(false);
    }
  // mockPlans/mockFuelCardProviders are static in-component fallback data and
  // retentionPolicies is read only as the fallback default for the data-retention
  // request — intentionally NOT dependencies: this is a mount loader, and re-running
  // it whenever retentionPolicies updates would loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load TCP security data when security tab is active
  useEffect(() => {
    if (activeTab === 'security') {
      fetchTcpSecurity();
    }
  }, [activeTab, fetchTcpSecurity]);

  // Load polling config when maintenance tab is active
  useEffect(() => {
    if (activeTab === 'maintenance') {
      api.get('/admin/global-settings/polling')
        .then(res => setPollingConfig(res.data))
        .catch(() => {});
    }
  }, [activeTab, api]);


  const handleSaveSmsSettings = async () => {
    setSaving(true);
    try {
      await api.put('/admin/commands/communications/sms', smsProvider);
      toast.success('SMS provider settings saved');
    } catch (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to save SMS settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEmailSettings = async () => {
    setSaving(true);
    try {
      await api.put('/admin/commands/communications/email', emailProvider);
      toast.success('Email provider settings saved');
    } catch (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    api.get('/admin/invite-reminder-settings')
      .then(res => setInviteReminder(prev => ({ ...prev, ...res.data })))
      .catch(() => {});
  }, [api]);

  const handleSaveInviteReminder = async () => {
    setInviteReminderSaving(true);
    try {
      const res = await api.put('/admin/invite-reminder-settings', {
        enabled: !!inviteReminder.enabled,
        days: Number(inviteReminder.days) || 3,
        max_reminders: Number(inviteReminder.max_reminders) || 0,
      });
      setInviteReminder(prev => ({ ...prev, ...res.data }));
      toast.success('Invitation reminder settings saved');
    } catch (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to save invitation reminder settings');
    } finally {
      setInviteReminderSaving(false);
    }
  };

  const handleRunRemindersNow = async () => {
    setRunningReminders(true);
    try {
      const res = await api.post('/admin/invite-reminders/run-now');
      const n = res.data?.reminders_sent ?? 0;
      toast.success(n > 0 ? `${n} reminder${n === 1 ? '' : 's'} sent` : 'No reminders were due right now');
    } catch (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to run reminders');
    } finally {
      setRunningReminders(false);
    }
  };

  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  const [emailPreviewSubject, setEmailPreviewSubject] = useState('');
  const [emailPreviewFrom, setEmailPreviewFrom] = useState('');
  
  const handleSendTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error('Please enter an email address to send the test to');
      return;
    }
    setSendingTestEmail(true);
    try {
      const response = await api.post('/admin/commands/communications/email/test', { test_email: testEmailAddress });
      toast.success(response.data.message || 'Test email sent successfully');
    } catch (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handlePreviewEmail = async () => {
    try {
      const response = await api.get('/admin/commands/communications/email/preview');
      setEmailPreviewHtml(response.data.html);
      setEmailPreviewSubject(response.data.subject);
      setEmailPreviewFrom(response.data.from);
      setEmailPreviewOpen(true);
    } catch (error) {
      toast.error('Failed to load email preview');
    }
  };

  // OSRM Admin functions
  const fetchOsrmData = useCallback(async () => {
    setOsrmLoading(true);
    try {
      const [statusRes, configRes] = await Promise.all([
        api.get('/admin/osrm/status').catch(() => ({ data: { status: 'error', message: 'Failed to fetch' } })),
        api.get('/admin/osrm/config').catch(() => ({ data: null })),
      ]);
      setOsrmStatus(statusRes.data);
      if (configRes.data) setOsrmConfig(configRes.data);
    } catch (e) {
      console.error('OSRM fetch error:', e);
    } finally {
      setOsrmLoading(false);
    }
  }, [api]);

  const handleSaveOsrmConfig = async () => {
    if (!osrmConfig) return;
    setOsrmSaving(true);
    try {
      const res = await api.put('/admin/osrm/config', {
        countries: osrmConfig.countries || [],
        update_schedule: osrmConfig.update_schedule,
        last_update: osrmConfig.last_update,
        notes: osrmConfig.notes || '',
      });
      if (res.data?.setup_commands) {
        setOsrmSetupCommands(res.data.setup_commands);
      }
      toast.success('OSRM configuration saved');
    } catch (e) {
      toast.error('Failed to save OSRM configuration');
    } finally {
      setOsrmSaving(false);
    }
  };

  const handleAddOsrmCountry = (continent, slug) => {
    if (!osrmConfig) return;
    const exists = osrmConfig.countries?.some(c => c.slug === slug);
    if (exists) { toast.error('Country already added'); return; }
    const displayName = slug.replace(/-/g, ' ').replace(/\bus\//g, 'US/').replace(/\b\w/g, c => c.toUpperCase());
    setOsrmConfig(prev => ({
      ...prev,
      countries: [...(prev.countries || []), { slug, continent, display_name: displayName }],
    }));
    setOsrmAddOpen(false);
  };

  const handleRemoveOsrmCountry = (slug) => {
    setOsrmConfig(prev => ({
      ...prev,
      countries: (prev.countries || []).filter(c => c.slug !== slug),
    }));
  };

  // Auto-load OSRM config when maps tab is active
  useEffect(() => {
    if (activeTab === 'maps' && !osrmConfig) {
      fetchOsrmData();
    }
  }, [activeTab, osrmConfig, fetchOsrmData]);

  // Overpass Admin functions
  const fetchOverpassData = useCallback(async () => {
    setOverpassLoading(true);
    try {
      const [statusRes, configRes] = await Promise.all([
        api.get('/admin/overpass/status').catch(() => ({ data: { status: 'error', message: 'Failed to fetch' } })),
        api.get('/admin/overpass/config').catch(() => ({ data: null })),
      ]);
      setOverpassStatus(statusRes.data);
      if (configRes.data) setOverpassConfig(configRes.data);
    } catch (e) {
      console.error('Overpass fetch error:', e);
    } finally {
      setOverpassLoading(false);
    }
  }, [api]);

  const handleSaveOverpassConfig = async () => {
    if (!overpassConfig) return;
    setOverpassSaving(true);
    try {
      const res = await api.put('/admin/overpass/config', {
        server_url: overpassConfig.server_url || '',
        is_self_hosted: overpassConfig.is_self_hosted || false,
        enabled: overpassConfig.enabled ?? true,
        update_schedule: overpassConfig.update_schedule || '',
        last_update: overpassConfig.last_update,
        notes: overpassConfig.notes || '',
      });
      if (res.data?.setup_commands) setOverpassSetupCommands(res.data.setup_commands);
      toast.success('Overpass configuration saved');
    } catch {
      toast.error('Failed to save Overpass configuration');
    } finally {
      setOverpassSaving(false);
    }
  };

  // Nominatim Admin functions
  const fetchNominatimData = useCallback(async () => {
    setNominatimLoading(true);
    try {
      const [statusRes, configRes] = await Promise.all([
        api.get('/admin/nominatim/status').catch(() => ({ data: { status: 'error', message: 'Failed to fetch' } })),
        api.get('/admin/nominatim/config').catch(() => ({ data: null })),
      ]);
      setNominatimStatus(statusRes.data);
      if (configRes.data) setNominatimConfig(configRes.data);
    } catch (e) {
      console.error('Nominatim fetch error:', e);
    } finally {
      setNominatimLoading(false);
    }
  }, [api]);

  const handleSaveNominatimConfig = async () => {
    if (!nominatimConfig) return;
    setNominatimSaving(true);
    try {
      const res = await api.put('/admin/nominatim/config', {
        server_url: nominatimConfig.server_url || 'http://localhost:8088',
        countries: nominatimConfig.countries || [],
        update_schedule: nominatimConfig.update_schedule,
        notes: nominatimConfig.notes || '',
      });
      if (res.data?.setup_commands) setNominatimSetupCommands(res.data.setup_commands);
      toast.success('Nominatim configuration saved');
    } catch {
      toast.error('Failed to save Nominatim configuration');
    } finally {
      setNominatimSaving(false);
    }
  };

  const handleAddNominatimCountry = (continent, slug) => {
    if (!nominatimConfig) return;
    const exists = nominatimConfig.countries?.some(c => c.slug === slug);
    if (exists) { toast.error('Country already added'); return; }
    setNominatimConfig(prev => ({
      ...prev,
      countries: [...(prev.countries || []), {
        slug,
        continent,
        display_name: slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      }],
    }));
    setNominatimAddOpen(false);
    toast.success(`Added ${slug} — save to generate update commands`);
  };

  const handleRemoveNominatimCountry = (slug) => {
    setNominatimConfig(prev => ({
      ...prev,
      countries: (prev.countries || []).filter(c => c.slug !== slug),
    }));
  };

  // Auto-load Nominatim config when maps tab is active
  useEffect(() => {
    if (activeTab === 'maps' && !nominatimConfig) {
      fetchNominatimData();
    }
  }, [activeTab, nominatimConfig, fetchNominatimData]);

  const handleNominatimTest = async () => {
    if (!nominatimTestQuery.trim()) return;
    setNominatimTestLoading(true);
    setNominatimTestResults(null);
    const q = nominatimTestQuery.trim();
    try {
      const serverUrl = nominatimConfig?.server_url || 'http://localhost:8088';
      let url;
      if (nominatimTestMode === 'reverse') {
        const parts = q.split(/[,\s]+/).filter(Boolean);
        if (parts.length < 2 || isNaN(parseFloat(parts[0])) || isNaN(parseFloat(parts[1]))) {
          toast.error('Enter valid coordinates: lat, lng (e.g. -33.8688, 151.2093)');
          setNominatimTestLoading(false);
          return;
        }
        url = `/admin/nominatim/test?mode=reverse&lat=${parts[0]}&lng=${parts[1]}`;
      } else {
        url = `/admin/nominatim/test?mode=search&q=${encodeURIComponent(q)}`;
      }
      const res = await api.get(url);
      setNominatimTestResults(res.data);
    } catch (err) {
      toast.error('Test failed — check Nominatim status');
      setNominatimTestResults({ error: err.message || 'Request failed' });
    } finally {
      setNominatimTestLoading(false);
    }
  };

  // Speed Limit Test Tool
  const handleTestSpeedLimit = async () => {
    if (!testLat || !testLng) { toast.error('Enter GPS coordinates'); return; }
    setTestLoading(true);
    setTestResults(null);
    try {
      const res = await api.post('/admin/test-speed-limit', { lat: parseFloat(testLat), lng: parseFloat(testLng) });
      setTestResults(res.data);
    } catch {
      toast.error('Test failed');
    } finally {
      setTestLoading(false);
    }
  };

  // Auto-load Overpass config when maps tab is active
  useEffect(() => {
    if (activeTab === 'maps' && !overpassConfig) fetchOverpassData();
    if (activeTab === 'maps' && !osrmOverrides) {
      api.get('/admin/osrm-speed-overrides').then(res => setOsrmOverrides(res.data)).catch(() => {});
    }
    if (activeTab === 'maps' && !speedLimitRefreshConfig) {
      api.get('/admin/speed-limit-refresh-config').then(res => setSpeedLimitRefreshConfig(res.data)).catch(() => {});
    }
  }, [activeTab, overpassConfig, osrmOverrides, speedLimitRefreshConfig, fetchOverpassData, api]);

  const handleSaveOsrmOverrides = async () => {
    if (!osrmOverrides) return;
    setOsrmOverridesSaving(true);
    try {
      await api.put('/admin/osrm-speed-overrides', { countries: osrmOverrides.countries || {}, enabled: osrmOverrides.enabled ?? true });
      toast.success('OSRM speed overrides saved');
    } catch {
      toast.error('Failed to save overrides');
    } finally {
      setOsrmOverridesSaving(false);
    }
  };

  const handleSaveSpeedLimitRefreshConfig = async () => {
    if (!speedLimitRefreshConfig) return;
    setSpeedLimitRefreshSaving(true);
    try {
      await api.put('/admin/speed-limit-refresh-config', {
        mode: speedLimitRefreshConfig.mode,
        timer_interval_seconds: speedLimitRefreshConfig.timer_interval_seconds,
      });
      toast.success('Speed limit refresh mode saved');
    } catch {
      toast.error('Failed to save refresh mode');
    } finally {
      setSpeedLimitRefreshSaving(false);
    }
  };

  const handleAddOverrideCountry = (name) => {
    if (!osrmOverrides || !name.trim()) return;
    const slug = name.trim().toLowerCase().replace(/\s+/g, '_');
    if (osrmOverrides.countries?.[slug]) { toast.error('Country already exists'); return; }
    // Copy defaults
    setOsrmOverrides(prev => ({
      ...prev,
      countries: { ...prev.countries, [slug]: { ...prev.countries._default } },
    }));
    setOverrideCountry(slug);
  };

  const handleSaveRetentionPolicies = async () => {
    setSaving(true);
    try {
      const res = await api.put('/admin/global-settings/data-retention', retentionPolicies);
      const sync = res.data?.s3_lifecycle_sync;
      if (sync?.status === 'updated') toast.success('Data retention saved — S3 bucket lifecycle updated');
      else if (sync?.status === 'in_sync') toast.success('Data retention saved — S3 bucket already in sync');
      else if (sync?.status === 'error') toast.warning('Retention saved, but S3 lifecycle update failed — check server logs');
      else toast.success('Data retention policies saved');
      try {
        const fresh = await api.get('/admin/global-settings/data-retention');
        if (fresh?.data) setRetentionPolicies(fresh.data);
      } catch { /* keep current state if refresh fails */ }
    } catch (error) {
      toast.error('Failed to save retention policies');
    } finally {
      setSaving(false);
    }
  };

  const renderPurgeNote = (cat, noun = 'records') => {
    const st = retentionPolicies?.purge_status?.[cat];
    if (!st || !st.at) return null;
    let when = st.at;
    try { when = formatDT(st.at, { seconds: true }); } catch { /* fallback to raw */ }
    return (
      <p className="text-xs text-emerald-600 dark:text-emerald-400" data-testid={`retention-purge-note-${cat}`}>
        Last cleanup removed {st.count} {noun} on {when}
      </p>
    );
  };

  const PURGE_CATEGORY_LABELS = {
    operational: 'Operational History',
    video: 'Video Metadata',
    audit: 'Audit Logs',
    webhook: 'Webhook Delivery Logs',
    chat: 'Chat Messages',
    sessions: 'Idle Sessions',
  };
  const [purgeHistoryOpen, setPurgeHistoryOpen] = useState(false);
  const [purgeHistory, setPurgeHistory] = useState([]);
  const [purgeHistoryLoading, setPurgeHistoryLoading] = useState(false);

  const openPurgeHistory = async () => {
    setPurgeHistoryOpen(true);
    setPurgeHistoryLoading(true);
    try {
      const res = await api.get('/admin/global-settings/data-retention/purge-history', { params: { limit: 100 } });
      setPurgeHistory(res.data?.items || []);
    } catch (error) {
      toast.error('Failed to load purge history');
      setPurgeHistory([]);
    } finally {
      setPurgeHistoryLoading(false);
    }
  };

  const handleSavePollingConfig = async () => {
    setSavingPolling(true);
    try {
      const res = await api.put('/admin/global-settings/polling', pollingConfig);
      setPollingConfig({ enabled: res.data.enabled, interval_seconds: res.data.interval_seconds });
      toast.success(`Polling ${res.data.enabled ? 'enabled' : 'disabled'} — interval: ${res.data.interval_seconds}s`);
    } catch (error) {
      toast.error('Failed to save polling settings');
    } finally {
      setSavingPolling(false);
    }
  };
  
  // Live refresh of SIM provider sync chips while the SIM tab is open
  useEffect(() => {
    if (activeTab !== 'sim') return;
    const tick = async () => {
      try {
        const r = await api.get('/admin/sim-providers');
        setSimProviders((r.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      } catch { /* keep last known list */ }
    };
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [activeTab, api]);

  // SIM Provider handlers
  const resetSimProviderForm = () => {
    setSimProviderForm({
      name: '',
      description: '',
      contact_email: '',
      contact_phone: '',
      account_number: '',
      is_active: true,
      provider_type: '',
      api_enabled: false,
      api_endpoint: '',
      api_key: '',
      api_auth_type: 'bearer',
      auto_sync_enabled: false,
      sms_source_address: '',
      data_streamer_enabled: false,
      data_streamer_username: '',
      data_streamer_password: '',
      usage_sync_enabled: true,
      usage_sync_frequency: 'daily',
      usage_sync_time: '00:00',
      usage_last_synced: null,
    });
    setSelectedSimProvider(null);
    setActiveSimProviderTab('details');
    setDataStreamerEvents([]);
  };
  
  // Validate + sanitise alert settings before sending (backend rejects invalid values with 422)
  const prepareSimProviderPayload = () => {
    const f = simProviderForm;
    if (f.spike_alert_enabled) {
      const t = Number(f.spike_threshold);
      if (!Number.isFinite(t) || t <= 0) {
        toast.error('Usage spike threshold must be a number greater than 0');
        return null;
      }
    }
    if (f.expiry_warning_enabled) {
      const d1 = Number(f.expiry_warn_days_1);
      const d2 = Number(f.expiry_warn_days_2);
      if (!Number.isInteger(d1) || d1 <= 0 || !Number.isInteger(d2) || d2 <= 0) {
        toast.error('Expiry warning days must be whole numbers greater than 0');
        return null;
      }
    }
    const num = (v, dflt) => {
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? n : dflt;
    };
    return {
      ...f,
      spike_mode: f.spike_mode === 'percent' ? 'percent' : 'fixed',
      spike_threshold: num(f.spike_threshold, 5),
      expiry_warn_days_1: Math.round(num(f.expiry_warn_days_1, 30)),
      expiry_warn_days_2: Math.round(num(f.expiry_warn_days_2, 7)),
    };
  };

  const handleAddSimProvider = async () => {
    if (!simProviderForm.name) {
      toast.error('Provider name is required');
      return;
    }
    const payload = prepareSimProviderPayload();
    if (!payload) return;
    
    setSaving(true);
    try {
      await api.post('/admin/sim-providers', payload);
      toast.success('SIM provider added successfully');
      fetchData();
      setIsAddSimProviderOpen(false);
      resetSimProviderForm();
    } catch (error) {
      console.error('Failed to add SIM provider:', error);
      toast.error(getApiErrorMessage(error) || 'Failed to add SIM provider');
    } finally {
      setSaving(false);
    }
  };
  
  const handleUpdateSimProvider = async () => {
    if (!selectedSimProvider) return;
    const payload = prepareSimProviderPayload();
    if (!payload) return;
    
    setSaving(true);
    try {
      await api.put(`/admin/sim-providers/${selectedSimProvider.id}`, payload);
      toast.success('SIM provider updated successfully');
      fetchData();
      setIsEditSimProviderOpen(false);
      resetSimProviderForm();
    } catch (error) {
      console.error('Failed to update SIM provider:', error);
      toast.error(getApiErrorMessage(error) || 'Failed to update SIM provider');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeleteSimProvider = async (providerId) => {
    setPendingGlobalAction({
      title: 'Delete SIM Provider?',
      message: 'Are you sure you want to delete this SIM provider? This action cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'destructive',
      action: async () => {
        try {
          await api.delete(`/admin/sim-providers/${providerId}`);
          toast.success('SIM provider deleted successfully');
          fetchData();
        } catch (error) {
          console.error('Failed to delete SIM provider:', error);
          toast.error('Failed to delete SIM provider');
        }
      },
    });
  };
  
  const openEditSimProvider = async (provider) => {
    setSelectedSimProvider(provider);
    setSimCards([]);
    setSimProviderForm({
      name: provider.name,
      description: provider.description || '',
      contact_email: provider.contact_email || '',
      contact_phone: provider.contact_phone || '',
      is_active: provider.is_active,
      provider_type: provider.provider_type || '',
      api_enabled: provider.api_enabled || false,
      api_endpoint: provider.api_endpoint || '',
      api_key: provider.api_key || '',
      api_auth_type: provider.api_auth_type || 'bearer',
      auto_sync_enabled: provider.auto_sync_enabled || false,
      sms_source_address: provider.sms_source_address || '',
      data_streamer_enabled: provider.data_streamer_enabled || false,
      data_streamer_username: provider.data_streamer_username || '',
      data_streamer_password: provider.data_streamer_password || '',
      usage_sync_enabled: provider.usage_sync_enabled ?? true,
      usage_sync_frequency: provider.usage_sync_frequency || 'daily',
      usage_sync_time: provider.usage_sync_time || '00:00',
      usage_last_synced: provider.usage_last_synced || null,
      account_number: provider.account_number || '',
      spike_alert_enabled: provider.spike_alert_enabled || false,
      spike_mode: provider.spike_mode || 'fixed',
      spike_threshold: provider.spike_threshold ?? 5,
      expiry_warning_enabled: provider.expiry_warning_enabled || false,
      expiry_warn_days_1: provider.expiry_warn_days_1 ?? 30,
      expiry_warn_days_2: provider.expiry_warn_days_2 ?? 7,
    });
    setActiveSimProviderTab('details');
    setIsEditSimProviderOpen(true);
  };
  
  // SIM Change Log (per-provider sync history: additions / removals / updates)
  const SIM_SYNC_LOGS_PAGE = 20;
  const [simSyncLogProvider, setSimSyncLogProvider] = useState(null);
  const [simSyncLogs, setSimSyncLogs] = useState([]);
  const [simSyncLogsTotal, setSimSyncLogsTotal] = useState(0);
  const [simSyncLogsSkip, setSimSyncLogsSkip] = useState(0);
  const [simSyncLogsLoading, setSimSyncLogsLoading] = useState(false);
  const [expandedSyncLogId, setExpandedSyncLogId] = useState(null);

  const fetchSimSyncLogs = async (providerId, skip = 0) => {
    setSimSyncLogsLoading(true);
    try {
      const res = await api.get(`/admin/sim-providers/${providerId}/sync-logs`, { params: { skip, limit: SIM_SYNC_LOGS_PAGE } });
      setSimSyncLogs(res.data.logs || []);
      setSimSyncLogsTotal(res.data.total || 0);
      setSimSyncLogsSkip(skip);
    } catch {
      toast.error('Failed to load SIM change log');
    } finally {
      setSimSyncLogsLoading(false);
    }
  };

  const openSimSyncLogs = (provider) => {
    setSimSyncLogProvider(provider);
    setExpandedSyncLogId(null);
    fetchSimSyncLogs(provider.id, 0);
  };

  const exportSimSyncLogsCsv = async () => {
    if (!simSyncLogProvider) return;
    try {
      const res = await api.get(`/admin/sim-providers/${simSyncLogProvider.id}/sync-logs/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `sim-change-log-${(simSyncLogProvider.name || 'provider').replace(/\s+/g, '-').toLowerCase()}-${getFileTimestamp()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('SIM change log exported');
    } catch {
      toast.error('Export failed');
    }
  };

  const openSimCardsView = async (provider) => {
    setSelectedProviderForSimCards(provider);
    setSimCardSearchTerm('');
    setSimCardStatusFilter('');
    setSelectedSimCardIds([]);
    setCurrentPage(1);
    setTotalSmsCount(0);
    await fetchSimCards(provider.id, '', '', 1);
    setIsViewSimCardsOpen(true);
    // Fetch total SMS count for Truphone
    if (provider.provider_type === 'truphone') {
      try {
        const smsRes = await api.get(`/admin/sim-providers/${provider.id}/sms-count`);
        setTotalSmsCount(smsRes.data || {});
      } catch { /* ignore */ }
    }
  };
  
  // Open SIM detail dialog and fetch complete data
  const handleSimDetailClose = (forceClose = false) => {
    if (!forceClose && simDetailDirty) {
      setShowSimUnsavedWarning(true);
      return;
    }
    setIsSimDetailOpen(false);
    setSmsMessage('');
    setSimDetailDirty(false);
    setShowSimUnsavedWarning(false);
    setConnectivityData(null);
    fetchSimCards(selectedProviderForSimCards?.id);
  };

  const openSimDetail = async (sim) => {
    setSelectedSim(sim);
    setTempStatus(sim.status?.toUpperCase() || 'INVENTORY');
    setIsSimDetailOpen(true);
    setSimDetailLoading(true);
    setScheduledChanges([]);
    setScheduleDate(null);
    setScheduleTime('09:00');
    setScheduleStatus('');
    setSmsHistory([]);
    setSmsMessage('');
    setSimDetailDirty(false);
    setShowAllSms(false);
    
    try {
      if (selectedProviderForSimCards?.provider_type === 'manual') {
        // Manual providers have no API — just use the stored data
        const simData = sanitizeRecord(sim);
        // Add account number from provider
        simData.account_id = selectedProviderForSimCards?.account_number || '';
        simData.provider_name = selectedProviderForSimCards?.name || '';
        setSimDetailData(simData);
        setTempStatus(simData.status?.toUpperCase() || 'INVENTORY');
      } else {
        // Load from local database instantly (Data Streamer + last sync keeps this updated)
        const response = await api.get(`/admin/sim-cards/${sim.id}/details`);
        const sanitized = sanitizeRecord(response.data);
        setSimDetailData(sanitized);
        setTempStatus(sanitized.status?.toUpperCase() || 'INVENTORY');
        fetchScheduledChanges(sim.id);
        fetchSmsHistory(sim.id);
      }
    } catch (error) {
      console.error('Failed to fetch SIM details:', error);
      toast.error('Failed to load complete SIM details');
      const fallback = sanitizeRecord(sim);
      fallback.account_id = selectedProviderForSimCards?.account_number || '';
      setSimDetailData(fallback);
    } finally {
      setSimDetailLoading(false);
    }
  };
  
  // Refresh SIM details from provider API (on-demand)
  const refreshSimDetail = async () => {
    if (!selectedSim) return;
    setSimDetailLoading(true);
    try {
      const response = await api.post(`/admin/sim-cards/${selectedSim.id}/fetch-details`);
      const sanitized = sanitizeRecord(response.data);
      setSimDetailData(sanitized);
      setTempStatus(sanitized.status?.toUpperCase() || 'INVENTORY');
      toast.success('Refreshed from provider API');
    } catch (error) {
      console.error('Failed to refresh details:', error);
      toast.error(getApiErrorMessage(error) || 'Failed to refresh from API');
    } finally {
      setSimDetailLoading(false);
    }
  };
  
  // Save status change
  const handleSaveStatusChange = async () => {
    if (!simDetailData || tempStatus === simDetailData.status?.toUpperCase()) {
      toast.info('No changes to save');
      return;
    }
    
    setUpdatingStatus(true);
    try {
      await api.put(`/admin/sim-cards/${simDetailData.id}/status`, null, {
        params: { new_status: tempStatus }
      });
      // Map dropdown value back to native stored format based on provider type
      const provType = selectedProviderForSimCards?.provider_type;
      let displayStatus = tempStatus.toLowerCase();
      if (provType === 'truphone') {
        const tpMap = {'ACTIVE': 'active', 'SUSPENDED': 'suspended', 'PRE-ACTIVE': 'pre-active'};
        displayStatus = tpMap[tempStatus] || tempStatus.toLowerCase();
      } else if (provType === '1nce') {
        const nceMap = {'ACTIVATED': 'activated', 'DEACTIVATED': 'deactivated'};
        displayStatus = nceMap[tempStatus] || tempStatus.toLowerCase();
      }
      toast.success(`Status updated to ${displayStatus}`);
      
      // Update local simDetailData status immediately
      setSimDetailData(prev => prev ? {...prev, status: displayStatus} : prev);
      setTempStatus(tempStatus);
      
      // Refresh SIM list (updates the list row) — don't refresh detail from API
      // 1NCE takes time to propagate, so trust the saved value
      setSimDetailDirty(false);
      fetchSimCards(selectedProviderForSimCards.id);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error(getApiErrorMessage(error) || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };
  
  // Refetch SIM cards when search, filter, or sort changes
  useEffect(() => {
    if (isViewSimCardsOpen && selectedProviderForSimCards) {
      const delayDebounce = setTimeout(() => {
        if (currentPage !== 1) {
          setCurrentPage(1); // page-change effect below fetches page 1
        } else {
          fetchSimCards(selectedProviderForSimCards.id);
        }
      }, 300);
      return () => clearTimeout(delayDebounce);
    }
  }, [simCardSearchTerm, simCardStatusFilter, sortBy, sortDirection]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Server-side pagination: fetch the requested page when page/pageSize changes
  useEffect(() => {
    if (isViewSimCardsOpen && selectedProviderForSimCards) {
      fetchSimCards(selectedProviderForSimCards.id);
    }
  }, [currentPage, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // SIM Card selection handlers (page-scoped: select/deselect current page, keep other pages' selections)
  const handleSelectAllSimCards = (checked) => {
    const pageIds = simCards.map(sim => sim.id);
    if (checked) {
      setSelectedSimCardIds(prev => [...new Set([...prev, ...pageIds])]);
    } else {
      const pageIdSet = new Set(pageIds);
      setSelectedSimCardIds(prev => prev.filter(id => !pageIdSet.has(id)));
    }
  };
  
  const handleSelectSimCard = (simId, checked) => {
    if (checked) {
      setSelectedSimCardIds(prev => [...prev, simId]);
    } else {
      setSelectedSimCardIds(prev => prev.filter(id => id !== simId));
    }
  };
  
  const clearSearch = () => {
    setSimCardSearchTerm('');
    setSimCardStatusFilter('');
  };
  
  // Copy to clipboard helper
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };
  
  const handleTestConnection = async (providerId) => {
    setTestingConnection(true);
    try {
      const response = await api.post(`/admin/sim-providers/${providerId}/test-connection`);
      toast.success(response.data.message || 'Connection test successful');
      fetchData(); // Refresh to get updated status
    } catch (error) {
      console.error('Failed to test connection:', error);
      toast.error(getApiErrorMessage(error) || 'Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };
  
  // SIM Card handlers — server-side pagination (page_size + skip, total via X-Total-Count)
  const fetchSimCards = async (providerId, overrideSearch, overrideStatus, overridePage) => {
    setLoadingSimCards(true);
    try {
      let url = `/admin/sim-providers/${providerId}/sim-cards?`;
      const search = overrideSearch !== undefined ? overrideSearch : simCardSearchTerm;
      const status = overrideStatus !== undefined ? overrideStatus : simCardStatusFilter;
      if (search) url += `search=${search}&`;
      if (status && status !== '__all__') url += `status=${status}&`;
      if (sortBy) url += `sort_by=${sortBy}&sort_direction=${sortDirection}&`;
      const page = overridePage || currentPage;
      url += `page_size=${pageSize}&skip=${(page - 1) * pageSize}&`;
      
      const response = await api.get(url);
      setSimCards((response.data || []).map(s => sanitizeRecord(s)));
      setSimTotal(parseInt(response.headers?.['x-total-count'], 10) || (response.data || []).length);
    } catch (error) {
      console.error('Failed to fetch SIM cards:', error);
      setSimCards([]);
      setSimTotal(0);
    } finally {
      setLoadingSimCards(false);
    }
  };
  
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };
  
  const SortableHeader = ({ column, className, children }) => (
    <TableHead 
      className={`cursor-pointer hover:bg-muted/50 select-none ${className || ''}`}
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortBy === column && (
          <span className="text-xs">
            {sortDirection === 'asc' ? '▲' : '▼'}
          </span>
        )}
      </div>
    </TableHead>
  );
  
  const handleExportSimCards = async (providerId, format = 'csv') => {
    try {
      // Export selected SIMs or all
      const idsToExport = selectedSimCardIds.length > 0 ? selectedSimCardIds : [];
      
      const response = await api.post(
        `/admin/sim-providers/${providerId}/sim-cards/export?format=${format}`,
        idsToExport,  // Send array of IDs to export
        { responseType: 'blob' }
      );
      
      const url_obj = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url_obj;
      const filename = selectedSimCardIds.length > 0 
        ? `sim_cards_${selectedProviderForSimCards?.name}_selected_${selectedSimCardIds.length}_${getFileTimestamp()}.csv`
        : `sim_cards_${selectedProviderForSimCards?.name}_all_${getFileTimestamp()}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      const count = selectedSimCardIds.length > 0 ? selectedSimCardIds.length : simCards.length;
      toast.success(`Exported ${count} SIM card(s) successfully`);
    } catch (error) {
      console.error('Failed to export SIM cards:', error);
      toast.error('Failed to export SIM cards');
    }
  };
  
  const handleAddSimCard = async () => {
    if (!simCardForm.iccid) {
      toast.error('ICCID is required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingSimCard) {
        // Edit existing SIM
        await api.put(`/admin/sim-cards/${editingSimCard.id}`, {
          ...simCardForm,
          data_limit_mb: parseInt(simCardForm.data_limit_mb) || 0,
          cost: parseFloat(simCardForm.cost) || 0,
        });
        toast.success('SIM card updated');
      } else {
        // Add new SIM
        await api.post(`/admin/sim-providers/${selectedProviderForSimCards.id}/sim-cards`, {
          ...simCardForm,
          data_limit_mb: parseInt(simCardForm.data_limit_mb) || 0,
          cost: parseFloat(simCardForm.cost) || 0,
          provider_id: selectedProviderForSimCards.id,
        });
        toast.success('SIM card added successfully');
      }
      fetchSimCards(selectedProviderForSimCards.id);
      setIsAddSimCardOpen(false);
      setEditingSimCard(null);
      setSimCardForm({
        iccid: '', mobile_number: '', imsi: '', imei: '', status: 'activated',
        plan_name: '', plan_type: 'postpaid', billing_cycle: 'monthly',
        data_limit_mb: '', data_unit: 'MB', cost: '', notes: '',
      });
    } catch (error) {
      console.error('Failed to add SIM card:', error);
      toast.error(getApiErrorMessage(error) || 'Failed to add SIM card');
    } finally {
      setSaving(false);
    }
  };
  
  const handleActivateSimCard = async (simId) => {
    try {
      await api.post(`/admin/sim-cards/${simId}/activate`);
      toast.success('SIM card activated');
      fetchSimCards(selectedProviderForSimCards.id);
    } catch (error) {
      console.error('Failed to activate SIM card:', error);
      toast.error('Failed to activate SIM card');
    }
  };
  
  const handleDeactivateSimCard = async (simId) => {
    try {
      await api.post(`/admin/sim-cards/${simId}/deactivate`);
      toast.success('SIM card deactivated');
      fetchSimCards(selectedProviderForSimCards.id);
    } catch (error) {
      console.error('Failed to deactivate SIM card:', error);
      toast.error('Failed to deactivate SIM card');
    }
  };
  
  const handleDeleteSimCard = async (simId) => {
    setPendingGlobalAction({
      title: 'Delete SIM Card?',
      message: 'Are you sure you want to delete this SIM card? This action cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'destructive',
      action: async () => {
        try {
          await api.delete(`/admin/sim-cards/${simId}`);
          toast.success('SIM card deleted');
          fetchSimCards(selectedProviderForSimCards.id);
        } catch (error) {
          console.error('Failed to delete SIM card:', error);
          toast.error('Failed to delete SIM card');
        }
      },
    });
  };
  
  const handleSyncSimCards = async (providerId, enhanced = false) => {
    setSaving(true);
    try {
      const url = `/admin/sim-providers/${providerId}/sync-sim-cards${enhanced ? '?enhanced=true' : ''}`;
      const response = await api.post(url);
      toast.success(response.data.message || 'SIM card sync completed');
      fetchSimCards(providerId);
    } catch (error) {
      console.error('Failed to sync SIM cards:', error);
      toast.error(getApiErrorMessage(error) || 'Failed to sync SIM cards');
    } finally {
      setSaving(false);
    }
  };

  // Parse ICCIDs from text input
  const parseICCIDs = (text) => {
    return [...new Set(
      text.split(/[\n,;\s]+/)
        .map(s => s.trim())
        .filter(s => /^\d{18,22}$/.test(s))
    )];
  };

  // Handle file upload for ICCID import
  const handleICCIDFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      setImportICCIDText(prev => prev ? prev + '\n' + evt.target.result : evt.target.result);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // Check which ICCIDs are missing from the database
  const handleCheckMissingICCIDs = async () => {
    const iccids = parseICCIDs(importICCIDText);
    if (iccids.length === 0) {
      toast.error('No valid ICCIDs found. Each ICCID should be 18-22 digits.');
      return;
    }
    setSaving(true);
    try {
      const response = await api.post(
        `/admin/sim-providers/${selectedProviderForSimCards.id}/check-missing-iccids`,
        iccids
      );
      setImportStats(response.data);
      setImportStep('preview');
    } catch (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to check ICCIDs');
    } finally {
      setSaving(false);
    }
  };

  // Import missing ICCIDs in batches
  const handleStartImport = async () => {
    if (!importStats?.missing_iccids?.length) return;
    const missingICCIDs = importStats.missing_iccids;
    const batchSize = 25;
    const totalBatches = Math.ceil(missingICCIDs.length / batchSize);
    
    setImportStep('importing');
    setImportProgress({ current: 0, total: missingICCIDs.length, synced: 0, failed: 0 });
    
    let totalSynced = 0;
    let totalFailed = 0;
    
    for (let i = 0; i < totalBatches; i++) {
      const batch = missingICCIDs.slice(i * batchSize, (i + 1) * batchSize);
      try {
        const response = await api.post(
          `/admin/sim-providers/${selectedProviderForSimCards.id}/sync-by-iccids`,
          batch
        );
        totalSynced += response.data.synced_count || 0;
        totalFailed += response.data.failed_count || 0;
      } catch (error) {
        totalFailed += batch.length;
        console.error(`Batch ${i + 1} failed:`, error);
      }
      setImportProgress({
        current: Math.min((i + 1) * batchSize, missingICCIDs.length),
        total: missingICCIDs.length,
        synced: totalSynced,
        failed: totalFailed,
      });
    }
    
    setImportStep('done');
    toast.success(`Import complete: ${totalSynced} synced, ${totalFailed} failed`);
    fetchSimCards(selectedProviderForSimCards.id);
  };

  // Reset import dialog state
  const resetImportDialog = () => {
    setImportICCIDText('');
    setImportStep('input');
    setImportStats(null);
    setImportProgress({ current: 0, total: 0, synced: 0, failed: 0 });
  };

  // Batch deactivate selected SIMs
  const handleBatchDeactivate = async () => {
    const ids = selectedSimCardIds.length > 0 ? selectedSimCardIds : simCards.map(s => s.id);
    setPendingGlobalAction({
      title: 'Deactivate SIM Cards',
      message: `Deactivate ${ids.length} SIM card(s)? This will call the provider API to change their status.`,
      confirmLabel: 'Deactivate',
      variant: 'destructive',
      action: async () => {
        setSaving(true);
        try {
          const response = await api.post(`/admin/sim-providers/${selectedProviderForSimCards.id}/batch-deactivate`, ids);
          toast.success(response.data.message);
          fetchSimCards(selectedProviderForSimCards.id);
        } catch (error) {
          toast.error('Failed to deactivate SIM cards');
        } finally {
          setSaving(false);
        }
      },
    });
  };

  // Sync usage data from Truphone Reports API
  const handleSyncUsage = async () => {
    setSaving(true);
    try {
      const response = await api.post(`/admin/sim-providers/${selectedProviderForSimCards.id}/sync-usage`);
      toast.success(response.data.message);
      fetchSimCards(selectedProviderForSimCards.id);
      // Refresh provider list and update the selected provider's last sync timestamp
      const providersRes = await api.get('/admin/sim-providers');
      setSimProviders((providersRes.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      const updatedProvider = (providersRes.data || []).find(p => p.id === selectedProviderForSimCards.id);
      if (updatedProvider) setSelectedProviderForSimCards(updatedProvider);
    } catch (error) {
      toast.error(getApiErrorMessage(error) || 'Usage sync failed');
    } finally {
      setSaving(false);
    }
  };

  // Update SIM expiry and data limit
  const handleUpdateLimits = async () => {
    if (!simDetailData || (!newExpiryDate && !newDataLimit)) return;
    setSaving(true);
    try {
      const body = {};
      if (newExpiryDate) body.expires_at = newExpiryDate.toISOString();
      if (newDataLimit) body.data_limit_mb = parseInt(newDataLimit);
      const response = await api.put(`/admin/sim-cards/${simDetailData.id}/update-limits`, body);
      toast.success('Expiry & data limit updated');
      // Update local state directly — don't refresh from API which would overwrite
      if (response.data.sim) {
        setSimDetailData(response.data.sim);
      }
      setNewExpiryDate(null);
      setNewDataLimit('');
    } catch (error) {
      toast.error(getApiErrorMessage(error) || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  // Reset manual limit overrides
  const handleResetLimits = async () => {
    if (!simDetailData) return;
    setPendingGlobalAction({
      title: 'Reset to API Values',
      message: 'Reset to API values? The expiry and data limit will be recalculated from the provider on next view.',
      confirmLabel: 'Reset',
      variant: 'default',
      action: async () => {
        setSaving(true);
        try {
          await api.put(`/admin/sim-cards/${simDetailData.id}/reset-limits`);
          toast.success('Limits reset — refreshing from API...');
          refreshSimDetail();
        } catch (error) {
          toast.error(getApiErrorMessage(error) || 'Reset failed');
        } finally {
          setSaving(false);
        }
      },
    });
  };

  // Fetch SMS history
  const fetchSmsHistory = async (simId) => {
    try {
      const response = await api.get(`/admin/sim-cards/${simId}/sms/history`);
      setSmsHistory((response.data || []).map(m => sanitizeRecord(m)));
    } catch { setSmsHistory([]); }
  };

  // Send SMS
  const handleSendSms = async () => {
    if (!smsMessage || !simDetailData) return;
    setSendingSms(true);
    try {
      await api.post(`/admin/sim-cards/${simDetailData.id}/sms/send`, { message: smsMessage });
      toast.success('SMS sent successfully');
      setSmsMessage('');
      fetchSmsHistory(simDetailData.id);
    } catch (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to send SMS');
    } finally {
      setSendingSms(false);
    }
  };

  // Fetch scheduled changes for a SIM
  const fetchScheduledChanges = async (simId) => {
    try {
      const response = await api.get(`/admin/sim-cards/${simId}/scheduled-changes`);
      setScheduledChanges((response.data || []).map(c => sanitizeRecord(c)));
    } catch { setScheduledChanges([]); }
  };

  // Create a scheduled change
  const handleCreateScheduledChange = async () => {
    if (!scheduleDate || !scheduleStatus || !simDetailData) return;
    if (scheduleStatus === simDetailData.status?.toUpperCase()) {
      toast.error(`SIM is already ${scheduleStatus}. Select a different status.`);
      return;
    }
    const [hours, minutes] = scheduleTime.split(':').map(Number);
    const combined = new Date(scheduleDate);
    combined.setHours(hours, minutes, 0, 0);
    try {
      await api.post(`/admin/sim-cards/${simDetailData.id}/schedule-change`, {
        target_status: scheduleStatus,
        scheduled_date: combined.toISOString(),
      });
      toast.success(`Scheduled ${scheduleStatus} for ${formatDT(combined.toISOString())}`);
      setScheduleDate(null);
      setScheduleTime('09:00');
      setScheduleStatus('');
      fetchScheduledChanges(simDetailData.id);
    } catch (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to create scheduled change');
    }
  };

  // Cancel a scheduled change
  const handleCancelScheduledChange = async (changeId) => {
    try {
      await api.delete(`/admin/scheduled-changes/${changeId}`);
      toast.success('Scheduled change cancelled');
      fetchScheduledChanges(simDetailData.id);
    } catch (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to cancel scheduled change');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-10rem)]">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563eb]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="global-settings-page">
      {/* Page Header */}
      <div className="flex items-center gap-3">
        <div className="hidden lg:flex items-center gap-3">
        <div className="p-2 rounded-lg bg-[#2563eb]/10">
          <Settings className="w-6 h-6 text-[#2563eb]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">System Settings</h1>
          <p className="text-sm text-muted-foreground">Platform-wide configuration and infrastructure settings</p>
        </div>
        </div>
        <Badge className="ml-auto bg-green-600">Super Admin Only</Badge>
      </div>

      {/* Sub-navigation Tabs */}
      <MobileSectionMenu
        items={[
          hasCommsProviders && { id: 'comms', label: 'Communications', icon: MessageSquare },
          hasBillingPlans && { id: 'billing', label: 'Billing & Plans', icon: CreditCard },
          hasMapsGeocoding && { id: 'maps', label: 'Maps', icon: Map },
          hasApiWebhooks && { id: 'api', label: 'API & Webhooks', icon: Key },
          hasFuelCards && { id: 'fuel', label: 'Fuel Cards', icon: Fuel },
          hasSimProvider && { id: 'sim', label: 'SIM Provider', icon: Smartphone },
          hasAccounting && { id: 'accounting', label: 'Accounting', icon: DollarSign },
          hasMaintenance && { id: 'maintenance', label: 'Maintenance', icon: Wrench },
          hasDataRetention && { id: 'retention', label: 'Data Retention', icon: Database },
          hasSecurity && { id: 'security', label: 'Security', icon: Shield },
        ].filter(Boolean)}
        activeId={activeTab}
        onChange={setActiveTab}
        testIdPrefix="global-settings-tabs"
      />
      <div className="hidden sm:flex gap-2 flex-wrap">
        {hasCommsProviders && (
          <Button size="sm"
            onClick={() => setActiveTab('comms')}
            className={`gap-2 min-w-[180px] justify-center ${activeTab === 'comms' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-comms"
          >
            <MessageSquare className="w-4 h-4" />
            Communications
          </Button>
        )}
        {hasBillingPlans && (
          <Button size="sm"
            onClick={() => setActiveTab('billing')}
            className={`gap-2 min-w-[180px] justify-center ${activeTab === 'billing' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-billing"
          >
            <CreditCard className="w-4 h-4" />
            Billing & Plans
          </Button>
        )}
        {hasMapsGeocoding && (
          <Button size="sm"
            onClick={() => setActiveTab('maps')}
            className={`gap-2 min-w-[180px] justify-center ${activeTab === 'maps' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-maps"
          >
            <Map className="w-4 h-4" />
            Maps
          </Button>
        )}
        {hasLocalization && null /* Localization tab removed — Date/Time and Units are in General Settings per-user */}
        {hasApiWebhooks && (
          <Button size="sm"
            onClick={() => setActiveTab('api')}
            className={`gap-2 min-w-[180px] justify-center ${activeTab === 'api' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-api"
          >
            <Key className="w-4 h-4" />
            API & Webhooks
          </Button>
        )}
        {hasFuelCards && (
          <Button size="sm"
            onClick={() => setActiveTab('fuel')}
            className={`gap-2 min-w-[180px] justify-center ${activeTab === 'fuel' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-fuel"
          >
            <Fuel className="w-4 h-4" />
            Fuel Cards
          </Button>
        )}
        {hasSimProvider && (
          <Button size="sm"
            onClick={() => setActiveTab('sim')}
            className={`gap-2 min-w-[180px] justify-center ${activeTab === 'sim' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-sim"
          >
            <Smartphone className="w-4 h-4" />
            SIM Provider
          </Button>
        )}
        {hasAccounting && (
          <Button size="sm"
            onClick={() => setActiveTab('accounting')}
            className={`gap-2 min-w-[180px] justify-center ${activeTab === 'accounting' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-accounting"
          >
            <DollarSign className="w-4 h-4" />
            Accounting
          </Button>
        )}
        {hasMaintenance && (
          <Button size="sm"
            onClick={() => setActiveTab('maintenance')}
            className={`gap-2 min-w-[180px] justify-center ${activeTab === 'maintenance' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-maintenance"
          >
            <Wrench className="w-4 h-4" />
            Maintenance
          </Button>
        )}
        {hasDataRetention && (
          <Button size="sm"
            onClick={() => setActiveTab('retention')}
            className={`gap-2 min-w-[180px] justify-center ${activeTab === 'retention' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-retention"
          >
            <Database className="w-4 h-4" />
            Data Retention
          </Button>
        )}
        {hasSecurity && (
          <Button size="sm"
            onClick={() => setActiveTab('security')}
            className={`gap-2 min-w-[180px] justify-center ${activeTab === 'security' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-security"
          >
            <Shield className="w-4 h-4" />
            Security
          </Button>
        )}
      </div>

      {/* ==================== COMMUNICATIONS PROVIDERS TAB ==================== */}
      {activeTab === 'comms' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Communications Providers
            </h2>
            <p className="text-sm text-muted-foreground mt-1">Configure SMS and email delivery services</p>
          </div>

          {/* SMS Provider */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                SMS Provider Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Primary Provider</Label>
                  <Select
                    value={smsProvider.primary_provider}
                    onValueChange={(v) => setSmsProvider(prev => ({ ...prev, primary_provider: v }))}
                  >
                    <SelectTrigger data-testid="sms-provider-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="twilio">Twilio</SelectItem>
                      <SelectItem value="mobilemessage">MobileMessage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Twilio-specific fields */}
                {smsProvider.primary_provider === 'twilio' && (
                  <>
                    <div className="space-y-2">
                      <Label>Account SID</Label>
                      <Input
                        value={smsProvider.account_sid || ''}
                        onChange={(e) => setSmsProvider(prev => ({ ...prev, account_sid: e.target.value }))}
                        onFocus={(e) => { if (e.target.value.includes('••••')) setSmsProvider(prev => ({ ...prev, account_sid: '' })); }}
                        placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        data-testid="sms-account-sid"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Auth Token</Label>
                      <Input
                        type="password"
                        value={smsProvider.auth_token || ''}
                        onChange={(e) => setSmsProvider(prev => ({ ...prev, auth_token: e.target.value }))}
                        onFocus={(e) => { if (e.target.value.includes('••••')) setSmsProvider(prev => ({ ...prev, auth_token: '' })); }}
                        placeholder="Enter auth token"
                        data-testid="sms-auth-token"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>From Number</Label>
                      <Input
                        value={smsProvider.from_number || ''}
                        onChange={(e) => setSmsProvider(prev => ({ ...prev, from_number: e.target.value }))}
                        placeholder="+1234567890"
                        data-testid="sms-from-number"
                      />
                    </div>
                  </>
                )}

                {/* MobileMessage-specific fields */}
                {smsProvider.primary_provider === 'mobilemessage' && (
                  <>
                    <div className="space-y-2">
                      <Label>API Username</Label>
                      <Input
                        value={smsProvider.mm_username || ''}
                        onChange={(e) => setSmsProvider(prev => ({ ...prev, mm_username: e.target.value }))}
                        placeholder="Your MobileMessage API username"
                        data-testid="sms-mm-username"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>API Password</Label>
                      <Input
                        type="password"
                        value={smsProvider.mm_password || ''}
                        onChange={(e) => setSmsProvider(prev => ({ ...prev, mm_password: e.target.value }))}
                        onFocus={(e) => { if (e.target.value.includes('••••')) setSmsProvider(prev => ({ ...prev, mm_password: '' })); }}
                        placeholder="Enter API password"
                        data-testid="sms-mm-password"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Sender ID</Label>
                      <Input
                        value={smsProvider.mm_sender_id || ''}
                        onChange={(e) => setSmsProvider(prev => ({ ...prev, mm_sender_id: e.target.value }))}
                        placeholder="61485900188 or CompanyName"
                        data-testid="sms-mm-sender-id"
                      />
                    </div>
                  </>
                )}

                {/* Generic API key for other providers */}
                {!['twilio', 'mobilemessage'].includes(smsProvider.primary_provider) && (
                  <div className="space-y-2">
                    <Label>API Key</Label>
                    <Input
                      type="password"
                      value={smsProvider.api_key || ''}
                      onChange={(e) => setSmsProvider(prev => ({ ...prev, api_key: e.target.value }))}
                      placeholder="Enter API key"
                    />
                  </div>
                )}

                {smsProvider.primary_provider === 'twilio' && (
                <div className="space-y-2">
                  <Label>Sender ID</Label>
                  <Input
                    value={smsProvider.sender_id || ''}
                    onChange={(e) => setSmsProvider(prev => ({ ...prev, sender_id: e.target.value }))}
                    placeholder="ENSUE"
                    data-testid="sms-sender-id"
                  />
                </div>
                )}

                <div className="space-y-2">
                  <Label>Fallback Provider</Label>
                  <Select
                    value={smsProvider.fallback_provider || ''}
                    onValueChange={(v) => setSmsProvider(prev => ({ ...prev, fallback_provider: v }))}
                    disabled={!smsProvider.fallback_enabled}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      {smsProvider.primary_provider !== 'twilio' && <SelectItem value="twilio">Twilio</SelectItem>}
                      {smsProvider.primary_provider !== 'mobilemessage' && <SelectItem value="mobilemessage">MobileMessage</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div>
                  <p className="font-medium text-sm">Enable Fallback Provider</p>
                  <p className="text-xs text-muted-foreground">Automatically switch if primary fails</p>
                </div>
                <Switch
                  checked={smsProvider.fallback_enabled}
                  onCheckedChange={(v) => setSmsProvider(prev => ({ ...prev, fallback_enabled: v }))}
                />
              </div>

              <div className="flex justify-end">
                <Button size="sm" onClick={handleSaveSmsSettings} disabled={saving} className="bg-[#2563eb] hover:bg-[#1d4ed8]">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save SMS Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Email Provider */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Email Provider Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email Provider</Label>
                  <Select
                    value={emailProvider.provider}
                    onValueChange={(v) => setEmailProvider(prev => ({ ...prev, provider: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sendgrid">SendGrid</SelectItem>
                      <SelectItem value="mailgun">Mailgun</SelectItem>
                      <SelectItem value="aws_ses">AWS SES</SelectItem>
                      <SelectItem value="smtp">Custom SMTP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>API Key</Label>
                  <Input
                    type="password"
                    value={emailProvider.api_key}
                    onChange={(e) => setEmailProvider(prev => ({ ...prev, api_key: e.target.value }))}
                    placeholder="Enter API key"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Sender Email</Label>
                  <Input
                    type="email"
                    value={emailProvider.sender_email}
                    onChange={(e) => setEmailProvider(prev => ({ ...prev, sender_email: e.target.value }))}
                    placeholder="noreply@ensue.com.au"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Sender Name</Label>
                  <Input
                    value={emailProvider.sender_name}
                    onChange={(e) => setEmailProvider(prev => ({ ...prev, sender_name: e.target.value }))}
                    placeholder="EnsueLIVETRAK"
                  />
                </div>

                {emailProvider.provider === 'smtp' && (
                  <>
                    <div className="space-y-2">
                      <Label>SMTP Host</Label>
                      <Input
                        value={emailProvider.smtp_host}
                        onChange={(e) => setEmailProvider(prev => ({ ...prev, smtp_host: e.target.value }))}
                        placeholder="smtp.example.com"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>SMTP Port</Label>
                      <Input
                        type="number"
                        value={emailProvider.smtp_port}
                        onChange={(e) => setEmailProvider(prev => ({ ...prev, smtp_port: parseInt(e.target.value) }))}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>SMTP Username</Label>
                      <Input
                        value={emailProvider.smtp_username || ''}
                        onChange={(e) => setEmailProvider(prev => ({ ...prev, smtp_username: e.target.value }))}
                        placeholder="username"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>SMTP Password</Label>
                      <Input
                        type="password"
                        value={emailProvider.smtp_password || ''}
                        onChange={(e) => setEmailProvider(prev => ({ ...prev, smtp_password: e.target.value }))}
                        placeholder="Enter password"
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="flex items-center justify-between border-t pt-4 mt-2">
                <div className="flex items-center gap-2 flex-1 mr-4">
                  <Input
                    type="email"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    placeholder="Enter email to send test..."
                    className="max-w-xs"
                    data-testid="test-email-input"
                  />
                  <Button 
                    variant="outline" 
                    onClick={handleSendTestEmail} 
                    disabled={sendingTestEmail || !testEmailAddress}
                    data-testid="send-test-email-btn"
                  >
                    {sendingTestEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Send Test
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handlePreviewEmail}
                    data-testid="preview-email-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Preview
                  </Button>
                </div>
                <Button size="sm" onClick={handleSaveEmailSettings} disabled={saving} className="bg-[#2563eb] hover:bg-[#1d4ed8]">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Email Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Account Invitations */}
          <Card data-testid="invite-reminder-settings-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Account Invitations
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Automatically re-send the welcome email to users who haven't activated their account yet. Each reminder includes a fresh 48-hour activation link.
              </p>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center justify-between">
                <div className="pr-4">
                  <Label className="text-sm font-medium">Enable auto-reminders</Label>
                  <p className="text-xs text-muted-foreground">When off, no automatic reminder emails are sent.</p>
                </div>
                <Switch
                  checked={!!inviteReminder.enabled}
                  onCheckedChange={(v) => setInviteReminder(prev => ({ ...prev, enabled: v }))}
                  data-testid="invite-reminder-enabled-toggle"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Resend if not activated after (days)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={60}
                    value={inviteReminder.days}
                    onChange={(e) => setInviteReminder(prev => ({ ...prev, days: e.target.value }))}
                    disabled={!inviteReminder.enabled}
                    data-testid="invite-reminder-days-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Maximum reminders</Label>
                  <Input
                    type="number"
                    min={0}
                    max={10}
                    value={inviteReminder.max_reminders}
                    onChange={(e) => setInviteReminder(prev => ({ ...prev, max_reminders: e.target.value }))}
                    disabled={!inviteReminder.enabled}
                    data-testid="invite-reminder-max-input"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={handleRunRemindersNow} disabled={runningReminders || !inviteReminder.enabled} data-testid="invite-reminder-run-now-btn">
                  {runningReminders ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                  Send reminders now
                </Button>
                <Button size="sm" onClick={handleSaveInviteReminder} disabled={inviteReminderSaving} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="invite-reminder-save-btn">
                  {inviteReminderSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Invitation Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ==================== BILLING & PLANS TAB ==================== */}
      {activeTab === 'billing' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Billing & Plans
              </h2>
              <p className="text-sm text-muted-foreground mt-1">Manage subscription plans and billing configuration</p>
            </div>
            <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8]">
              <Plus className="w-4 h-4 mr-2" />
              Create Plan
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Subscription Plans</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plan Name</TableHead>
                    <TableHead>Max Devices</TableHead>
                    <TableHead>Storage (GB)</TableHead>
                    <TableHead>History (Days)</TableHead>
                    <TableHead>Features</TableHead>
                    <TableHead>Price/Month</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plans.map((plan) => (
                    <TableRow key={plan.id}>
                      <TableCell className="font-medium">{plan.name}</TableCell>
                      <TableCell>{plan.max_devices === -1 ? 'Unlimited' : plan.max_devices}</TableCell>
                      <TableCell>{plan.max_storage_gb === -1 ? 'Unlimited' : plan.max_storage_gb}</TableCell>
                      <TableCell>{plan.history_days}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {plan.features.slice(0, 3).map((feature) => (
                            <Badge key={feature} variant="outline" className="text-xs">{feature}</Badge>
                          ))}
                          {plan.features.length > 3 && (
                            <Badge variant="outline" className="text-xs">+{plan.features.length - 3} more</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-semibold">${plan.price}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline">Edit</Button>
                          <Button size="sm" variant="outline">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Billing Provider Config */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Billing Provider Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Billing Provider</Label>
                  <Select defaultValue="stripe">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="stripe">Stripe</SelectItem>
                      <SelectItem value="paypal">PayPal</SelectItem>
                      <SelectItem value="chargebee">Chargebee</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>API Key</Label>
                  <Input type="password" placeholder="sk_live_..." />
                </div>

                <div className="space-y-2">
                  <Label>Webhook Secret</Label>
                  <Input type="password" placeholder="whsec_..." />
                </div>

                <div className="space-y-2">
                  <Label>Grace Period (Days)</Label>
                  <Input type="number" defaultValue="7" />
                </div>
              </div>

              <div className="flex justify-end">
                <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8]">
                  <Save className="w-4 h-4 mr-2" />
                  Save Billing Configuration
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ==================== MAPS & GEOCODING TAB ==================== */}
      {activeTab === 'maps' && (
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Map className="w-5 h-5" />
              Maps & Geocoding
            </h2>
            <p className="text-sm text-muted-foreground mt-1">Configure map providers and geocoding services</p>
          </div>

          {/* ==================== OSRM Routing Engine ==================== */}
          <Card data-testid="osrm-admin-panel">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    OSRM Routing Engine
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">Self-hosted road routing for Snap to Road and ETA calculations</p>
                </div>
                <Button variant="outline" size="sm" onClick={fetchOsrmData} disabled={osrmLoading} data-testid="osrm-refresh-btn">
                  {osrmLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  <span className="ml-1.5 text-xs">Check Status</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Service Status */}
              {osrmStatus && (
                <div className={`p-3.5 rounded-lg border ${
                  osrmStatus.status === 'online' ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' :
                  osrmStatus.status === 'not_configured' ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800' :
                  'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'
                }`} data-testid="osrm-status">
                  <div className="flex items-center gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                      osrmStatus.status === 'online' ? 'bg-green-500' :
                      osrmStatus.status === 'not_configured' ? 'bg-amber-500' :
                      'bg-red-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${
                        osrmStatus.status === 'online' ? 'text-green-800 dark:text-green-300' :
                        osrmStatus.status === 'not_configured' ? 'text-amber-800 dark:text-amber-300' :
                        'text-red-800 dark:text-red-300'
                      }`}>
                        {osrmStatus.status === 'online' ? 'Online' : osrmStatus.status === 'not_configured' ? 'Not Configured' : 'Offline'}
                      </p>
                      <p className="text-xs text-muted-foreground">{osrmStatus.message}</p>
                    </div>
                    <div className="text-right text-xs text-muted-foreground space-y-0.5">
                      {osrmStatus.response_time_ms != null && <p>Response: {osrmStatus.response_time_ms}ms</p>}
                    </div>
                  </div>
                  {osrmStatus.server_url && (
                    <p className="text-xs text-muted-foreground mt-2 font-mono">{osrmStatus.server_url}</p>
                  )}
                </div>
              )}
              {!osrmStatus && !osrmLoading && (
                <div className="p-4 rounded-lg border border-dashed text-center">
                  <p className="text-sm text-muted-foreground">Click "Check Status" to ping the OSRM service</p>
                </div>
              )}

              {/* Configured Countries */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-sm font-medium">Configured Countries</h4>
                    <p className="text-xs text-muted-foreground">Regions loaded into the OSRM routing engine</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => { if (!osrmConfig) fetchOsrmData(); setOsrmAddOpen(true); }} data-testid="osrm-add-country-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Country
                  </Button>
                </div>
                {osrmConfig?.countries?.length > 0 ? (
                  <div className="space-y-1.5">
                    {osrmConfig.countries.map((c) => (
                      <div key={c.slug} className="flex items-center justify-between p-2.5 rounded border bg-muted/20" data-testid={`osrm-country-${c.slug}`}>
                        <div>
                          <p className="text-sm font-medium">{c.display_name}</p>
                          <p className="text-[10px] text-muted-foreground">{c.continent} &middot; {c.slug}</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleRemoveOsrmCountry(c.slug)} className="h-7 w-7 p-0 text-muted-foreground hover:text-red-500">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : osrmConfig ? (
                  <p className="text-sm text-muted-foreground p-3 border border-dashed rounded text-center">No countries configured</p>
                ) : (
                  <p className="text-sm text-muted-foreground p-3 border border-dashed rounded text-center">Click "Check Status" or "Add Country" to load configuration</p>
                )}
              </div>

              {/* Update Schedule & Notes */}
              {osrmConfig && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t">
                  <div className="space-y-2">
                    <Label className="text-xs">Update Schedule</Label>
                    <Input
                      value={osrmConfig.update_schedule || ''}
                      onChange={(e) => setOsrmConfig(prev => ({ ...prev, update_schedule: e.target.value }))}
                      placeholder="e.g. Monthly (1st of month, 3:00 AM)"
                      className="text-xs"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Last Import Date</Label>
                    <div className="px-3 py-2 rounded-md border bg-muted/30 text-xs">
                      {osrmConfig.last_update ? formatDateOnly(osrmConfig.last_update) : <span className="text-muted-foreground">Not recorded — updated automatically after import</span>}
                    </div>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-xs">Notes</Label>
                    <Textarea
                      value={osrmConfig.notes || ''}
                      onChange={(e) => setOsrmConfig(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Admin notes about the OSRM setup..."
                      rows={2}
                      className="text-xs"
                    />
                  </div>
                </div>
              )}

              {/* Setup Commands — toggled by button */}
              {osrmShowCommands && osrmSetupCommands && (
                <div className="pt-3 border-t">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium">Server Setup Commands</h4>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(osrmSetupCommands); toast.success('Commands copied'); }} className="text-xs h-7">
                        <Copy className="w-3 h-3 mr-1" /> Copy
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setOsrmShowCommands(false)} className="h-7 w-7 p-0">
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <pre className="text-[11px] bg-slate-900 text-green-400 p-3 rounded-lg overflow-x-auto max-h-60 font-mono whitespace-pre">{osrmSetupCommands}</pre>
                  <p className="text-[10px] text-muted-foreground mt-2">Run these commands on your OSRM EC2 server via SSH to apply the country changes.</p>
                </div>
              )}

              {/* Save + View Commands buttons */}
              {osrmConfig && (
                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={() => { setOsrmShowCommands(!osrmShowCommands); if (!osrmSetupCommands) handleSaveOsrmConfig(); }} data-testid="osrm-show-commands-btn">
                    <Download className="w-4 h-4 mr-2" />
                    {osrmShowCommands ? 'Hide' : 'View'} Setup Commands
                  </Button>
                  <Button size="sm" onClick={handleSaveOsrmConfig} disabled={osrmSaving} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="osrm-save-btn">
                    {osrmSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save OSRM Configuration
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ==================== Nominatim Geocoding Engine ==================== */}
          <Card data-testid="nominatim-admin-panel">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Nominatim Geocoding Engine
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">Self-hosted reverse geocoding for address lookups and autocomplete</p>
                </div>
                <Button variant="outline" size="sm" onClick={fetchNominatimData} disabled={nominatimLoading} data-testid="nominatim-refresh-btn">
                  {nominatimLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  <span className="ml-1.5 text-xs">Check Status</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Service Status */}
              {nominatimStatus && (
                <div className={`p-3.5 rounded-lg border ${
                  nominatimStatus.status === 'online' ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' :
                  nominatimStatus.status === 'not_configured' ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800' :
                  'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-2.5 h-2.5 rounded-full ${
                        nominatimStatus.status === 'online' ? 'bg-green-500' :
                        nominatimStatus.status === 'not_configured' ? 'bg-amber-500' :
                        'bg-red-500'
                      }`} />
                      <div>
                        <p className={`text-sm font-medium ${
                          nominatimStatus.status === 'online' ? 'text-green-800 dark:text-green-300' :
                          nominatimStatus.status === 'not_configured' ? 'text-amber-800 dark:text-amber-300' :
                          'text-red-800 dark:text-red-300'
                        }`}>
                          {nominatimStatus.status === 'online' ? 'Online' : nominatimStatus.status === 'not_configured' ? 'Not Configured' : 'Offline'}
                        </p>
                        <p className="text-xs text-muted-foreground">{nominatimStatus.message}</p>
                      </div>
                    </div>
                    <div className="text-right text-xs text-muted-foreground space-y-0.5">
                      {nominatimStatus.response_time_ms != null && <p>Response: {nominatimStatus.response_time_ms}ms</p>}
                    </div>
                  </div>
                  {nominatimStatus.test_result && nominatimStatus.status === 'online' && (
                    <p className="text-[10px] text-muted-foreground mt-2 font-mono truncate">Test: {nominatimStatus.test_result}</p>
                  )}
                  {nominatimStatus.server_url && (
                    <p className="text-xs text-muted-foreground mt-2 font-mono">{nominatimStatus.server_url}</p>
                  )}
                </div>
              )}
              {!nominatimStatus && !nominatimLoading && (
                <div className="p-4 rounded-lg border border-dashed text-center">
                  <p className="text-sm text-muted-foreground">Click "Check Status" to ping the Nominatim service</p>
                </div>
              )}

              {/* Test Search Tool */}
              <div className="pt-1">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-1.5">
                  <Search className="w-3.5 h-3.5" /> Test Search
                </h4>
                <div className="flex gap-2 mb-2">
                  <div className="flex rounded-md border overflow-hidden text-xs">
                    <button
                      onClick={() => { setNominatimTestMode('address'); setNominatimTestResults(null); }}
                      className={`px-3 py-1.5 transition-colors ${nominatimTestMode === 'address' ? 'bg-[#2563eb] text-white' : 'bg-muted/30 hover:bg-muted'}`}
                      data-testid="nominatim-test-mode-address"
                    >
                      Address
                    </button>
                    <button
                      onClick={() => { setNominatimTestMode('reverse'); setNominatimTestResults(null); }}
                      className={`px-3 py-1.5 transition-colors ${nominatimTestMode === 'reverse' ? 'bg-[#2563eb] text-white' : 'bg-muted/30 hover:bg-muted'}`}
                      data-testid="nominatim-test-mode-reverse"
                    >
                      GPS Coordinates
                    </button>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={nominatimTestQuery}
                    onChange={(e) => setNominatimTestQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleNominatimTest()}
                    placeholder={nominatimTestMode === 'address' ? 'e.g. 25 Martin Place, Sydney' : 'e.g. -33.8688, 151.2093'}
                    className="text-xs flex-1"
                    data-testid="nominatim-test-input"
                  />
                  <Button variant="outline" size="sm" onClick={handleNominatimTest} disabled={nominatimTestLoading || !nominatimTestQuery.trim()} className="h-9 px-3" data-testid="nominatim-test-btn">
                    {nominatimTestLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                    <span className="ml-1.5 text-xs">Search</span>
                  </Button>
                </div>

                {/* Test Results */}
                {nominatimTestResults && (
                  <div className="mt-3 rounded-lg border overflow-hidden">
                    {nominatimTestResults.error ? (
                      <div className="p-3 bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400 text-xs">
                        Error: {nominatimTestResults.error}
                      </div>
                    ) : (
                      <>
                        <div className="px-3 py-2 bg-muted/30 border-b flex items-center justify-between">
                          <span className="text-[10px] text-muted-foreground">
                            {nominatimTestResults.results?.length || 0} result{nominatimTestResults.results?.length !== 1 ? 's' : ''} for "{nominatimTestResults.query}"
                          </span>
                          <span className="text-[10px] text-muted-foreground">
                            {nominatimTestResults.response_time_ms}ms
                          </span>
                        </div>
                        {nominatimTestResults.results?.length > 0 ? (
                          <div className="divide-y max-h-60 overflow-y-auto">
                            {nominatimTestResults.results.map((r, i) => (
                              <div key={i} className="px-3 py-2.5 hover:bg-muted/20 transition-colors" data-testid={`nominatim-test-result-${i}`}>
                                <p className="text-xs font-medium text-foreground">{r.display_name}</p>
                                <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                                  <span className="font-mono">{parseFloat(r.lat).toFixed(6)}, {parseFloat(r.lon).toFixed(6)}</span>
                                  {r.type && <Badge variant="outline" className="text-[9px] px-1.5 py-0">{r.type}</Badge>}
                                  {r.importance != null && <span>score: {r.importance.toFixed(3)}</span>}
                                  <a
                                    href={`https://www.google.com/maps?q=${r.lat},${r.lon}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-500 hover:text-blue-600 hover:underline"
                                  >
                                    View on Map
                                  </a>
                                </div>
                                {r.address && Object.keys(r.address).length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1.5">
                                    {Object.entries(r.address).filter(([k]) => !['country_code'].includes(k)).slice(0, 8).map(([k, v]) => (
                                      <span key={k} className="text-[9px] bg-muted/50 px-1.5 py-0.5 rounded">
                                        <span className="text-muted-foreground">{k}:</span> {v}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="p-3 text-xs text-muted-foreground text-center">No results found</div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Configured Countries */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-sm font-medium">Configured Countries</h4>
                    <p className="text-xs text-muted-foreground">Regions loaded into the Nominatim geocoding database</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => { if (!nominatimConfig) fetchNominatimData(); setNominatimAddOpen(true); }} data-testid="nominatim-add-country-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Country
                  </Button>
                </div>
                {nominatimConfig?.countries?.length > 0 ? (
                  <div className="space-y-1.5">
                    {nominatimConfig.countries.map((c) => (
                      <div key={c.slug} className="flex items-center justify-between p-2.5 rounded border bg-muted/20" data-testid={`nominatim-country-${c.slug}`}>
                        <div>
                          <p className="text-sm font-medium">{c.display_name}</p>
                          <p className="text-[10px] text-muted-foreground">{c.continent} &middot; {c.slug}</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleRemoveNominatimCountry(c.slug)} className="h-7 w-7 p-0 text-muted-foreground hover:text-red-500">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : nominatimConfig ? (
                  <p className="text-sm text-muted-foreground p-3 border border-dashed rounded text-center">No countries configured</p>
                ) : (
                  <p className="text-sm text-muted-foreground p-3 border border-dashed rounded text-center">Click "Check Status" or "Add Country" to load configuration</p>
                )}
              </div>

              {/* Update Schedule & Notes */}
              {nominatimConfig && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t">
                  <div className="space-y-2">
                    <Label className="text-xs">Update Schedule</Label>
                    <Input
                      value={nominatimConfig.update_schedule || ''}
                      onChange={(e) => setNominatimConfig(prev => ({ ...prev, update_schedule: e.target.value }))}
                      placeholder="e.g. Monthly (1st of month, 4:00 AM)"
                      className="text-xs"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Last Import Date</Label>
                    <div className="px-3 py-2 rounded-md border bg-muted/30 text-xs">
                      {nominatimConfig.last_update ? formatDateOnly(nominatimConfig.last_update) : <span className="text-muted-foreground">Not recorded — updated automatically after import</span>}
                    </div>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-xs">Notes</Label>
                    <Textarea
                      value={nominatimConfig.notes || ''}
                      onChange={(e) => setNominatimConfig(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Admin notes about the Nominatim setup..."
                      rows={2}
                      className="text-xs"
                    />
                  </div>
                </div>
              )}

              {/* Setup Commands */}
              {nominatimShowCommands && nominatimSetupCommands && (
                <div className="pt-3 border-t">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium">Server Update Commands</h4>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(nominatimSetupCommands); toast.success('Commands copied'); }} className="text-xs h-7">
                        <Copy className="w-3 h-3 mr-1" /> Copy
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setNominatimShowCommands(false)} className="h-7 w-7 p-0">
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <pre className="text-[11px] bg-slate-900 text-green-400 p-3 rounded-lg overflow-x-auto max-h-60 font-mono whitespace-pre">{nominatimSetupCommands}</pre>
                  <p className="text-[10px] text-muted-foreground mt-2">Run these commands on your EC2 server via SSH. Requires temporary upgrade to c6i.4xlarge (32GB RAM) for the import.</p>
                </div>
              )}

              {/* Save + View Commands buttons */}
              {nominatimConfig && (
                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={() => { setNominatimShowCommands(!nominatimShowCommands); if (!nominatimSetupCommands) handleSaveNominatimConfig(); }} data-testid="nominatim-show-commands-btn">
                    <Download className="w-4 h-4 mr-2" />
                    {nominatimShowCommands ? 'Hide' : 'View'} Update Commands
                  </Button>
                  <Button size="sm" onClick={handleSaveNominatimConfig} disabled={nominatimSaving} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="nominatim-save-btn">
                    {nominatimSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Nominatim Configuration
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ==================== Overpass API (Speed Limits) ==================== */}
          <Card data-testid="overpass-admin-panel">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Database className="w-4 h-4" />
                    Overpass API (Road Speed Limits)
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">OpenStreetMap speed limit data — primary source for Advanced Road Speed Alerts</p>
                </div>
                <div className="flex items-center gap-2">
                  {overpassConfig && (
                    <Switch
                      checked={overpassConfig.enabled ?? true}
                      onCheckedChange={(v) => setOverpassConfig(prev => ({ ...prev, enabled: v }))}
                      data-testid="overpass-enabled-toggle"
                    />
                  )}
                  <Button variant="outline" size="sm" onClick={fetchOverpassData} disabled={overpassLoading} data-testid="overpass-refresh-btn">
                    {overpassLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    <span className="ml-1.5 text-xs">Check Status</span>
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              {overpassConfig && !(overpassConfig.enabled ?? true) && (
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                  <p className="text-xs text-amber-700 dark:text-amber-400">Service is disabled — Overpass will not be queried for speed limits. Toggle ON above to re-enable.</p>
                </div>
              )}

              {/* Service Status — matches OSRM styling */}
              {overpassStatus && (
                <div className={`p-3.5 rounded-lg border ${
                  overpassStatus.status === 'online' ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' :
                  overpassStatus.status === 'not_configured' ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800' :
                  'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'
                }`} data-testid="overpass-status">
                  <div className="flex items-center gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                      overpassStatus.status === 'online' ? 'bg-green-500' :
                      overpassStatus.status === 'not_configured' ? 'bg-amber-500' : 'bg-red-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${
                        overpassStatus.status === 'online' ? 'text-green-800 dark:text-green-300' :
                        overpassStatus.status === 'not_configured' ? 'text-amber-800 dark:text-amber-300' :
                        'text-red-800 dark:text-red-300'
                      }`}>
                        {overpassStatus.status === 'online' ? 'Online' : overpassStatus.status === 'not_configured' ? 'Not Configured' : 'Offline'}
                      </p>
                      <p className="text-xs text-muted-foreground">{overpassStatus.message}</p>
                    </div>
                    <div className="text-right text-xs text-muted-foreground space-y-0.5">
                      {overpassStatus.response_time_ms != null && <p>Response: {overpassStatus.response_time_ms}ms</p>}
                      {overpassStatus.data_timestamp && <p>OSM Data: {formatDateOnly(overpassStatus.data_timestamp)}</p>}
                    </div>
                  </div>
                  {overpassStatus.server_url && (
                    <p className="text-xs text-muted-foreground mt-2 font-mono">{overpassStatus.server_url}</p>
                  )}
                </div>
              )}
              {!overpassStatus && !overpassLoading && (
                <div className="p-4 rounded-lg border border-dashed text-center">
                  <p className="text-sm text-muted-foreground">Click "Check Status" to ping the Overpass API service</p>
                </div>
              )}

              {/* Configuration */}
              {overpassConfig && (
                <>
                  <div>
                    <Label className="text-xs">Server URL</Label>
                    <Input
                      value={overpassConfig.server_url || ''}
                      onChange={(e) => setOverpassConfig(prev => ({ ...prev, server_url: e.target.value }))}
                      placeholder="https://overpass-api.de/api/interpreter"
                      className="mt-1 text-xs font-mono"
                      data-testid="overpass-server-url"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">Public: https://overpass-api.de/api/interpreter — Self-hosted: your server URL</p>
                  </div>
                  <div className="flex items-center gap-3 p-2.5 rounded border">
                    <Checkbox
                      checked={overpassConfig.is_self_hosted || false}
                      onCheckedChange={(v) => setOverpassConfig(prev => ({ ...prev, is_self_hosted: v }))}
                      data-testid="overpass-self-hosted"
                    />
                    <div>
                      <Label className="text-xs font-medium">Self-Hosted Instance</Label>
                      <p className="text-[10px] text-muted-foreground">Check this if you're running your own Overpass API server (no rate limits)</p>
                    </div>
                  </div>

                  {/* Update Schedule & Notes — matches OSRM grid layout */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t">
                    <div className="space-y-2">
                      <Label className="text-xs">Update Schedule</Label>
                      <Input
                        value={overpassConfig.update_schedule || ''}
                        onChange={(e) => setOverpassConfig(prev => ({ ...prev, update_schedule: e.target.value }))}
                        placeholder="e.g. Monthly (1st of month, 3:00 AM)"
                        className="text-xs"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Last Import Date (OSM Data Age)</Label>
                      <div className="px-3 py-2 rounded-md border bg-muted/30 text-xs">
                        {overpassStatus?.data_timestamp ? formatDateOnly(overpassStatus.data_timestamp) : overpassConfig.last_update ? formatDateOnly(overpassConfig.last_update) : <span className="text-muted-foreground">Not available — click Check Status</span>}
                      </div>
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label className="text-xs">Notes</Label>
                      <Textarea
                        value={overpassConfig.notes || ''}
                        onChange={(e) => setOverpassConfig(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Admin notes about the Overpass setup..."
                        rows={2}
                        className="text-xs"
                      />
                    </div>
                  </div>

                  {/* Setup Commands — matches OSRM styling exactly */}
                  {overpassShowCommands && overpassSetupCommands && (
                    <div className="pt-3 border-t">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium">Server Update Commands</h4>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(overpassSetupCommands); toast.success('Commands copied'); }} className="text-xs h-7">
                            <Copy className="w-3 h-3 mr-1" /> Copy
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setOverpassShowCommands(false)} className="h-7 w-7 p-0">
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                      <pre className="text-[11px] bg-slate-900 text-green-400 p-3 rounded-lg overflow-x-auto max-h-60 font-mono whitespace-pre">{overpassSetupCommands}</pre>
                      <p className="text-[10px] text-muted-foreground mt-2">Run these commands on your server via SSH to refresh Overpass data from the OSRM combined PBF.</p>
                    </div>
                  )}

                  {/* Save + View Commands buttons — matches OSRM layout */}
                  <div className="flex items-center justify-end gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => { setOverpassSetupCommands(overpassSetupCommands || 'Loading...'); setOverpassShowCommands(!overpassShowCommands); if (!overpassSetupCommands) handleSaveOverpassConfig(); }} data-testid="overpass-show-commands-btn">
                      <Download className="w-4 h-4 mr-2" />
                      {overpassShowCommands ? 'Hide' : 'View'} Update Commands
                    </Button>
                    <Button size="sm" onClick={handleSaveOverpassConfig} disabled={overpassSaving} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="overpass-save-btn">
                      {overpassSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Overpass Configuration
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* ==================== Speed Limit Refresh Mode ==================== */}
          <Card data-testid="speed-limit-refresh-mode-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <RefreshCw className="w-4 h-4" />
                Speed Limit Refresh Mode
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Controls how speed limit data is refreshed for devices on the Live Tracking page
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {!speedLimitRefreshConfig ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {/* WebSocket Mode */}
                    <label
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        speedLimitRefreshConfig.mode === 'websocket'
                          ? 'border-[#2563eb] bg-blue-50 dark:bg-blue-950/20'
                          : 'border-border hover:bg-muted/50'
                      }`}
                      data-testid="refresh-mode-websocket"
                    >
                      <input
                        type="radio"
                        name="refreshMode"
                        value="websocket"
                        checked={speedLimitRefreshConfig.mode === 'websocket'}
                        onChange={() => setSpeedLimitRefreshConfig(prev => ({ ...prev, mode: 'websocket' }))}
                        className="mt-1"
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <Zap className="w-3.5 h-3.5 text-amber-500" />
                          <span className="text-sm font-medium">Real-time (WebSocket)</span>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-green-600 border-green-300">Recommended</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Speed limit is re-fetched immediately when a device sends new GPS data. Most accurate — updates as soon as the device moves to a new road. Best for small-to-medium fleets (up to ~200 devices per user session).
                        </p>
                      </div>
                    </label>

                    {/* Timer Mode */}
                    <label
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        speedLimitRefreshConfig.mode === 'timer'
                          ? 'border-[#2563eb] bg-blue-50 dark:bg-blue-950/20'
                          : 'border-border hover:bg-muted/50'
                      }`}
                      data-testid="refresh-mode-timer"
                    >
                      <input
                        type="radio"
                        name="refreshMode"
                        value="timer"
                        checked={speedLimitRefreshConfig.mode === 'timer'}
                        onChange={() => setSpeedLimitRefreshConfig(prev => ({ ...prev, mode: 'timer' }))}
                        className="mt-1"
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-blue-500" />
                          <span className="text-sm font-medium">Interval (Batch Timer)</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Speed limits are refreshed in a single batch request at a set interval. Uses 2 consecutive speeding readings before alerting to prevent false positives during speed zone transitions. Best for large fleets or high server load.
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Timer interval setting — only visible in timer mode */}
                  {speedLimitRefreshConfig.mode === 'timer' && (
                    <div className="pl-8 space-y-2">
                      <Label className="text-xs">Refresh Interval (seconds)</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={5}
                          max={60}
                          value={speedLimitRefreshConfig.timer_interval_seconds || 10}
                          onChange={(e) => setSpeedLimitRefreshConfig(prev => ({ ...prev, timer_interval_seconds: Math.max(5, Math.min(60, parseInt(e.target.value) || 10)) }))}
                          className="w-20 h-8 text-sm"
                          data-testid="timer-interval-input"
                        />
                        <span className="text-xs text-muted-foreground">seconds (5–60)</span>
                      </div>
                      <p className="text-[11px] text-amber-600 dark:text-amber-400">
                        In timer mode, speeding alerts require 2 consecutive readings to confirm — this prevents false alerts when a vehicle transitions between speed zones.
                      </p>
                    </div>
                  )}

                  <div className="flex items-center justify-end pt-2">
                    <Button size="sm" onClick={handleSaveSpeedLimitRefreshConfig} disabled={speedLimitRefreshSaving} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="refresh-mode-save-btn">
                      {speedLimitRefreshSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Refresh Mode
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* ==================== Speed Limit Test Tool ==================== */}
          <Card data-testid="speed-limit-test-tool">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Search className="w-4 h-4" />
                Speed Limit Test Tool
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Compare speed limit results from Overpass (OSM) and OSRM Estimated side-by-side</p>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Address search */}
              <div>
                <Label className="text-xs">Search by Address</Label>
                <div className="mt-1">
                  <AddressAutocomplete
                    inputClassName="h-8 text-xs"
                    value={testAddressValue}
                    onChange={setTestAddressValue}
                    onSelect={(s) => {
                      setTestLat(String(s.lat));
                      setTestLng(String(s.lon));
                      toast.success(`Found: ${s.display_name?.substring(0, 80)}`);
                    }}
                    placeholder="e.g. 180 Menangle Rd, Menangle Park NSW"
                    data-testid="test-address"
                  />
                </div>
              </div>

              {/* GPS coordinates — single input */}
              <div className="flex gap-3 items-end">
                <div className="flex-1">
             <Label className="text-xs">GPS Coordinates</Label>
                  <Input
                    value={testLat && testLng ? `${testLat}, ${testLng}` : ''}
                    onChange={(e) => {
                      const parts = e.target.value.split(',').map(s => s.trim());
                      setTestLat(parts[0] || '');
                      setTestLng(parts[1] || '');
                    }}
                    placeholder="-33.947802, 150.907218"
                    className="mt-1 h-8 text-xs font-mono"
                    data-testid="test-coords-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleTestSpeedLimit(); }}
                  />
                </div>
                <Button size="sm" onClick={handleTestSpeedLimit} disabled={testLoading} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="test-speed-btn">
                  {testLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Search className="w-3.5 h-3.5 mr-1.5" />}
                  Test
                </Button>
              </div>

              {/* Results Table */}
              {testResults && (
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="text-[10px] py-2">Source</TableHead>
                        <TableHead className="text-[10px] py-2">Speed Limit</TableHead>
                        <TableHead className="text-[10px] py-2">Road Name</TableHead>
                        <TableHead className="text-[10px] py-2">Response</TableHead>
                        <TableHead className="text-[10px] py-2">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow className={testResults.overpass?.enabled === false ? 'opacity-40' : ''}>
                        <TableCell className="text-xs py-2">
                          <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400 text-[9px]">Overpass (OSM)</Badge>
                        </TableCell>
                        <TableCell className="text-xs py-2 font-bold">
                          {testResults.overpass?.speed_limit != null ? `${testResults.overpass.speed_limit} km/h` : '—'}
                        </TableCell>
                        <TableCell className="text-xs py-2">{testResults.overpass?.road_name || '—'}</TableCell>
                        <TableCell className="text-xs py-2 text-muted-foreground">{testResults.overpass?.response_ms ? `${testResults.overpass.response_ms}ms` : '—'}</TableCell>
                        <TableCell className="text-xs py-2">
                          {testResults.overpass?.enabled === false
                            ? <span className="text-[10px] text-red-500">disabled</span>
                            : <span className={`text-[10px] ${testResults.overpass?.status === 'ok' ? 'text-green-600' : 'text-amber-600'}`}>{testResults.overpass?.status || '—'}</span>
                          }
                        </TableCell>
                      </TableRow>
                      <TableRow className={testResults.osrm?.enabled === false ? 'opacity-40' : ''}>
                        <TableCell className="text-xs py-2">
                          <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 text-[9px]">OSRM Estimated</Badge>
                        </TableCell>
                        <TableCell className="text-xs py-2 font-bold">
                          {testResults.osrm?.speed_limit != null ? `${testResults.osrm.speed_limit} km/h` : '—'}
                        </TableCell>
                        <TableCell className="text-xs py-2">
                          {testResults.osrm?.road_name || '—'}
                          {testResults.osrm?.road_type && <span className="text-[10px] text-muted-foreground ml-1">({testResults.osrm.road_type})</span>}
                        </TableCell>
                        <TableCell className="text-xs py-2 text-muted-foreground">{testResults.osrm?.response_ms ? `${testResults.osrm.response_ms}ms` : '—'}</TableCell>
                        <TableCell className="text-xs py-2">
                          {testResults.osrm?.enabled === false
                            ? <span className="text-[10px] text-red-500">disabled</span>
                            : <span className={`text-[10px] ${testResults.osrm?.status === 'ok' ? 'text-green-600' : 'text-amber-600'}`}>{testResults.osrm?.status || '—'}</span>
                          }
                        </TableCell>
                      </TableRow>
                      <TableRow className="bg-muted/30">
                        <TableCell className="text-xs py-2">
                          <Badge className="bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-400 text-[9px]">Used Result</Badge>
                        </TableCell>
                        <TableCell className="text-xs py-2 font-bold">
                          {testResults.cascade?.speed_limit != null ? `${testResults.cascade.speed_limit} km/h` : '—'}
                        </TableCell>
                        <TableCell className="text-xs py-2 text-muted-foreground" colSpan={2}>
                          {testResults.cascade?.source
                            ? `Using: ${testResults.cascade.source === 'osm' ? 'Overpass (OSM)' : 'OSRM Estimated'}`
                            : 'No data available'}
                        </TableCell>
                        <TableCell className="text-xs py-2">
                          <span className={`text-[10px] ${testResults.cascade?.speed_limit != null ? 'text-green-600 font-medium' : 'text-red-600'}`}>
                            {testResults.cascade?.speed_limit != null ? 'Active' : 'No result'}
                          </span>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ==================== OSRM Speed Profile Overrides ==================== */}
          <Card data-testid="osrm-speed-overrides-panel">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Gauge className="w-4 h-4" />
                    OSRM Speed Profile Overrides
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">Custom speed limits per road type — used when Overpass has no data (2nd fallback)</p>
                </div>
                {osrmOverrides && (
                  <Switch
                    checked={osrmOverrides.enabled ?? true}
                    onCheckedChange={(v) => setOsrmOverrides(prev => ({ ...prev, enabled: v }))}
                    data-testid="osrm-overrides-enabled-toggle"
                  />
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              {osrmOverrides && !(osrmOverrides.enabled ?? true) && (
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                  <p className="text-xs text-amber-700 dark:text-amber-400">OSRM Speed Profile is disabled — estimated speed limits will not be used as fallback. Toggle ON above to re-enable.</p>
                </div>
              )}
              {osrmOverrides && (
                <>
                  {/* Country selector */}
                  <div className="flex items-center gap-3">
                    <Label className="text-xs whitespace-nowrap">Country Profile:</Label>
                    <Select value={overrideCountry} onValueChange={setOverrideCountry}>
                      <SelectTrigger className="h-8 text-xs flex-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.keys(osrmOverrides.countries || {}).map(key => (
                          <SelectItem key={key} value={key}>{key === '_default' ? 'Default (All Countries)' : key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" className="text-xs h-8" onClick={() => { setAddCountryName(''); setAddCountryOpen(true); }}>
                      <Plus className="w-3 h-3 mr-1" /> Add Country
                    </Button>
                  </div>

                  {/* Speed override table */}
                  {osrmOverrides.countries?.[overrideCountry] && (
                    <div className="rounded-lg border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead className="text-[10px] py-2">Road Type</TableHead>
                            <TableHead className="text-[10px] py-2">OSRM Default</TableHead>
                            <TableHead className="text-[10px] py-2">Your Speed Limit</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Object.entries(osrmOverrides.road_types || {}).map(([key, info]) => (
                            <TableRow key={key}>
                              <TableCell className="py-2">
                                <p className="text-xs font-medium">{info.label}</p>
                                <p className="text-[10px] text-muted-foreground">{info.description}</p>
                              </TableCell>
                              <TableCell className="text-xs py-2 text-muted-foreground">{info.osrm_default} km/h</TableCell>
                              <TableCell className="py-2">
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    value={osrmOverrides.countries[overrideCountry]?.[key] || ''}
                                    onChange={(e) => setOsrmOverrides(prev => ({
                                      ...prev,
                                      countries: {
                                        ...prev.countries,
                                        [overrideCountry]: {
                                          ...prev.countries[overrideCountry],
                                          [key]: e.target.value ? Number(e.target.value) : null,
                                        },
                                      },
                                    }))}
                                    className="w-20 h-7 text-xs px-2"
                                  />
                                  <span className="text-[10px] text-muted-foreground">km/h</span>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  <div className="p-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                    <p className="text-[10px] text-amber-700 dark:text-amber-400">
                      These values are used as a last resort when Overpass (OSM) has no speed data for a road. The "Estimated" badge will appear in the telemetry panel.
                    </p>
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-2">
                    {overrideCountry !== '_default' && (
                      <Button variant="outline" size="sm" className="text-xs text-red-600 hover:text-red-700" onClick={() => {
                        setOsrmOverrides(prev => {
                          const next = { ...prev, countries: { ...prev.countries } };
                          delete next.countries[overrideCountry];
                          return next;
                        });
                        setOverrideCountry('_default');
                      }}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove Country
                      </Button>
                    )}
                    <Button size="sm" onClick={handleSaveOsrmOverrides} disabled={osrmOverridesSaving} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="osrm-overrides-save-btn">
                      {osrmOverridesSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Speed Overrides
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Add Speed Override Country Dialog */}
          <Dialog open={addCountryOpen} onOpenChange={setAddCountryOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Add Country Profile</DialogTitle>
                <DialogDescription>Select a country from your OSRM database to create a speed profile override.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <Label className="text-xs">Country</Label>
                  <Select value={addCountryName} onValueChange={setAddCountryName}>
                    <SelectTrigger className="mt-1 text-xs"><SelectValue placeholder="Select a country..." /></SelectTrigger>
                    <SelectContent>
                      {(osrmConfig?.countries || [])
                        .filter(c => !osrmOverrides?.countries?.[c.slug] && !osrmOverrides?.countries?.[c.display_name?.toLowerCase().replace(/\s+/g, '_')])
                        .map(c => (
                          <SelectItem key={c.slug} value={c.display_name}>{c.display_name}</SelectItem>
                        ))
                      }
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" size="sm" onClick={() => setAddCountryOpen(false)}>Cancel</Button>
                <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8]" onClick={() => {
                  if (addCountryName.trim()) {
                    handleAddOverrideCountry(addCountryName);
                    setAddCountryOpen(false);
                  }
                }} disabled={!addCountryName.trim()}>
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Country
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Add Country Dialog */}
          <Dialog open={osrmAddOpen} onOpenChange={setOsrmAddOpen}>
            <DialogContent className="max-w-lg max-h-[85vh] flex flex-col p-0" data-testid="osrm-add-country-dialog">
              <DialogHeader className="px-5 pt-5 pb-3">
                <DialogTitle>Add Country / Region</DialogTitle>
                <DialogDescription>Select a region from the Geofabrik database to add to your OSRM configuration.</DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto px-5 pb-5" style={{ maxHeight: '65vh' }}>
                <div className="space-y-5">
                  {osrmConfig?.available_regions && Object.entries(osrmConfig.available_regions).map(([continent, countries]) => (
                    <div key={continent}>
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 sticky top-0 bg-background py-1">{continent}</h4>
                      <div className="grid grid-cols-2 gap-1">
                        {countries.map((slug) => {
                          const alreadyAdded = osrmConfig.countries?.some(c => c.slug === slug);
                          return (
                            <button
                              key={slug}
                              onClick={() => !alreadyAdded && handleAddOsrmCountry(continent, slug)}
                              disabled={alreadyAdded}
                              className={`text-left text-xs px-2.5 py-1.5 rounded border transition-colors ${
                                alreadyAdded
                                  ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 cursor-default'
                                  : 'hover:bg-muted/60 border-transparent hover:border-border cursor-pointer'
                              }`}
                              data-testid={`osrm-region-${slug}`}
                            >
                              {alreadyAdded && <CheckCircle className="w-3 h-3 inline mr-1" />}
                              {slug.replace(/-/g, ' ').replace(/\bus\//g, 'US/').replace(/\b\w/g, c => c.toUpperCase())}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Nominatim Add Country Dialog */}
          <Dialog open={nominatimAddOpen} onOpenChange={setNominatimAddOpen}>
            <DialogContent className="max-w-lg max-h-[85vh] flex flex-col p-0" data-testid="nominatim-add-country-dialog">
              <DialogHeader className="px-5 pt-5 pb-3">
                <DialogTitle>Add Country / Region</DialogTitle>
                <DialogDescription>Select a region from the Geofabrik database to add to your Nominatim configuration.</DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto px-5 pb-5" style={{ maxHeight: '65vh' }}>
                <div className="space-y-5">
                  {nominatimConfig?.available_regions && Object.entries(nominatimConfig.available_regions).map(([continent, countries]) => (
                    <div key={continent}>
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 sticky top-0 bg-background py-1">{continent}</h4>
                      <div className="grid grid-cols-2 gap-1">
                        {countries.map((slug) => {
                          const alreadyAdded = nominatimConfig.countries?.some(c => c.slug === slug);
                          return (
                            <button
                              key={slug}
                              onClick={() => !alreadyAdded && handleAddNominatimCountry(continent, slug)}
                              disabled={alreadyAdded}
                              className={`text-left text-xs px-2.5 py-1.5 rounded border transition-colors ${
                                alreadyAdded
                                  ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 cursor-default'
                                  : 'hover:bg-muted/60 border-transparent hover:border-border cursor-pointer'
                              }`}
                              data-testid={`nominatim-region-${slug}`}
                            >
                              {alreadyAdded && <CheckCircle className="w-3 h-3 inline mr-1" />}
                              {slug.replace(/-/g, ' ').replace(/\bus\//g, 'US/').replace(/\b\w/g, c => c.toUpperCase())}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}
      {/* ==================== API & WEBHOOKS TAB ==================== */}
      {activeTab === 'api' && (
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Key className="w-5 h-5" />
              API & Webhooks (Global)
            </h2>
            <p className="text-sm text-muted-foreground mt-1">Platform-wide API configuration and rate limits</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Global API Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Rate Limit (Per Tenant/Hour)</Label>
                  <Input
                    type="number"
                    value={apiConfig.rate_limit_per_tenant}
                    onChange={(e) => setApiConfig(prev => ({ ...prev, rate_limit_per_tenant: parseInt(e.target.value) }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Rate Limit (Per API Key/Hour)</Label>
                  <Input
                    type="number"
                    value={apiConfig.rate_limit_per_key}
                    onChange={(e) => setApiConfig(prev => ({ ...prev, rate_limit_per_key: parseInt(e.target.value) }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label>API Version</Label>
                  <Select
                    value={apiConfig.api_version}
                    onValueChange={(v) => setApiConfig(prev => ({ ...prev, api_version: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="v1">v1 (Current)</SelectItem>
                      <SelectItem value="v2">v2 (Beta)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div>
                  <p className="font-medium text-sm">Enable Request Logging</p>
                  <p className="text-xs text-muted-foreground">Log all API requests for debugging</p>
                </div>
                <Switch
                  checked={apiConfig.enable_request_logging}
                  onCheckedChange={(v) => setApiConfig(prev => ({ ...prev, enable_request_logging: v }))}
                />
              </div>

              <div className="flex justify-end">
                <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8]">
                  <Save className="w-4 h-4 mr-2" />
                  Save API Configuration
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Webhook System Config */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Webhook System Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Max Retry Attempts</Label>
                  <Input type="number" defaultValue="3" />
                </div>

                <div className="space-y-2">
                  <Label>Backoff Strategy</Label>
                  <Select defaultValue="exponential">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="linear">Linear</SelectItem>
                      <SelectItem value="exponential">Exponential</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Timeout (seconds)</Label>
                  <Input type="number" defaultValue="30" />
                </div>
              </div>

              <div className="flex justify-end">
                <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8]">
                  <Save className="w-4 h-4 mr-2" />
                  Save Webhook Configuration
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ==================== FUEL CARDS TAB ==================== */}
      {activeTab === 'fuel' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Fuel className="w-5 h-5" />
                Fuel Card Integration
              </h2>
              <p className="text-sm text-muted-foreground mt-1">Configure fuel card provider integrations</p>
            </div>
            <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8]">
              <Plus className="w-4 h-4 mr-2" />
              Add Provider
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Fuel Card Providers</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Provider</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Sync</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mockFuelCardProviders.map((provider) => (
                    <TableRow key={provider.id}>
                      <TableCell className="font-medium">{provider.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-green-100 text-green-700">{provider.status}</Badge>
                      </TableCell>
                      <TableCell>{provider.last_sync}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline">Configure</Button>
                          <Button size="sm" variant="outline">Sync Now</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ==================== SIM PROVIDER TAB ==================== */}
      {activeTab === 'sim' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Smartphone className="w-5 h-5" />
                SIM Provider Management
              </h2>
              <p className="text-sm text-muted-foreground mt-1">Manage SIM providers for device connectivity</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button size="sm" 
                onClick={() => { resetSimProviderForm(); setSimCards([]); setIsAddSimProviderOpen(true); }}
                className="bg-[#2563eb] hover:bg-[#1d4ed8]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add SIM Provider
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search providers..."
              value={simProviderSearch}
              onChange={(e) => setSimProviderSearch(e.target.value)}
              className="pl-10 pr-8"
            />
            {simProviderSearch && (
              <button className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setSimProviderSearch('')}>
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">SIM Providers ({simProviders.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Provider Name</TableHead>
                    <TableHead className="text-center">Total SIMs</TableHead>
                    <TableHead className="text-center">Activated</TableHead>
                    <TableHead className="text-center">Deactivated</TableHead>
                    <TableHead className="text-center">In Session</TableHead>
                    <TableHead className="text-center">Activated No Session & No Data for 60 Days</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Sync Status</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {simProviders
                  .filter(p => !simProviderSearch || p.name?.toLowerCase().includes(simProviderSearch.toLowerCase()) || p.description?.toLowerCase().includes(simProviderSearch.toLowerCase()) || p.provider_type?.toLowerCase().includes(simProviderSearch.toLowerCase()))
                  .map(provider => (
                    <TableRow key={provider.id}>
                      <TableCell className="font-medium">
                        <button
                          className="text-left font-medium hover:underline hover:text-blue-600 cursor-pointer"
                          onClick={() => openSimCardsView(provider)}
                          title="View SIM Cards"
                          data-testid={`sim-provider-name-link-${provider.id}`}
                        >
                          {provider.name}
                        </button>
                      </TableCell>
                      <TableCell className="text-center font-medium">{provider.sim_total ?? '-'}</TableCell>
                      <TableCell className="text-center font-medium text-green-600">{provider.sim_active ?? '-'}</TableCell>
                      <TableCell className="text-center font-medium text-red-600">{provider.sim_inactive ?? '-'}</TableCell>
                      <TableCell className="text-center font-medium text-blue-600">{provider.sim_in_session ?? '-'}</TableCell>
                      <TableCell className="text-center font-medium text-orange-600">{provider.sim_no_session ?? '-'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{provider.description || '-'}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5 items-start">
                          <SimSyncStatusChip provider={provider} />
                          {simLastSyncISO(provider) && (
                            <span className="text-[10px] text-muted-foreground whitespace-nowrap">{formatDT(simLastSyncISO(provider))}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {provider.is_active ? (
                          <Badge className="bg-green-600 text-white">Active</Badge>
                        ) : (
                          <Badge className="bg-gray-500 text-white">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => openSimCardsView(provider)}
                            title="View SIM Cards"
                            data-testid={`sim-provider-view-btn-${provider.id}`}
                          >
                            <Smartphone className="w-4 h-4 text-blue-600" />
                          </Button>
                          <span title={provider.provider_type === 'manual' || !provider.api_enabled ? 'Manual provider — no sync' : undefined}>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              disabled={provider.provider_type === 'manual' || !provider.api_enabled}
                              onClick={() => openSimSyncLogs(provider)}
                              title={provider.provider_type === 'manual' || !provider.api_enabled ? 'Manual provider — no sync' : 'SIM Change Log'}
                              data-testid={`sim-provider-sync-log-btn-${provider.id}`}
                            >
                              <History className={`w-4 h-4 ${provider.provider_type === 'manual' || !provider.api_enabled ? 'text-muted-foreground' : 'text-amber-600'}`} />
                            </Button>
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => openEditSimProvider(provider)}
                            title="Edit"
                            data-testid={`sim-provider-edit-btn-${provider.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => handleDeleteSimProvider(provider.id)}
                            title="Delete"
                            data-testid={`sim-provider-delete-btn-${provider.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {simProviders.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No SIM providers configured. Click &quot;Add SIM Provider&quot; to get started.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          
          {/* SIM Change Log Dialog */}
          <Dialog open={!!simSyncLogProvider} onOpenChange={(o) => { if (!o) setSimSyncLogProvider(null); }}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="sim-sync-log-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between pr-6">
                  <span>SIM Change Log — {simSyncLogProvider?.name}</span>
                  <Button variant="outline" size="sm" onClick={exportSimSyncLogsCsv} disabled={simSyncLogsTotal === 0} data-testid="sim-sync-log-export-btn">
                    <Download className="w-3.5 h-3.5 mr-1.5" />
                    Export CSV
                  </Button>
                </DialogTitle>
              </DialogHeader>
              {simSyncLogsLoading ? (
                <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
              ) : simSyncLogs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8" data-testid="sim-sync-log-empty">
                  No sync history yet. Entries appear after each sync run (quick sync, full sync or scheduled usage sync).
                </p>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Completed</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-center">Added</TableHead>
                        <TableHead className="text-center">Removed</TableHead>
                        <TableHead className="text-center">Updated</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {simSyncLogs.map((log, i) => {
                        const hasDetails = (log.added_iccids?.length || 0) > 0 || (log.removed_iccids?.length || 0) > 0;
                        const expanded = expandedSyncLogId === log.id;
                        const typeLabel = { quick_sync: 'Quick Sync', full_sync: 'Full Sync', usage_sync: 'Usage Sync' }[log.sync_type] || log.sync_type;
                        return (
                          <React.Fragment key={log.id}>
                            <TableRow
                              className={hasDetails ? 'cursor-pointer hover:bg-muted/50' : ''}
                              onClick={() => hasDetails && setExpandedSyncLogId(expanded ? null : log.id)}
                              data-testid={`sim-sync-log-row-${i}`}
                            >
                              <TableCell className="text-xs whitespace-nowrap">{formatDT(log.completed_at)}</TableCell>
                              <TableCell><Badge variant="outline" className="text-[10px]">{typeLabel}</Badge></TableCell>
                              <TableCell className={`text-center font-medium ${log.added_count ? 'text-green-600' : 'text-muted-foreground'}`}>{log.added_count || 0}</TableCell>
                              <TableCell className={`text-center font-medium ${log.removed_count ? 'text-red-600' : 'text-muted-foreground'}`}>{log.removed_count || 0}</TableCell>
                              <TableCell className="text-center text-muted-foreground">{log.updated_count || 0}</TableCell>
                              <TableCell>
                                <Badge className={`text-[10px] ${log.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300 hover:bg-green-100' : 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300 hover:bg-red-100'}`}>
                                  {log.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                {hasDetails && (
                                  <span className="text-xs text-blue-600" data-testid={`sim-sync-log-expand-${i}`}>{expanded ? 'Hide' : 'View'}</span>
                                )}
                              </TableCell>
                            </TableRow>
                            {expanded && (
                              <TableRow>
                                <TableCell colSpan={7} className="bg-muted/30">
                                  {(log.removed_iccids?.length || 0) > 0 && (
                                    <div className="mb-2" data-testid="sim-sync-log-removed-iccids">
                                      <p className="text-xs font-medium text-red-600 mb-1">Removed ICCIDs ({log.removed_count || log.removed_iccids.length})</p>
                                      <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
                                        {(log.removed_details?.length ? log.removed_details : log.removed_iccids.map((i) => ({ iccid: i, mobile_number: '' }))).map((d) => (
                                          <span key={d.iccid} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300">{d.iccid}{d.mobile_number ? ` · ${d.mobile_number}` : ''}</span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {(log.added_iccids?.length || 0) > 0 && (
                                    <div data-testid="sim-sync-log-added-iccids">
                                      <p className="text-xs font-medium text-green-600 mb-1">Added ICCIDs ({log.added_count || log.added_iccids.length})</p>
                                      <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
                                        {log.added_iccids.map((icc) => (
                                          <span key={icc} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300">{icc}</span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <div className="flex items-center justify-between pt-2">
                    <span className="text-xs text-muted-foreground">
                      {simSyncLogsSkip + 1}–{Math.min(simSyncLogsSkip + SIM_SYNC_LOGS_PAGE, simSyncLogsTotal)} of {simSyncLogsTotal}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline" size="sm" disabled={simSyncLogsSkip === 0}
                        onClick={() => fetchSimSyncLogs(simSyncLogProvider.id, Math.max(0, simSyncLogsSkip - SIM_SYNC_LOGS_PAGE))}
                        data-testid="sim-sync-log-prev"
                      >Previous</Button>
                      <Button
                        variant="outline" size="sm" disabled={simSyncLogsSkip + SIM_SYNC_LOGS_PAGE >= simSyncLogsTotal}
                        onClick={() => fetchSimSyncLogs(simSyncLogProvider.id, simSyncLogsSkip + SIM_SYNC_LOGS_PAGE)}
                        data-testid="sim-sync-log-next"
                      >Next</Button>
                    </div>
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>

          {/* Add SIM Provider Dialog */}
          <Dialog open={isAddSimProviderOpen} onOpenChange={setIsAddSimProviderOpen}>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle>Add New SIM Provider</DialogTitle>
              </DialogHeader>
              
              {/* Tabs */}
              <div className="flex items-center gap-2 border-b">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveSimProviderTab('details')}
                  className={activeSimProviderTab === 'details' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
                >
                  Provider Details
                </Button>
                {simProviderForm.provider_type !== 'manual' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setActiveSimProviderTab('api')}
                    className={activeSimProviderTab === 'api' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
                  >
                    API Setup
                  </Button>
                )}
                {simProviderForm.provider_type && simProviderForm.provider_type !== 'manual' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setActiveSimProviderTab('sync')}
                    className={activeSimProviderTab === 'sync' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
                  >
                    Sync Schedule
                  </Button>
                )}
              </div>
              
              <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                {/* Tab 1: Provider Details */}
                {activeSimProviderTab === 'details' && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Provider Type *</Label>
                      <Select value={simProviderForm.provider_type} onValueChange={(v) => {
                        const defaults = {
                          jasper: { api_endpoint: 'https://restapi.jasper.com', api_auth_type: 'basic', api_enabled: true },
                          truphone: { api_endpoint: 'https://iot.truphone.com', api_auth_type: 'bearer', api_enabled: true },
                          '1nce': { api_endpoint: 'https://api.1nce.com/management-api', api_auth_type: 'bearer', api_enabled: true },
                          manual: { api_endpoint: '', api_key: '', api_enabled: false },
                        };
                        setSimProviderForm({...simProviderForm, provider_type: v, ...(defaults[v] || {})});
                      }}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select provider type..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="jasper">M2M One / Cisco Jasper</SelectItem>
                          <SelectItem value="truphone">Truphone / 1Global</SelectItem>
                          <SelectItem value="1nce">1NCE</SelectItem>
                          <SelectItem value="manual">Manual (No API)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Provider Name *</Label>
                      <Input
                        value={simProviderForm.name}
                        onChange={(e) => setSimProviderForm({...simProviderForm, name: e.target.value})}
                        placeholder="e.g., M2M One, Truphone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={simProviderForm.description}
                        onChange={(e) => setSimProviderForm({...simProviderForm, description: e.target.value})}
                        placeholder="Brief description of the provider..."
                        rows={3}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Contact Email</Label>
                        <Input
                          type="email"
                          value={simProviderForm.contact_email}
                          onChange={(e) => setSimProviderForm({...simProviderForm, contact_email: e.target.value})}
                          placeholder="support@provider.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Contact Phone</Label>
                        <Input
                          value={simProviderForm.contact_phone}
                          onChange={(e) => setSimProviderForm({...simProviderForm, contact_phone: e.target.value})}
                          placeholder="+61 2 1234 5678"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Account Number</Label>
                      <Input
                        value={simProviderForm.account_number || ''}
                        onChange={(e) => setSimProviderForm({...simProviderForm, account_number: e.target.value})}
                        placeholder="Provider account / customer reference number"
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg border">
                      <div>
                        <p className="font-medium text-sm">Active Provider</p>
                        <p className="text-xs text-muted-foreground">Make this provider available for device assignment</p>
                      </div>
                      <Switch
                        checked={simProviderForm.is_active}
                        onCheckedChange={(v) => setSimProviderForm({...simProviderForm, is_active: v})}
                      />
                    </div>
                  </div>
                )}
                
                {/* Tab 2: API Setup */}
                {activeSimProviderTab === 'api' && (
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200">
                      <p className="text-sm">
                        Configure API integration to enable real-time SIM management features including:
                        status checking, activation/deactivation, tower location tracking, and usage monitoring.
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 rounded-lg border">
                      <div>
                        <p className="font-medium text-sm">Enable API Integration</p>
                        <p className="text-xs text-muted-foreground">Allow system to communicate with provider&apos;s API</p>
                      </div>
                      <Switch
                        checked={simProviderForm.api_enabled}
                        onCheckedChange={(v) => setSimProviderForm({...simProviderForm, api_enabled: v})}
                      />
                    </div>
                    
                    {simProviderForm.api_enabled && (
                      <>
                        <div className="space-y-2">
                          <Label>API Endpoint URL</Label>
                          <Input
                            value={simProviderForm.api_endpoint}
                            onChange={(e) => setSimProviderForm({...simProviderForm, api_endpoint: e.target.value})}
                            placeholder="https://api.provider.com/v1"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Authentication Type</Label>
                          <Select 
                            value={simProviderForm.api_auth_type} 
                            onValueChange={(v) => setSimProviderForm({...simProviderForm, api_auth_type: v})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="bearer">Bearer Token</SelectItem>
                              <SelectItem value="api_key">API Key</SelectItem>
                              <SelectItem value="oauth">OAuth 2.0</SelectItem>
                              <SelectItem value="basic">Basic Auth</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>API Key / Token</Label>
                          <Input
                            type="password"
                            value={simProviderForm.api_key}
                            onChange={(e) => setSimProviderForm({...simProviderForm, api_key: e.target.value})}
                            placeholder={
                              simProviderForm.provider_type === 'jasper' ? 'username:password' :
                              simProviderForm.provider_type === 'truphone' ? 'API Token from Truphone Portal' :
                              simProviderForm.provider_type === '1nce' ? 'username:password (Management API credentials)' :
                              'Enter your API key or token'
                            }
                          />
                          <p className="text-xs text-muted-foreground">
                            {simProviderForm.provider_type === 'jasper' && 'Format: username:password — from M2M One / Jasper Control Center API settings'}
                            {simProviderForm.provider_type === 'truphone' && 'Bearer Token — from Truphone IoT Portal > API Settings'}
                            {simProviderForm.provider_type === '1nce' && 'Format: username:password — from 1NCE Portal > Management API'}
                          </p>
                        </div>
                        
                        {simProviderForm.provider_type === '1nce' && (
                          <div className="space-y-2">
                            <Label>SMS Source Address</Label>
                            <Input
                              data-testid="add-sms-source-address"
                              value={simProviderForm.sms_source_address}
                              onChange={(e) => setSimProviderForm({...simProviderForm, sms_source_address: e.target.value})}
                              placeholder="e.g. 12345"
                            />
                            <p className="text-xs text-muted-foreground">
                              Required for sending SMS via 1NCE API. This is the sender ID visible to the SIM device (max 11 characters).
                            </p>
                          </div>
                        )}

                        <div className="flex items-center justify-between p-3 rounded-lg border">
                          <div>
                            <p className="font-medium text-sm">Auto-Sync SIM Status</p>
                            <p className="text-xs text-muted-foreground">Automatically sync SIM card status every 15 minutes</p>
                          </div>
                          <Switch
                            checked={simProviderForm.auto_sync_enabled}
                            onCheckedChange={(v) => setSimProviderForm({...simProviderForm, auto_sync_enabled: v})}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
                
                {/* Tab 3: Sync Schedule (Add dialog) */}
                {activeSimProviderTab === 'sync' && (
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200">
                      <p className="text-sm">Configure automatic usage data sync schedule.</p>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg border">
                      <div>
                        <p className="font-medium text-sm">Enable Usage Sync</p>
                        <p className="text-xs text-muted-foreground">Automatically sync data usage on a schedule</p>
                      </div>
                      <Switch checked={simProviderForm.usage_sync_enabled} onCheckedChange={(v) => setSimProviderForm({...simProviderForm, usage_sync_enabled: v})} />
                    </div>
                    {simProviderForm.usage_sync_enabled && (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Frequency</Label>
                            <Select value={simProviderForm.usage_sync_frequency} onValueChange={(v) => setSimProviderForm({...simProviderForm, usage_sync_frequency: v})}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="daily">Daily</SelectItem>
                                <SelectItem value="every_2_days">Every 2 Days</SelectItem>
                                <SelectItem value="every_3_days">Every 3 Days</SelectItem>
                                <SelectItem value="weekly">Weekly</SelectItem>
                                <SelectItem value="fortnightly">Fortnightly</SelectItem>
                                <SelectItem value="monthly">Monthly</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Sync Time (Sydney)</Label>
                            <Input type="time" value={simProviderForm.usage_sync_time} onChange={(e) => setSimProviderForm({...simProviderForm, usage_sync_time: e.target.value})} />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <Button size="sm" variant="outline" onClick={() => { setIsAddSimProviderOpen(false); resetSimProviderForm(); }}>Cancel</Button>
                <Button size="sm" onClick={handleAddSimProvider} disabled={saving} className="bg-[#2563eb] hover:bg-[#1d4ed8]">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Add Provider
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          {/* Edit SIM Provider Dialog */}
          <Dialog open={isEditSimProviderOpen} onOpenChange={setIsEditSimProviderOpen}>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle>Edit SIM Provider: {selectedSimProvider?.name}</DialogTitle>
              </DialogHeader>
              
              {/* Tabs */}
              <div className="flex items-center gap-2 border-b">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveSimProviderTab('details')}
                  className={activeSimProviderTab === 'details' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
                >
                  Provider Details
                </Button>
                {simProviderForm.provider_type !== 'manual' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setActiveSimProviderTab('api')}
                    className={activeSimProviderTab === 'api' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
                  >
                    API Setup
                  </Button>
                )}
                {simProviderForm.provider_type && simProviderForm.provider_type !== 'manual' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setActiveSimProviderTab('sync')}
                    className={activeSimProviderTab === 'sync' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
                  >
                    Sync Schedule
                  </Button>
                )}
                {simProviderForm.provider_type && simProviderForm.provider_type !== 'manual' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="sim-provider-alerts-tab"
                    onClick={() => setActiveSimProviderTab('alerts')}
                    className={activeSimProviderTab === 'alerts' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
                  >
                    Alerts
                  </Button>
                )}
                {simProviderForm.provider_type === '1nce' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="data-streamer-tab"
                    onClick={() => {
                      setActiveSimProviderTab('streamer');
                      if (selectedSimProvider?.id && dataStreamerEvents.length === 0) {
                        setLoadingStreamerEvents(true);
                        api.get(`/admin/data-streamer/events/${selectedSimProvider.id}?limit=20`)
                          .then(res => setDataStreamerEvents(res.data?.events || []))
                          .catch(() => {})
                          .finally(() => setLoadingStreamerEvents(false));
                      }
                    }}
                    className={activeSimProviderTab === 'streamer' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
                  >
                    Data Streamer
                  </Button>
                )}
              </div>
              
              <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                {/* Tab 1: Provider Details */}
                {activeSimProviderTab === 'details' && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Provider Type</Label>
                      <Select value={simProviderForm.provider_type} onValueChange={(v) => {
                        const defaults = {
                          jasper: { api_endpoint: 'https://restapi.jasper.com', api_auth_type: 'basic', api_enabled: true },
                          truphone: { api_endpoint: 'https://iot.truphone.com', api_auth_type: 'bearer', api_enabled: true },
                          '1nce': { api_endpoint: 'https://api.1nce.com/management-api', api_auth_type: 'bearer', api_enabled: true },
                          manual: { api_endpoint: '', api_key: '', api_enabled: false },
                        };
                        setSimProviderForm({...simProviderForm, provider_type: v, ...(defaults[v] || {})});
                      }}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select provider type..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="jasper">M2M One / Cisco Jasper</SelectItem>
                          <SelectItem value="truphone">Truphone / 1Global</SelectItem>
                          <SelectItem value="1nce">1NCE</SelectItem>
                          <SelectItem value="manual">Manual (No API)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Provider Name *</Label>
                      <Input
                        value={simProviderForm.name}
                        onChange={(e) => setSimProviderForm({...simProviderForm, name: e.target.value})}
                        placeholder="e.g., M2M One, Truphone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={simProviderForm.description}
                        onChange={(e) => setSimProviderForm({...simProviderForm, description: e.target.value})}
                        placeholder="Brief description of the provider..."
                        rows={3}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Contact Email</Label>
                        <Input
                          type="email"
                          value={simProviderForm.contact_email}
                          onChange={(e) => setSimProviderForm({...simProviderForm, contact_email: e.target.value})}
                          placeholder="support@provider.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Contact Phone</Label>
                        <Input
                          value={simProviderForm.contact_phone}
                          onChange={(e) => setSimProviderForm({...simProviderForm, contact_phone: e.target.value})}
                          placeholder="+61 2 1234 5678"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Account Number</Label>
                      <Input
                        value={simProviderForm.account_number || ''}
                        onChange={(e) => setSimProviderForm({...simProviderForm, account_number: e.target.value})}
                        placeholder="Provider account / customer reference number"
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg border">
                      <div>
                        <p className="font-medium text-sm">Active Provider</p>
                        <p className="text-xs text-muted-foreground">Make this provider available for device assignment</p>
                      </div>
                      <Switch
                        checked={simProviderForm.is_active}
                        onCheckedChange={(v) => setSimProviderForm({...simProviderForm, is_active: v})}
                      />
                    </div>
                  </div>
                )}
                
                {/* Tab 2: API Setup */}
                {activeSimProviderTab === 'api' && (
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200">
                      <p className="text-sm">
                        Configure API integration to enable real-time SIM management features including:
                        status checking, activation/deactivation, tower location tracking, and usage monitoring.
                      </p>
                    </div>
                    
                    {selectedSimProvider?.api_status && (
                      <div className={`p-3 rounded-lg border ${
                        selectedSimProvider.api_status === 'connected' 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex items-center gap-2">
                          <CheckCircle className={`w-4 h-4 ${
                            selectedSimProvider.api_status === 'connected' ? 'text-green-600' : 'text-red-600'
                          }`} />
                          <p className="text-sm font-medium">
                            API Status: {selectedSimProvider.api_status === 'connected' ? 'Connected' : 'Disconnected'}
                          </p>
                        </div>
                        {selectedSimProvider.api_last_sync && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Last sync: {formatDT(selectedSimProvider.api_last_sync)}
                          </p>
                        )}
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between p-3 rounded-lg border">
                      <div>
                        <p className="font-medium text-sm">Enable API Integration</p>
                        <p className="text-xs text-muted-foreground">Allow system to communicate with provider&apos;s API</p>
                      </div>
                      <Switch
                        checked={simProviderForm.api_enabled}
                        onCheckedChange={(v) => setSimProviderForm({...simProviderForm, api_enabled: v})}
                      />
                    </div>
                    
                    {simProviderForm.api_enabled && (
                      <>
                        <div className="space-y-2">
                          <Label>API Endpoint URL</Label>
                          <Input
                            value={simProviderForm.api_endpoint}
                            onChange={(e) => setSimProviderForm({...simProviderForm, api_endpoint: e.target.value})}
                            placeholder="https://api.provider.com/v1"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Authentication Type</Label>
                          <Select 
                            value={simProviderForm.api_auth_type} 
                            onValueChange={(v) => setSimProviderForm({...simProviderForm, api_auth_type: v})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="bearer">Bearer Token</SelectItem>
                              <SelectItem value="api_key">API Key</SelectItem>
                              <SelectItem value="oauth">OAuth 2.0</SelectItem>
                              <SelectItem value="basic">Basic Auth</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>API Key / Token</Label>
                          <Input
                            type="password"
                            value={simProviderForm.api_key}
                            onChange={(e) => setSimProviderForm({...simProviderForm, api_key: e.target.value})}
                            placeholder={
                              simProviderForm.provider_type === 'jasper' ? 'username:password' :
                              simProviderForm.provider_type === 'truphone' ? 'API Token from Truphone Portal' :
                              simProviderForm.provider_type === '1nce' ? 'username:password (Management API credentials)' :
                              'Enter your API key or token'
                            }
                          />
                          <p className="text-xs text-muted-foreground">
                            {simProviderForm.provider_type === 'jasper' && 'Format: username:password — from M2M One / Jasper Control Center API settings'}
                            {simProviderForm.provider_type === 'truphone' && 'Bearer Token — from Truphone IoT Portal > API Settings'}
                            {simProviderForm.provider_type === '1nce' && 'Format: username:password — from 1NCE Portal > Management API'}
                          </p>
                        </div>
                        
                        {simProviderForm.provider_type === '1nce' && (
                          <div className="space-y-2">
                            <Label>SMS Source Address</Label>
                            <Input
                              data-testid="add-sms-source-address"
                              value={simProviderForm.sms_source_address}
                              onChange={(e) => setSimProviderForm({...simProviderForm, sms_source_address: e.target.value})}
                              placeholder="e.g. 12345"
                            />
                            <p className="text-xs text-muted-foreground">
                              Required for sending SMS via 1NCE API. This is the sender ID visible to the SIM device (max 11 characters).
                            </p>
                          </div>
                        )}

                        <div className="flex items-center justify-between p-3 rounded-lg border">
                          <div>
                            <p className="font-medium text-sm">Auto-Sync SIM Status</p>
                            <p className="text-xs text-muted-foreground">Automatically sync SIM card status every 15 minutes</p>
                          </div>
                          <Switch
                            checked={simProviderForm.auto_sync_enabled}
                            onCheckedChange={(v) => setSimProviderForm({...simProviderForm, auto_sync_enabled: v})}
                          />
                        </div>
                        
                        <div className="pt-4 border-t">
                          <Button 
                            variant="outline" 
                            onClick={() => handleTestConnection(selectedSimProvider?.id)}
                            disabled={testingConnection}
                            className="w-full"
                          >
                            {testingConnection ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Testing Connection...
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Test API Connection
                              </>
                            )}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}
                
                {/* Tab 3: Sync Schedule */}
                {activeSimProviderTab === 'sync' && (
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200">
                      <p className="text-sm">
                        Configure automatic usage data sync schedule. This fetches data usage reports from the provider API and updates SIM card usage records.
                      </p>
                      <p className="text-sm mt-2 font-medium">
                        Last completed sync: {simProviderForm.usage_last_synced 
                          ? formatDT(simProviderForm.usage_last_synced) 
                          : <span className="text-muted-foreground font-normal">Never</span>}
                      </p>
                    </div>

                    {/* Run Sync Now button */}
                    <Button
                      variant="outline"
                      onClick={async () => {
                        setSaving(true);
                        try {
                          const response = await api.post(`/admin/sim-providers/${selectedSimProvider.id}/sync-usage`);
                          toast.success(response.data.message);
                          const providersRes = await api.get('/admin/sim-providers');
                          setSimProviders((providersRes.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
                          const updated = (providersRes.data || []).find(p => p.id === selectedSimProvider.id);
                          if (updated) {
                            setSimProviderForm(prev => ({...prev, usage_last_synced: updated.usage_last_synced}));
                          }
                        } catch (e) {
                          toast.error(getApiErrorMessage(e) || 'Sync failed');
                        } finally {
                          setSaving(false);
                        }
                      }}
                      disabled={saving}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${saving ? 'animate-spin' : ''}`} />
                      {saving ? 'Syncing...' : 'Run Sync Now'}
                    </Button>

                    <div className="flex items-center justify-between p-3 rounded-lg border">
                      <div>
                        <p className="font-medium text-sm">Enable Usage Sync</p>
                        <p className="text-xs text-muted-foreground">Automatically sync data usage on a schedule</p>
                      </div>
                      <Switch
                        checked={simProviderForm.usage_sync_enabled}
                        onCheckedChange={(v) => setSimProviderForm({...simProviderForm, usage_sync_enabled: v})}
                      />
                    </div>
                    
                    {simProviderForm.usage_sync_enabled && (
                      <>
                        <div className="space-y-2">
                          <Label>Sync Frequency</Label>
                          <Select 
                            value={simProviderForm.usage_sync_frequency} 
                            onValueChange={(v) => setSimProviderForm({...simProviderForm, usage_sync_frequency: v})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="daily">Daily</SelectItem>
                              <SelectItem value="every_2_days">Every 2 Days</SelectItem>
                              <SelectItem value="every_3_days">Every 3 Days</SelectItem>
                              <SelectItem value="weekly">Weekly</SelectItem>
                              <SelectItem value="fortnightly">Fortnightly</SelectItem>
                              <SelectItem value="monthly">Monthly</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Sync Time</Label>
                          <Input
                            type="time"
                            value={simProviderForm.usage_sync_time}
                            onChange={(e) => setSimProviderForm({...simProviderForm, usage_sync_time: e.target.value})}
                            className="dark:[color-scheme:dark]"
                          />
                          <p className="text-xs text-muted-foreground">
                            Time is in your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone})
                          </p>
                        </div>
                        
                        <div className="p-3 bg-muted/30 rounded-lg border">
                          <p className="text-sm">
                            <strong>Schedule:</strong> {(() => {
                              const freq = simProviderForm.usage_sync_frequency;
                              const freqLabel = {daily: 'Every day', every_2_days: 'Every 2 days', every_3_days: 'Every 3 days', weekly: 'Every week', fortnightly: 'Every 2 weeks', monthly: 'Every month'};
                              return `${freqLabel[freq] || freq} at ${simProviderForm.usage_sync_time || '00:00'} (${Intl.DateTimeFormat().resolvedOptions().timeZone})`;
                            })()}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                )}
                
                {/* Tab: Alerts (usage spike + prepaid expiry) */}
                {activeSimProviderTab === 'alerts' && (
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200">
                      <p className="text-sm">
                        Alerts are checked once a day after this provider's sync and delivered via the Audit Alerts
                        settings (bell, email, push) to Super Admins and admins with the Audit Alerts privilege.
                      </p>
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-lg border">
                      <div>
                        <p className="font-medium text-sm">Usage Spike Alerts</p>
                        <p className="text-xs text-muted-foreground">Alert when a SIM uses more data in one day than its threshold</p>
                      </div>
                      <Switch
                        checked={simProviderForm.spike_alert_enabled}
                        onCheckedChange={(v) => setSimProviderForm({...simProviderForm, spike_alert_enabled: v})}
                        data-testid="spike-alert-enabled-switch"
                      />
                    </div>

                    {simProviderForm.spike_alert_enabled && (
                      <>
                        <div className="space-y-2">
                          <Label>Detection Mode</Label>
                          <Select
                            value={simProviderForm.spike_mode}
                            onValueChange={(v) => setSimProviderForm({...simProviderForm, spike_mode: v})}
                          >
                            <SelectTrigger data-testid="spike-mode-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="fixed">Fixed MB per day</SelectItem>
                              <SelectItem value="percent">% of each SIM's plan per day</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>{simProviderForm.spike_mode === 'percent' ? 'Threshold (% of SIM plan per day)' : 'Threshold (MB per day)'}</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.1"
                            value={simProviderForm.spike_threshold}
                            onChange={(e) => setSimProviderForm({...simProviderForm, spike_threshold: e.target.value === '' ? '' : Number(e.target.value)})}
                            data-testid="spike-threshold-input"
                          />
                          <p className="text-xs text-muted-foreground">
                            {simProviderForm.spike_mode === 'percent'
                              ? `Example: at ${simProviderForm.spike_threshold || 20}%, a SIM on a 10 MB plan alerts when it uses more than ${((simProviderForm.spike_threshold || 20) * 0.1).toFixed(1)} MB in a day. SIMs without a plan limit are skipped.`
                              : `Alerts when any SIM uses more than ${simProviderForm.spike_threshold || 5} MB in one day.`}
                          </p>
                        </div>
                      </>
                    )}

                    <div className="flex items-center justify-between p-3 rounded-lg border">
                      <div>
                        <p className="font-medium text-sm">Prepaid Expiry Warnings</p>
                        <p className="text-xs text-muted-foreground">Warn before SIM plan expiry dates — for prepaid providers (not needed for PAYG month-to-month plans)</p>
                      </div>
                      <Switch
                        checked={simProviderForm.expiry_warning_enabled}
                        onCheckedChange={(v) => setSimProviderForm({...simProviderForm, expiry_warning_enabled: v})}
                        data-testid="expiry-warning-enabled-switch"
                      />
                    </div>

                    {simProviderForm.expiry_warning_enabled && (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Early warning (days before)</Label>
                          <Input
                            type="number"
                            min="1"
                            value={simProviderForm.expiry_warn_days_1}
                            onChange={(e) => setSimProviderForm({...simProviderForm, expiry_warn_days_1: e.target.value === '' ? '' : Number(e.target.value)})}
                            data-testid="expiry-warn-days-1-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Final warning (days before)</Label>
                          <Input
                            type="number"
                            min="1"
                            value={simProviderForm.expiry_warn_days_2}
                            onChange={(e) => setSimProviderForm({...simProviderForm, expiry_warn_days_2: e.target.value === '' ? '' : Number(e.target.value)})}
                            data-testid="expiry-warn-days-2-input"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground col-span-2">
                          Each SIM alerts once per warning stage and re-arms automatically when its expiry date is renewed.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Tab 4: Data Streamer (1NCE only) */}
                {activeSimProviderTab === 'streamer' && simProviderForm.provider_type === '1nce' && (
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200">
                      <p className="text-sm">
                        The 1NCE Data Streamer pushes real-time SIM event and usage data to your app via HTTPS webhook.
                        This updates SIM card records (status, location, usage) automatically.
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 rounded-lg border">
                      <div>
                        <p className="font-medium text-sm">Enable Data Streamer</p>
                        <p className="text-xs text-muted-foreground">Accept incoming event and usage data from 1NCE</p>
                      </div>
                      <Switch
                        data-testid="data-streamer-toggle"
                        checked={simProviderForm.data_streamer_enabled}
                        onCheckedChange={(v) => setSimProviderForm({...simProviderForm, data_streamer_enabled: v})}
                      />
                    </div>
                    
                    {simProviderForm.data_streamer_enabled && (
                      <>
                        <div className="space-y-2">
                          <Label>Webhook URL</Label>
                          <div className="flex gap-2">
                            <Input
                              readOnly
                              data-testid="data-streamer-webhook-url"
                              value={selectedSimProvider?.id ? `${window.location.origin}/api/webhooks/1nce/data-streamer/${selectedSimProvider.id}` : 'Save provider first to generate URL'}
                              className="font-mono text-xs bg-muted"
                            />
                            {selectedSimProvider?.id && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  navigator.clipboard.writeText(`${window.location.origin}/api/webhooks/1nce/data-streamer/${selectedSimProvider.id}`);
                                  toast.success('Webhook URL copied');
                                }}
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Enter this URL as the API Callback in the 1NCE Portal &gt; Data Streamer configuration.
                          </p>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Basic Auth Username</Label>
                            <Input
                              data-testid="data-streamer-username"
                              value={simProviderForm.data_streamer_username}
                              onChange={(e) => setSimProviderForm({...simProviderForm, data_streamer_username: e.target.value})}
                              placeholder="Username for webhook auth"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Basic Auth Password</Label>
                            <Input
                              type="password"
                              data-testid="data-streamer-password"
                              value={simProviderForm.data_streamer_password}
                              onChange={(e) => setSimProviderForm({...simProviderForm, data_streamer_password: e.target.value})}
                              placeholder="Password for webhook auth"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Set these credentials and use them as the Basic Auth header when configuring Data Streamer in the 1NCE Portal.
                        </p>
                        
                        {simProviderForm.data_streamer_username && simProviderForm.data_streamer_password && (
                          <div className="space-y-2">
                            <Label>Base64 Auth Value (for 1NCE Portal)</Label>
                            <div className="flex gap-2">
                              <Input
                                readOnly
                                value={btoa(`${simProviderForm.data_streamer_username}:${simProviderForm.data_streamer_password}`)}
                                className="font-mono text-xs bg-muted"
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                data-testid="copy-base64-header"
                                onClick={() => {
                                  navigator.clipboard.writeText(btoa(`${simProviderForm.data_streamer_username}:${simProviderForm.data_streamer_password}`));
                                  toast.success('Base64 value copied — paste into 1NCE Portal Auth Header field');
                                }}
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Copy this Base64 value and paste it into the 1NCE Portal &gt; Data Streamer &gt; Auth Header field. Do NOT include the word "Basic" — 1NCE adds that automatically.
                            </p>
                          </div>
                        )}
                        
                        <div className="p-4 bg-amber-50 dark:bg-amber-950 rounded-lg border border-amber-200">
                          <p className="text-sm font-medium mb-2">Setup Instructions</p>
                          <ol className="text-xs space-y-1 list-decimal list-inside text-muted-foreground">
                            <li>Go to the <strong>1NCE Portal</strong> &gt; Configuration &gt; Data Streamer</li>
                            <li>Create a new stream with API Type: <strong>RestAPI</strong></li>
                            <li>Set Stream Type to <strong>Event Data</strong> (repeat for Usage Data)</li>
                            <li>Paste the Webhook URL above as the <strong>API Callback</strong></li>
                            <li>Set the <strong>Basic Auth Header</strong> — click the copy button above to get the encoded value</li>
                            <li>Save and activate the stream</li>
                          </ol>
                        </div>
                        
                        {/* Diagnostics & Test */}
                        <div className="space-y-2 pt-4 border-t">
                          <div className="flex items-center justify-between">
                            <Label className="text-base font-semibold">Diagnostics</Label>
                            <Button
                              variant="outline"
                              size="sm"
                              data-testid="test-webhook-btn"
                              disabled={testingWebhook || !selectedSimProvider?.id}
                              onClick={() => {
                                setTestingWebhook(true);
                                api.post(`/admin/data-streamer/test-webhook/${selectedSimProvider.id}`)
                                  .then(res => {
                                    setStreamerDiagnostics(res.data);
                                    if (res.data?.test_result?.success) {
                                      toast.success('Webhook self-test passed!');
                                    } else {
                                      toast.error('Webhook self-test failed');
                                    }
                                  })
                                  .catch(() => toast.error('Failed to run diagnostics'))
                                  .finally(() => setTestingWebhook(false));
                              }}
                            >
                              {testingWebhook ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
                              Test Webhook
                            </Button>
                          </div>
                          
                          {streamerDiagnostics && (
                            <div className="space-y-2 text-xs">
                              <div className={`p-3 rounded-lg border ${streamerDiagnostics.test_result?.success ? 'bg-green-50 dark:bg-green-950 border-green-200' : 'bg-red-50 dark:bg-red-950 border-red-200'}`}>
                                <p className="font-semibold">{streamerDiagnostics.test_result?.success ? 'Self-test: PASSED' : 'Self-test: FAILED'}</p>
                                <p className="text-muted-foreground">Status: {streamerDiagnostics.test_result?.status_code || 'N/A'}</p>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div className="p-2 rounded border bg-muted/30">
                                  <p className="text-muted-foreground">Total Events</p>
                                  <p className="font-semibold text-sm">{streamerDiagnostics.total_events || 0}</p>
                                </div>
                                <div className="p-2 rounded border bg-muted/30">
                                  <p className="text-muted-foreground">Auth Failures</p>
                                  <p className={`font-semibold text-sm ${streamerDiagnostics.auth_failures > 0 ? 'text-red-600' : ''}`}>{streamerDiagnostics.auth_failures || 0}</p>
                                </div>
                                <div className="p-2 rounded border bg-muted/30 col-span-2">
                                  <p className="text-muted-foreground">Last Event</p>
                                  <p className="font-semibold text-sm">{streamerDiagnostics.last_event_at ? formatDT(streamerDiagnostics.last_event_at) : 'Never'} {streamerDiagnostics.last_event_type ? `(${streamerDiagnostics.last_event_type})` : ''}</p>
                                </div>
                              </div>
                              {streamerDiagnostics.auth_failures > 0 && streamerDiagnostics.recent_auth_failures?.length > 0 && (
                                <div className="p-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950">
                                  <p className="font-semibold text-red-700 dark:text-red-400 mb-1">Recent Auth Failures:</p>
                                  {streamerDiagnostics.recent_auth_failures.map((f, i) => (
                                    <p key={i} className="text-red-600 dark:text-red-400">
                                      {formatDT(f.received_at)} — {f.raw_data?.reason} {f.raw_data?.received_user ? `(user: ${f.raw_data.received_user})` : ''} from {f.raw_data?.client_ip}
                                    </p>
                                  ))}
                                </div>
                              )}
                              <div className="p-3 rounded-lg border bg-muted/30">
                                <p className="text-muted-foreground mb-1">Expected Base64 Auth Value (must match 1NCE Portal):</p>
                                <div className="flex gap-2 items-center">
                                  <code className="text-xs bg-background px-2 py-1 rounded border flex-1 break-all">{streamerDiagnostics.base64_value || 'N/A'}</code>
                                  {streamerDiagnostics.base64_value && (
                                    <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(streamerDiagnostics.base64_value); toast.success('Copied!'); }}>
                                      <Copy className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Recent Events */}
                        <div className="space-y-2 pt-4 border-t">
                          <div className="flex items-center justify-between">
                            <Label className="text-base font-semibold">Recent Events</Label>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid="refresh-streamer-events"
                              onClick={() => {
                                if (!selectedSimProvider?.id) return;
                                setLoadingStreamerEvents(true);
                                api.get(`/admin/data-streamer/events/${selectedSimProvider.id}?limit=20`)
                                  .then(res => setDataStreamerEvents(res.data?.events || []))
                                  .catch(() => toast.error('Failed to load events'))
                                  .finally(() => setLoadingStreamerEvents(false));
                              }}
                            >
                              <RefreshCw className={`w-4 h-4 ${loadingStreamerEvents ? 'animate-spin' : ''}`} />
                            </Button>
                          </div>
                          {loadingStreamerEvents ? (
                            <div className="flex justify-center py-4">
                              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                            </div>
                          ) : dataStreamerEvents.length > 0 ? (
                            <div className="max-h-[250px] overflow-y-auto space-y-1">
                              {dataStreamerEvents.filter(evt => evt.event_type !== 'AUTH_FAILURE').map((evt, i) => {
                                const raw = evt.raw_data || {};
                                const tLoc = raw.towerLocation || {};
                                const tMeta = raw.towerMetadata || {};
                                const hasLocation = tLoc.latitude && tLoc.longitude;
                                const hasTower = tMeta.MCC || tMeta.CellID;
                                return (
                                  <div key={evt.id || i} className="p-2.5 rounded border text-xs font-mono bg-muted/30">
                                    <div className="flex justify-between">
                                      <span className="font-semibold">{evt.event_type || 'unknown'}</span>
                                      <span className="text-muted-foreground">{evt.received_at ? formatDT(evt.received_at) : ''}</span>
                                    </div>
                                    <p className="text-muted-foreground">ICCID: {evt.iccid}</p>
                                    {hasLocation && (
                                      <p className="text-green-600 dark:text-green-400">
                                        Location: {Number(tLoc.latitude).toFixed(4)}, {Number(tLoc.longitude).toFixed(4)}
                                      </p>
                                    )}
                                    {hasTower && (
                                      <p className="text-blue-600 dark:text-blue-400">
                                        Tower: {tMeta.MCC && `MCC:${tMeta.MCC}`} {tMeta.MNC && `MNC:${tMeta.MNC}`} {tMeta.CellID && `CID:${tMeta.CellID}`} {tMeta.LAC && `LAC:${tMeta.LAC}`}
                                      </p>
                                    )}
                                    {raw.radioAccessType && (
                                      <p className="text-muted-foreground">Radio: {raw.radioAccessType}</p>
                                    )}
                                    {(raw.txBytes || raw.rxBytes) && (
                                      <p className="text-amber-600 dark:text-amber-400">
                                        Data: TX {raw.txBytes || 0}B / RX {raw.rxBytes || 0}B
                                      </p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground py-2">No events received yet. Events will appear here once 1NCE starts streaming data.</p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <Button size="sm" variant="outline" onClick={() => { setIsEditSimProviderOpen(false); resetSimProviderForm(); }}>Cancel</Button>
                <Button size="sm" onClick={handleUpdateSimProvider} disabled={saving} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="sim-provider-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          {/* View SIM Cards Dialog (Standalone) */}
          <Dialog open={isViewSimCardsOpen} onOpenChange={(open) => { setIsViewSimCardsOpen(open); if (!open) fetchData(); }}>
            <DialogContent className="flex h-[90vh] max-h-[90vh] w-[min(96vw,72rem)] max-w-none flex-col overflow-hidden p-0" data-testid="sim-cards-dialog-content">
              <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 pr-20">
                <div className="flex min-w-0 items-start justify-between gap-4">
                  <DialogTitle className="min-w-0 break-words pt-1" data-testid="sim-cards-dialog-title">SIM Cards - {selectedProviderForSimCards?.name}</DialogTitle>
                  <div className="flex max-w-[46%] flex-wrap items-center justify-end gap-2" data-testid="sim-cards-dialog-summary-badges">
                    <Badge className="whitespace-nowrap bg-blue-600 text-white text-base px-3 py-1" data-testid="sim-cards-total-badge">
                      {simTotal} SIM{simTotal !== 1 ? 's' : ''} Total
                    </Badge>
                    {selectedProviderForSimCards?.provider_type === 'truphone' && totalSmsCount.total > 0 && (
                      <Badge className="whitespace-nowrap bg-purple-600 text-white text-base px-3 py-1" data-testid="sim-cards-total-sms-badge">
                        {totalSmsCount.total} SMS ({totalSmsCount.sent || 0} via system)
                      </Badge>
                    )}
                  </div>
                </div>
              </DialogHeader>
              
              <div className="flex-shrink-0 px-6 space-y-4" data-testid="sim-cards-dialog-controls">
                <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200">
                  <p className="text-sm">
                    View and manage all SIM cards linked to this provider. Monitor status, activate/deactivate cards, and track tower locations.
                  </p>
                </div>
                
                {/* Search and Filters */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by ICCID, Mobile Number, IMSI, or IMEI..."
                        value={simCardSearchTerm}
                        onChange={(e) => setSimCardSearchTerm(e.target.value)}
                        className="pl-10 pr-10"
                        data-testid="sim-cards-search-input"
                      />
                      {simCardSearchTerm && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearSearch}
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                          data-testid="sim-cards-clear-search-button"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div>
                    <Select 
                      value={simCardStatusFilter || ''} 
                      onValueChange={setSimCardStatusFilter}
                    >
                      <SelectTrigger className="dark:bg-[#172031]" data-testid="sim-cards-status-filter-select">
                        <SelectValue placeholder="All Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All Status</SelectItem>
                        {selectedProviderForSimCards?.provider_type === 'truphone' ? (
                          <>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="suspended">Suspended</SelectItem>
                            <SelectItem value="pre-active">Pre-Active</SelectItem>
                            <SelectItem value="provisioned">Provisioned</SelectItem>
                            <SelectItem value="test">Test</SelectItem>
                            <SelectItem value="retired">Retired</SelectItem>
                            <SelectItem value="expiring_soon" data-testid="sim-filter-expiring-soon">⏳ Expiring Soon (within {selectedProviderForSimCards?.expiry_warn_days_1 || 30} days)</SelectItem>
                            <SelectItem value="expiring_or_low_data">⚠ Expiring / Low Data (expires in 30 days or &lt;25MB left)</SelectItem>
                          </>
                        ) : selectedProviderForSimCards?.provider_type === '1nce' ? (
                          <>
                            <SelectItem value="active">Enabled</SelectItem>
                            <SelectItem value="suspended">Disabled</SelectItem>
                            <SelectItem value="provisioned">Onboarding</SelectItem>
                            <SelectItem value="retired">Terminated</SelectItem>
                            <SelectItem value="expiring_soon" data-testid="sim-filter-expiring-soon">⏳ Expiring Soon (within {selectedProviderForSimCards?.expiry_warn_days_1 || 30} days)</SelectItem>
                            <SelectItem value="expiring_or_low_data">⚠ Expiring / Low Data (expires in 30 days or &lt;25MB left)</SelectItem>
                          </>
                        ) : selectedProviderForSimCards?.provider_type === 'manual' ? (
                          <>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                            <SelectItem value="suspended">Suspended</SelectItem>
                            <SelectItem value="terminated">Terminated</SelectItem>
                            <SelectItem value="expiring_soon" data-testid="sim-filter-expiring-soon">⏳ Expiring Soon (within {selectedProviderForSimCards?.expiry_warn_days_1 || 30} days)</SelectItem>
                          </>
                        ) : (
                          <>
                            <SelectItem value="activated">Activated</SelectItem>
                            <SelectItem value="activated_in_session">Activated (In Session)</SelectItem>
                            <SelectItem value="activated_no_session">Activated (No Session)</SelectItem>
                            <SelectItem value="activated_no_session_no_data">Activated (No Session - No Data)</SelectItem>
                            <SelectItem value="deactivated">Deactivated</SelectItem>
                            <SelectItem value="inventory">Inventory</SelectItem>
                            <SelectItem value="suspended">Suspended</SelectItem>
                            <SelectItem value="test_ready">Test Ready</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="flex justify-between items-center gap-2">
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => { setEditingSimCard(null); setSimCardForm({ iccid: '', mobile_number: '', imsi: '', imei: '', status: 'activated', plan_name: '', plan_type: 'postpaid', billing_cycle: 'monthly', data_limit_mb: '', data_unit: 'MB', cost: '', notes: '' }); setIsAddSimCardOpen(true); }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add SIM Card
                    </Button>
                    {selectedProviderForSimCards?.provider_type !== 'manual' && (
                      <>
                        <Button 
                          variant="outline"
                          size="sm"
                          onClick={() => handleSyncSimCards(selectedProviderForSimCards?.id, false)}
                          disabled={!selectedProviderForSimCards?.api_enabled || saving}
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Quick Sync
                        </Button>
                        {selectedProviderForSimCards?.provider_type === 'truphone' && (
                          <>
                            <Button 
                              variant="outline"
                              size="sm"
                              onClick={handleSyncUsage}
                              disabled={!selectedProviderForSimCards?.api_enabled || saving}
                              title="Fetch data usage from provider API"
                            >
                              <Database className="w-4 h-4 mr-2" />
                              {saving ? 'Syncing...' : 'Sync Usage'}
                            </Button>
                            {selectedProviderForSimCards?.usage_last_synced && (
                              <span className="text-xs text-muted-foreground">
                                Last sync: {formatDT(selectedProviderForSimCards.usage_last_synced)}
                              </span>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                  <div className="flex gap-2 items-center">
                    {selectedSimCardIds.length > 0 && (
                      <span className="text-sm text-muted-foreground">
                        {selectedSimCardIds.length} selected
                      </span>
                    )}
                    {simCardStatusFilter === 'activated_no_session_no_data' && simCards.length > 0 && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleBatchDeactivate}
                        disabled={saving}
                      >
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        {selectedSimCardIds.length > 0 ? `Deactivate ${selectedSimCardIds.length} Selected` : `Deactivate All ${simCards.length} Stale`}
                      </Button>
                    )}
                    {selectedProviderForSimCards?.provider_type !== 'manual' && (
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={() => { resetImportDialog(); setIsImportICCIDsOpen(true); }}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Import ICCIDs
                      </Button>
                    )}
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => handleExportSimCards(selectedProviderForSimCards?.id, 'csv')}
                      disabled={selectedSimCardIds.length === 0}
                      data-testid="export-simcards-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {selectedSimCardIds.length > 0 ? `Export (${selectedSimCardIds.length})` : 'Export'}
                    </Button>
                    {selectedProviderForSimCards?.provider_type === 'manual' && (
                      <>
                        <input type="file" accept=".csv" className="hidden" id="manual-sim-import" onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = async (ev) => {
                            try {
                              const lines = ev.target.result.split('\n').filter(l => l.trim());
                              if (lines.length < 2) { toast.error('CSV is empty'); return; }
                              const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim().toLowerCase());
                              let created = 0;
                              for (let i = 1; i < lines.length; i++) {
                                const vals = lines[i].match(/("([^"]|"")*"|[^,]*)/g)?.map(v => v.replace(/^"|"$/g, '').replace(/""/g, '"').trim()) || [];
                                const row = {};
                                headers.forEach((h, idx) => { if (vals[idx]) row[h] = vals[idx]; });
                                if (!row.iccid) continue;
                                try {
                                  await api.post(`/admin/sim-providers/${selectedProviderForSimCards.id}/sim-cards`, {
                                    iccid: row.iccid, mobile_number: row['mobile number'] || row.mobile || '', imei: row.imei || '',
                                    status: row.status || 'activated', plan_name: row['data plan'] || row.plan || '',
                                    plan_type: row['plan type'] || 'postpaid', billing_cycle: row['billing cycle'] || 'monthly',
                                    data_limit_mb: parseInt(row['data limit']) || 0, cost: parseFloat(row.cost) || 0,
                                    notes: row.notes || '',
                                  });
                                  created++;
                                } catch {}
                              }
                              toast.success(`Imported ${created} SIM card(s)`);
                              fetchSimCards(selectedProviderForSimCards.id);
                            } catch { toast.error('Failed to parse CSV'); }
                          };
                          reader.readAsText(file);
                          e.target.value = '';
                        }} />
                        <Button variant="outline" size="sm" onClick={() => document.getElementById('manual-sim-import').click()}>
                          <Download className="w-4 h-4 mr-2" />
                          Import
                        </Button>
                      </>
                    )}
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => fetchSimCards(selectedProviderForSimCards?.id)}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="relative flex min-h-0 flex-1 flex-col px-6 pb-6 pt-4" data-testid="sim-cards-table-region">
                {loadingSimCards ? (
                  <div className="absolute inset-x-6 bottom-6 top-4 z-20 flex items-center justify-center rounded-md border border-slate-200/70 bg-background/70 backdrop-blur-sm dark:border-slate-700/70" data-testid="sim-cards-loading-overlay">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : null}
                  <div className="flex min-h-0 flex-1 flex-col opacity-100">
                    {/* Pagination Controls */}
                    <div className="flex flex-shrink-0 justify-between items-center mb-4 gap-3" data-testid="sim-cards-pagination-bar">
                      <div className="text-sm text-muted-foreground">
                        Showing {simTotal === 0 ? 0 : (currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, simTotal)} of {simTotal} SIM cards
                      </div>
                      <div className="flex gap-2 items-center">
                        <span className="text-sm">Show:</span>
                        <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
                          <SelectTrigger className="w-24 dark:bg-[#172031]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="50">50</SelectItem>
                            <SelectItem value="100">100</SelectItem>
                            <SelectItem value="500">500</SelectItem>
                            <SelectItem value="1000">1000</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </Button>
                        <span className="text-sm">
                          Page {currentPage} of {Math.max(1, Math.ceil(simTotal / pageSize))}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(p => Math.min(Math.max(1, Math.ceil(simTotal / pageSize)), p + 1))}
                          disabled={currentPage >= Math.ceil(simTotal / pageSize)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                    
                    <SyncedTableScroll minWidth="1040px" maxHeight="100%" testId="sim-cards-table-scroll">
                      <Table className="table-fixed" wrapperClassName="overflow-visible" data-testid="sim-cards-table">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">
                              <Checkbox
                                checked={simCards.length > 0 && simCards.every(s => selectedSimCardIds.includes(s.id))}
                                onCheckedChange={handleSelectAllSimCards}
                              />
                            </TableHead>
                            <SortableHeader column="iccid" className="w-40">ICCID</SortableHeader>
                            <SortableHeader column="mobile_number" className="w-32">Mobile Number</SortableHeader>
                            <SortableHeader column="imei" className="w-40">IMEI</SortableHeader>
                            {(selectedProviderForSimCards?.provider_type === 'truphone' || selectedProviderForSimCards?.provider_type === '1nce') && (
                              <>
                                <SortableHeader column="data_used_mb" className="w-24">Data Used</SortableHeader>
                                <SortableHeader column="expires_at" className="w-20">Expires</SortableHeader>
                              </>
                            )}
                            {selectedProviderForSimCards?.provider_type !== 'manual' && (
                              <TableHead className="w-20">Session</TableHead>
                            )}
                            <TableHead className="w-24">Status</TableHead>
                            <TableHead className="w-28">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {simCards.map(sim => (
                            <TableRow key={sim.id}>
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  checked={selectedSimCardIds.includes(sim.id)}
                                  onCheckedChange={(checked) => handleSelectSimCard(sim.id, checked)}
                                />
                              </TableCell>
                              <TableCell className="font-mono text-xs px-2">
                                <div className="flex items-center gap-1">
                                  <span>{sim.iccid}</span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 w-5 p-0 shrink-0"
                                    onClick={() => copyToClipboard(sim.iccid, 'ICCID')}
                                    title="Copy ICCID"
                                  >
                                    <Copy className="w-3 h-3" />
                                  </Button>
                                </div>
                              </TableCell>
                              <TableCell className="font-mono text-xs px-2">
                                <div className="flex items-center gap-1">
                                  <span>{sim.mobile_number || '-'}</span>
                                  {sim.mobile_number && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-5 w-5 p-0 shrink-0"
                                      onClick={() => copyToClipboard(sim.mobile_number, 'Mobile Number')}
                                      title="Copy Mobile Number"
                                    >
                                      <Copy className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="font-mono text-xs px-2">
                                <div className="flex items-center gap-1">
                                  <span>{sim.imei || '-'}</span>
                                  {sim.imei && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-5 w-5 p-0 shrink-0"
                                      onClick={() => copyToClipboard(sim.imei, 'IMEI')}
                                      title="Copy IMEI"
                                    >
                                      <Copy className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                              {(selectedProviderForSimCards?.provider_type === 'truphone' || selectedProviderForSimCards?.provider_type === '1nce') && (
                                <>
                                  <TableCell className="text-xs">
                                    {sim.data_used_mb != null ? (
                                      <span className={sim.data_limit_mb && sim.data_used_mb >= sim.data_limit_mb - 25 ? 'text-red-600 font-semibold' : ''}>
                                        {Number(sim.data_used_mb).toFixed(2)} / {sim.data_limit_mb || '?'} MB
                                      </span>
                                    ) : '-'}
                                  </TableCell>
                                  <TableCell className="text-xs">
                                    {sim.expires_at ? (() => {
                                      const d = Math.ceil((new Date(sim.expires_at) - new Date()) / 86400000);
                                      return <span className={d < 90 ? 'text-red-600 font-semibold' : d < 365 ? 'text-amber-600' : ''}>
                                        {formatDateOnly(sim.expires_at)}
                                      </span>;
                                    })() : '-'}
                                  </TableCell>
                                </>
                              )}
                              {selectedProviderForSimCards?.provider_type !== 'manual' && (
                                <TableCell className="text-xs">
                                  {sim.session_status ? (
                                    <Badge variant="outline" className={
                                      sim.session_status === 'in_session' ? 'border-green-500 text-green-700' : 'border-gray-400'
                                    }>
                                      {sim.session_status === 'in_session' ? 'In Session' : 'No Session'}
                                    </Badge>
                                  ) : '-'}
                                </TableCell>
                              )}
                              <TableCell className="px-2">
                                <Badge className={'text-[10px] px-1.5 whitespace-nowrap ' + (
                                  sim.status === 'active' || sim.status === 'activated' ? 'bg-green-600 text-white' :
                                  sim.status === 'suspended' ? 'bg-amber-600 text-white' :
                                  sim.status === 'inventory' ? 'bg-blue-500 text-white' :
                                  sim.status === 'activation_ready' ? 'bg-cyan-500 text-white' :
                                  sim.status === 'test_ready' ? 'bg-purple-500 text-white' :
                                  'bg-gray-500 text-white')
                                }>
                                  {({'active': 'Activated', 'activated': 'Activated', 'suspended': 'Deactivated', 'deactivated': 'Deactivated', 'pre-active': 'Pre-Active', 'inventory': 'Inventory', 'retired': 'Retired', 'test': 'Test', 'test_ready': 'Test Ready', 'activation_ready': 'Activation Ready', 'onboarding': 'Onboarding', 'terminated': 'Terminated'})[sim.status] || sim.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="px-2">
                                <div className="flex gap-1">
                                  <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => openSimDetail(sim)} title="View Details">
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  {(selectedProviderForSimCards?.provider_type === 'manual' || !selectedProviderForSimCards?.api_enabled) && (
                                    <>
                                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" data-testid={`sim-edit-btn-${sim.id}`} onClick={() => {
                                      setSimCardForm({
                                        iccid: sim.iccid || '', mobile_number: sim.mobile_number || '', imsi: sim.imsi || '',
                                        imei: sim.imei || '', status: sim.status || 'activated', plan_name: sim.plan_name || '',
                                        plan_type: sim.plan_type || 'postpaid', billing_cycle: sim.billing_cycle || 'monthly',
                                        data_limit_mb: sim.data_limit_mb || '', data_unit: sim.data_unit || 'MB',
                                        cost: sim.cost || '', notes: sim.notes || '',
                                      });
                                      setEditingSimCard(sim);
                                      setIsAddSimCardOpen(true);
                                    }} title="Edit">
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" data-testid={`sim-delete-btn-${sim.id}`} onClick={() => handleDeleteSimCard(sim.id)} title="Delete">
                                      <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                    </>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                          {simCards.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                                {selectedProviderForSimCards?.provider_type === 'manual' 
                                  ? 'No SIM cards found. Click "Add SIM Card" to add cards manually.'
                                  : 'No SIM cards found. Click "Add SIM Card" or "Quick Sync" to import cards.'
                                }
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </SyncedTableScroll>
                  </div>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* SIM Card Detail Dialog (On-Demand) */}
          <Dialog open={isSimDetailOpen} onOpenChange={(open) => { if (!open) handleSimDetailClose(); }}>
            <DialogContent className="max-w-3xl bg-slate-50 dark:bg-slate-900">
              <DialogHeader>
                <DialogTitle>SIM Card Details - {selectedSim?.iccid}</DialogTitle>
              </DialogHeader>
              
              {simDetailLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  <span className="ml-3">Fetching details from {selectedProviderForSimCards?.name || 'provider'}...</span>
                </div>
              ) : simDetailData ? (
                <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
                  {/* Identification Section */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg border-b pb-2">Identification</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-muted-foreground">ICCID</Label>
                        <div className="flex items-center gap-2">
                          <p className="font-mono text-sm">{simDetailData.iccid}</p>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => copyToClipboard(simDetailData.iccid, 'ICCID')}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Mobile Number (MSISDN)</Label>
                        <div className="flex items-center gap-2">
                          <p className="font-mono text-sm">{simDetailData.mobile_number || '-'}</p>
                          {simDetailData.mobile_number && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => copyToClipboard(simDetailData.mobile_number, 'Mobile Number')}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">IMEI</Label>
                        <div className="flex items-center gap-2">
                          <p className="font-mono text-sm">{simDetailData.imei || '-'}</p>
                          {simDetailData.imei && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => copyToClipboard(simDetailData.imei, 'IMEI')}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                      {!(selectedProviderForSimCards?.provider_type === 'truphone' || selectedProviderForSimCards?.provider_type === '1nce' || selectedProviderForSimCards?.provider_type === 'manual') && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Modem ID (Jasper IMEI)</Label>
                          <div className="flex items-center gap-2">
                            <p className="font-mono text-sm">{simDetailData.modem_id || '-'}</p>
                            {simDetailData.modem_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => copyToClipboard(simDetailData.modem_id, 'Modem ID')}
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                      <div>
                        <Label className="text-xs text-muted-foreground">IMSI</Label>
                        <p className="font-mono text-sm">{simDetailData.imsi || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">
                          {(selectedProviderForSimCards?.provider_type === 'truphone') ? 'Organization' : 'Account ID'}
                        </Label>
                        <p className="text-sm">{simDetailData.account_id || simDetailData.customer_name || selectedProviderForSimCards?.account_number || '-'}</p>
                      </div>
                      {(selectedProviderForSimCards?.provider_type === 'truphone') && simDetailData.customer_name && simDetailData.customer_name !== simDetailData.account_id && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Label</Label>
                          <p className="text-sm">{simDetailData.customer_name}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Status & Plan Section */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg border-b pb-2">Status & Plan</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Current Status</Label>
                        {selectedProviderForSimCards?.provider_type === 'manual' ? (
                          <div className="mt-1">
                            <Badge className={`${
                              ({'activated': 'bg-green-600', 'active': 'bg-green-600', 'deactivated': 'bg-red-600', 'suspended': 'bg-red-600', 'pre-active': 'bg-amber-500', 'inventory': 'bg-gray-500', 'retired': 'bg-gray-500'})[simDetailData?.status] || 'bg-gray-500'
                            } text-white`}>
                              {({'active': 'Activated', 'activated': 'Activated', 'suspended': 'Deactivated', 'deactivated': 'Deactivated', 'pre-active': 'Pre-Active', 'inventory': 'Inventory', 'retired': 'Retired'})[simDetailData?.status] || simDetailData?.status || '-'}
                            </Badge>
                          </div>
                        ) : (
                          <div className="mt-1">
                            <Select 
                              value={tempStatus} 
                              onValueChange={(v) => { setTempStatus(v); setSimDetailDirty(true); }}
                              disabled={updatingStatus}
                            >
                              <SelectTrigger className="dark:bg-[#172031]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {selectedProviderForSimCards?.provider_type === 'truphone' ? (
                                  <>
                                    <SelectItem value="ACTIVE">Active</SelectItem>
                                    <SelectItem value="SUSPENDED">Suspended</SelectItem>
                                    <SelectItem value="PRE-ACTIVE">Pre-Active</SelectItem>
                                  </>
                                ) : selectedProviderForSimCards?.provider_type === '1nce' ? (
                                  <>
                                    <SelectItem value="ACTIVATED">Activated</SelectItem>
                                    <SelectItem value="DEACTIVATED">Deactivated</SelectItem>
                                  </>
                                ) : (
                                  <>
                                    <SelectItem value="ACTIVATED">Activated</SelectItem>
                                    <SelectItem value="ACTIVATION_READY">Activation Ready</SelectItem>
                                    <SelectItem value="TEST_READY">Test Ready</SelectItem>
                                    <SelectItem value="DEACTIVATED">Deactivated</SelectItem>
                                    <SelectItem value="INVENTORY">Inventory</SelectItem>
                                    <SelectItem value="RETIRED">Retired</SelectItem>
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                      {selectedProviderForSimCards?.provider_type !== 'manual' && (
                      <div>
                        <Label>Session Status</Label>
                        <div className="text-sm pt-2">
                          <Badge variant="outline" className={
                            simDetailData.session_status === 'in_session' ? 'border-green-500 text-green-700' : 'border-gray-400'
                          }>
                            {simDetailData.session_status === 'in_session' ? 'In Session' : simDetailData.session_status === 'no_session' ? 'No Session' : '-'}
                          </Badge>
                        </div>
                      </div>
                      )}
                      <div className="col-span-2">
                        <Label>Plan Name</Label>
                        <p className="text-sm pt-1">{simDetailData.plan_name || '-'}</p>
                      </div>
                      
                      {/* Show manual-specific fields as read-only */}
                      {selectedProviderForSimCards?.provider_type === 'manual' && (
                        <>
                          <div>
                            <Label className="text-xs text-muted-foreground">Plan Type</Label>
                            <p className="text-sm">{simDetailData.plan_type === 'prepaid' ? 'Prepaid' : 'Postpaid'}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Billing Cycle</Label>
                            <p className="text-sm">{simDetailData.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Data Limit</Label>
                            <p className="text-sm">{simDetailData.data_limit_mb || 0} {simDetailData.data_unit || 'MB'}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Cost per {simDetailData.billing_cycle === 'yearly' ? 'Year' : 'Month'}</Label>
                            <p className="text-sm">${simDetailData.cost || '0.00'}</p>
                          </div>
                        </>
                      )}
                      {(selectedProviderForSimCards?.provider_type === 'truphone' || selectedProviderForSimCards?.provider_type === '1nce') && simDetailData.apn && (
                        <div>
                          <Label>APN</Label>
                          <p className="text-sm pt-1 font-mono">{simDetailData.apn}</p>
                        </div>
                      )}
                      {simDetailData.ip_address && (
                        <div>
                          <Label>IP Address</Label>
                          <p className="text-sm pt-1 font-mono">{simDetailData.ip_address}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Usage & Activity Section — hidden for manual providers */}
                  {selectedProviderForSimCards?.provider_type !== 'manual' && (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg border-b pb-2">Usage & Activity</h3>
                    
                    {/* Data usage progress bar for prepaid SIMs */}
                    {simDetailData.data_limit_mb > 0 && (
                      <div className="p-3 rounded-lg border bg-muted/30">
                        <div className="flex justify-between text-sm mb-1">
                          <span>Data Used{selectedProviderForSimCards?.provider_type === 'jasper' ? ' (This Month)' : ''}: <strong>{(typeof simDetailData.data_used_mb === 'number' ? simDetailData.data_used_mb.toFixed(2) : simDetailData.data_used_mb) || '0.00'} MB</strong></span>
                          {simDetailData.usage_last_synced && <span className="text-xs text-muted-foreground ml-2">(synced {formatDT(simDetailData.usage_last_synced)})</span>}
                          <span>Limit: <strong>{simDetailData.data_limit_mb} MB</strong></span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-3">
                          <div
                            className={`h-3 rounded-full transition-all ${
                              (simDetailData.data_used_mb / simDetailData.data_limit_mb) > 0.9 ? 'bg-red-500' :
                              (simDetailData.data_used_mb / simDetailData.data_limit_mb) > 0.7 ? 'bg-amber-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min((simDetailData.data_used_mb / simDetailData.data_limit_mb) * 100, 100)}%` }}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {((simDetailData.data_used_mb / simDetailData.data_limit_mb) * 100).toFixed(1)}% used — {(simDetailData.data_limit_mb - simDetailData.data_used_mb).toFixed(2)} MB remaining
                        </p>
                      </div>
                    )}
                    
                    {/* Expiry progress bar for prepaid SIMs */}
                    {simDetailData.activated_at && simDetailData.expires_at && (
                      (() => {
                        const start = new Date(simDetailData.activated_at).getTime();
                        const end = new Date(simDetailData.expires_at).getTime();
                        const now = Date.now();
                        const totalDays = Math.ceil((end - start) / 86400000);
                        const elapsed = Math.ceil((now - start) / 86400000);
                        const remaining = Math.max(0, Math.ceil((end - now) / 86400000));
                        const pct = Math.min(Math.max((elapsed / totalDays) * 100, 0), 100);
                        return (
                          <div className="p-3 rounded-lg border bg-muted/30">
                            <div className="flex justify-between text-sm mb-1">
                              <span>Activated: <strong>{formatDateOnly(simDetailData.activated_at)}</strong></span>
                              <span>Expires: <strong>{formatDateOnly(simDetailData.expires_at)}</strong></span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-3">
                              <div
                                className={`h-3 rounded-full transition-all ${
                                  pct > 90 ? 'bg-red-500' : pct > 75 ? 'bg-amber-500' : 'bg-blue-500'
                                }`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {pct.toFixed(1)}% elapsed — <strong>{remaining} days</strong> remaining of {totalDays} days ({(totalDays / 365).toFixed(1)} years)
                            </p>
                          </div>
                        );
                      })()
                    )}
                    
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {!(simDetailData.data_limit_mb > 0) && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Data Used{selectedProviderForSimCards?.provider_type === 'jasper' ? ' (This Month)' : ''}</Label>
                          <p className="text-sm font-medium">
                            {simDetailData.data_used_mb !== null && simDetailData.data_used_mb !== undefined 
                              ? `${Number(simDetailData.data_used_mb).toFixed(2)} MB` 
                              : '-'}
                          </p>
                        </div>
                      )}
                      <div>
                        <Label className="text-xs text-muted-foreground">Session Count</Label>
                        <p className="text-sm">{simDetailData.ctd_session_count || '0'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">SMS Usage</Label>
                        <p className="text-sm">{smsHistory.length > 0 ? smsHistory.length : (simDetailData.ctd_sms_usage || '0')}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Activated On</Label>
                        <p className="text-sm">
                          {simDetailData.activated_at 
                            ? formatDateOnly(simDetailData.activated_at)
                            : simDetailData.last_activity
                              ? formatDateOnly(simDetailData.last_activity)
                              : '-'}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Expires</Label>
                        {simDetailData.expires_at ? (() => {
                          const exp = new Date(simDetailData.expires_at);
                          const now = new Date();
                          const daysLeft = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
                          return (
                            <div>
                              <p className="text-sm">{formatDateOnly(simDetailData.expires_at)}</p>
                              <p className={`text-xs ${daysLeft < 90 ? 'text-red-600 font-semibold' : daysLeft < 365 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                                {daysLeft > 0 ? `${daysLeft} days remaining` : 'EXPIRED'}
                              </p>
                            </div>
                          );
                        })() : <p className="text-sm">-</p>}
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Customer</Label>
                        <p className="text-sm">{simDetailData.customer_name || '-'}</p>
                      </div>
                    </div>
                  </div>
                  )}
                  
                  {/* Tower Location & Connectivity Section — hidden for manual providers */}
                  {selectedProviderForSimCards?.provider_type !== 'manual' && (
                  <>
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg border-b pb-2 flex items-center justify-between">
                      <span><MapPin className="w-4 h-4 inline mr-2" />Tower Location & Connectivity</span>
                      <div className="flex gap-2">
                        {selectedProviderForSimCards?.provider_type === '1nce' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              data-testid="locate-sim-btn"
                              disabled={locatingSimCard}
                              onClick={async () => {
                                if (!simDetailData?.iccid) return;
                                setLocatingSimCard(true);
                                try {
                                  const res = await api.get(`/admin/sim-cards/${simDetailData.iccid}/locate?days=1`);
                                  const data = res.data;
                                  if (data.locations && data.locations.length > 0) {
                                    const loc = data.locations[0];
                                    const tLoc = loc.towerLocation || {};
                                    const tMeta = loc.towerMetadata || {};
                                    setSimDetailData(prev => ({
                                      ...prev,
                                      last_latitude: tLoc.latitude || prev.last_latitude,
                                      last_longitude: tLoc.longitude || prev.last_longitude,
                                      last_tower_id: tMeta.CellID ? `CID:${tMeta.CellID} LAC:${tMeta.LAC || ''}` : prev.last_tower_id,
                                      last_tower_name: tMeta.MCC ? `MCC:${tMeta.MCC} MNC:${tMeta.MNC || ''}` : prev.last_tower_name,
                                      radio_access_type: loc.radioAccessType || prev.radio_access_type,
                                      location_accuracy_m: loc.resolutionMetadata?.horizontalAccuracy || prev.location_accuracy_m,
                                      location_updated_at: new Date().toISOString(),
                                    }));
                                    toast.success(`Location found: ${data.count} record(s)`);
                                  } else {
                                    toast.info('No location data available for this SIM');
                                  }
                                } catch (e) {
                                  toast.error(getApiErrorMessage(e) || 'Failed to locate SIM');
                                } finally {
                                  setLocatingSimCard(false);
                                }
                              }}
                            >
                              {locatingSimCard ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <MapPin className="w-3 h-3 mr-1" />}
                              Locate
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              data-testid="connectivity-btn"
                              disabled={loadingConnectivity}
                              onClick={async () => {
                                if (!simDetailData?.iccid) return;
                                setLoadingConnectivity(true);
                                try {
                                  const res = await api.get(`/admin/sim-cards/${simDetailData.iccid}/connectivity`);
                                  setConnectivityData(res.data);
                                  // Update session status in detail data
                                  setSimDetailData(prev => ({
                                    ...prev,
                                    session_status: res.data.connection_status === 'online' ? 'in_session' : 'no_session',
                                    ip_address: res.data.ip_address || prev.ip_address,
                                    apn: res.data.apn || prev.apn,
                                  }));
                                  toast.success(`Status: ${res.data.connection_status || 'unknown'}`);
                                } catch (e) {
                                  toast.error(getApiErrorMessage(e) || 'Failed to check connectivity');
                                } finally {
                                  setLoadingConnectivity(false);
                                }
                              }}
                            >
                              {loadingConnectivity ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Wifi className="w-3 h-3 mr-1" />}
                              Connectivity
                            </Button>
                          </>
                        )}
                      </div>
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs text-muted-foreground">Tower Name</Label>
                        <p className="text-sm">{simDetailData.last_tower_name || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Tower ID</Label>
                        <p className="text-sm font-mono">{simDetailData.last_tower_id || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Coordinates</Label>
                        <p className="text-sm font-mono">
                          {simDetailData.last_latitude != null && simDetailData.last_longitude != null
                            ? `${Number(simDetailData.last_latitude).toFixed(4)}, ${Number(simDetailData.last_longitude).toFixed(4)}`
                            : '-'}
                        </p>
                      </div>
                      {simDetailData.radio_access_type && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Radio Access</Label>
                          <p className="text-sm">{simDetailData.radio_access_type}</p>
                        </div>
                      )}
                      {simDetailData.location_accuracy_m && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Accuracy</Label>
                          <p className="text-sm">{simDetailData.location_accuracy_m}m</p>
                        </div>
                      )}
                      {simDetailData.location_updated_at && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Located At</Label>
                          <p className="text-sm">{formatDT(simDetailData.location_updated_at)}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Connectivity Details (1NCE) */}
                    {connectivityData && (
                      <div className="mt-3 p-3 rounded-lg border bg-muted/30">
                        <p className="text-sm font-medium mb-2">Live Connectivity</p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs text-muted-foreground">Status</Label>
                            <p className="text-sm">
                              <Badge variant="outline" className={connectivityData.connection_status === 'online' ? 'border-green-500 text-green-700' : 'border-red-400 text-red-600'}>
                                {connectivityData.connection_status === 'online' ? 'Online' : connectivityData.connection_status === 'offline' ? 'Offline' : 'Unknown'}
                              </Badge>
                            </p>
                          </div>
                          {connectivityData.ip_address && (
                            <div>
                              <Label className="text-xs text-muted-foreground">IP Address</Label>
                              <p className="text-sm font-mono">{connectivityData.ip_address}</p>
                            </div>
                          )}
                          {connectivityData.apn && (
                            <div>
                              <Label className="text-xs text-muted-foreground">APN</Label>
                              <p className="text-sm font-mono">{connectivityData.apn}</p>
                            </div>
                          )}
                          {connectivityData.operator && (
                            <div>
                              <Label className="text-xs text-muted-foreground">Operator</Label>
                              <p className="text-sm">{connectivityData.operator}</p>
                            </div>
                          )}
                          {connectivityData.country && (
                            <div>
                              <Label className="text-xs text-muted-foreground">Country</Label>
                              <p className="text-sm">{connectivityData.country}</p>
                            </div>
                          )}
                          {connectivityData.cell_id && (
                            <div>
                              <Label className="text-xs text-muted-foreground">Cell ID</Label>
                              <p className="text-sm font-mono">{connectivityData.cell_id}</p>
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          Updated: {connectivityData.updated_at ? formatDT(connectivityData.updated_at) : 'now'}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* SMS Section */}
                  <div className="space-y-3 pt-4 border-t">
                    <h3 className="font-semibold text-lg flex items-center justify-between">
                      <span><MessageSquare className="w-4 h-4 inline mr-2" />SMS Messages</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => simDetailData && fetchSmsHistory(simDetailData.id)}
                        title="Check for new messages"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </h3>
                    {selectedProviderForSimCards?.provider_type === 'truphone' && (
                      <div className="flex items-center justify-between p-2 rounded-lg border bg-muted/30">
                        <div>
                          <p className="text-sm font-medium">Device Outgoing SMS (MO)</p>
                          <p className="text-xs text-muted-foreground">
                            {simDetailData?.sms_mo_service === 'active' ? 'Enabled — device can send SMS' : 'Status unknown or managed by provider'}
                          </p>
                        </div>
                        <Badge variant="outline" className={simDetailData?.sms_mo_service === 'active' ? 'border-green-500 text-green-700' : 'border-gray-400'}>
                          {simDetailData?.sms_mo_service === 'active' ? 'Active' : simDetailData?.sms_mo_service || '-'}
                        </Badge>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Input
                        value={smsMessage}
                        onChange={(e) => setSmsMessage(e.target.value)}
                        placeholder="Type message to send to this SIM..."
                        className="flex-1"
                        onKeyDown={(e) => e.key === 'Enter' && handleSendSms()}
                        maxLength={320}
                      />
                      <Button
                        size="sm"
                        className="bg-[#2563eb] hover:bg-[#1d4ed8]"
                        onClick={handleSendSms}
                        disabled={sendingSms || !smsMessage}
                      >
                        {sendingSms ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send'}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">{smsMessage.length}/320 characters</p>
                    <p className="text-xs text-amber-600 dark:text-amber-400">Message is sent as raw text. Include any required PIN/password formatting (e.g. "0000,command" for Meitrack or "[space][space]command" for Teltonika).</p>
                    
                    {smsHistory.length > 0 ? (
                      <>
                        <div className="space-y-2">
                          {(showAllSms ? smsHistory : smsHistory.slice(0, 2)).map(msg => (
                            <div key={msg.id} className={`p-2 rounded-lg text-sm ${
                              msg.direction === 'incoming' 
                                ? 'bg-green-50 dark:bg-green-950 border border-green-200 ml-0 mr-8'
                                : 'bg-blue-50 dark:bg-blue-950 border border-blue-200 ml-8 mr-0'
                            }`}>
                              <div className="flex justify-between items-start gap-2">
                                <p className="flex-1 whitespace-pre-wrap break-all" dangerouslySetInnerHTML={{
                                  __html: (msg.message || '(no content)').replace(
                                    /(https?:\/\/[^\s]+)/g,
                                    '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline hover:text-blue-800">$1</a>'
                                  )
                                }} />
                                <Badge variant="outline" className="text-xs shrink-0">
                                  {msg.direction === 'incoming' ? '← In' : '→ Out'}
                                </Badge>
                              </div>
                              <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                                <span>{msg.status}</span>
                                <span>{msg.created_at ? formatDT(msg.created_at) : ''}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        {smsHistory.length > 2 && (
                          <Button variant="ghost" size="sm" onClick={() => setShowAllSms(!showAllSms)} className="w-full text-xs">
                            {showAllSms ? 'Show Less' : `Show All ${smsHistory.length} Messages`}
                          </Button>
                        )}
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground">No SMS history for this SIM.</p>
                    )}
                  </div>
                  </>
                  )}
                  
                  {/* Scheduled Changes Section — hidden for manual providers */}
                  {selectedProviderForSimCards?.provider_type !== 'manual' && (
                  <div className="space-y-3 pt-4 border-t">
                    <h3 className="font-semibold text-lg">
                      <Clock className="w-4 h-4 inline mr-2" />
                      Scheduled Changes
                    </h3>
                    
                    {/* Create new scheduled change */}
                    <div className="flex items-end gap-3 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200">
                      <div className="flex-1 space-y-1">
                        <Label className="text-xs">Target Status</Label>
                        <Select value={scheduleStatus} onValueChange={setScheduleStatus}>
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Select status..." />
                          </SelectTrigger>
                          <SelectContent>
                            {selectedProviderForSimCards?.provider_type === 'truphone' ? (
                              <>
                                <SelectItem value="ACTIVE">Active</SelectItem>
                                <SelectItem value="SUSPENDED">Suspended</SelectItem>
                                <SelectItem value="PRE-ACTIVE">Pre-Active</SelectItem>
                                <SelectItem value="PROVISIONED">Provisioned</SelectItem>
                                <SelectItem value="TEST">Test</SelectItem>
                                <SelectItem value="RETIRED">Retired</SelectItem>
                              </>
                            ) : selectedProviderForSimCards?.provider_type === '1nce' ? (
                              <>
                                <SelectItem value="ACTIVATED">Activated (Enabled)</SelectItem>
                                <SelectItem value="DEACTIVATED">Deactivated (Disabled)</SelectItem>
                              </>
                            ) : (
                              <>
                                <SelectItem value="ACTIVATED">Activated</SelectItem>
                                <SelectItem value="DEACTIVATED">Deactivated</SelectItem>
                                <SelectItem value="INVENTORY">Inventory</SelectItem>
                                <SelectItem value="RETIRED">Retired</SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full h-9 justify-start text-left font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {scheduleDate ? formatDateOnly(scheduleDate) : 'Pick a date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 z-[10000]" align="start">
                            <Calendar
                              mode="single"
                              selected={scheduleDate}
                              onSelect={setScheduleDate}
                              disabled={(date) => date < new Date()}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Time</Label>
                        <Input
                          type="time"
                          value={scheduleTime}
                          onChange={(e) => setScheduleTime(e.target.value)}
                          className="h-9 w-28 dark:[color-scheme:dark]"
                        />
                      </div>
                      <Button
                        size="sm"
                        className="bg-[#2563eb] hover:bg-[#1d4ed8] h-9"
                        onClick={handleCreateScheduledChange}
                        disabled={!scheduleDate || !scheduleStatus || scheduleStatus === simDetailData?.status?.toUpperCase()}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Schedule
                      </Button>
                    </div>
                    {scheduleStatus && scheduleStatus === simDetailData?.status?.toUpperCase() && (
                      <p className="text-xs text-amber-600 dark:text-amber-400">
                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                        SIM is already {scheduleStatus}. Select a different target status.
                      </p>
                    )}
                    
                    {/* List of pending scheduled changes */}
                    {scheduledChanges.length > 0 ? (
                      <div className="space-y-2">
                        {scheduledChanges.map(change => (
                          <div key={change.id} className="flex items-center justify-between p-2 bg-amber-50 dark:bg-amber-950 rounded border border-amber-200">
                            <div className="flex items-center gap-3">
                              <Clock className="w-4 h-4 text-amber-600" />
                              <span className="text-sm">
                                Change to <Badge variant="outline" className="mx-1">{change.target_status}</Badge> 
                                on <strong>{formatDT(change.scheduled_date)}</strong>
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleCancelScheduledChange(change.id)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No pending scheduled changes.</p>
                    )}
                  </div>
                  )}
                  
                  {/* Update Expiry & Data Limit (Truphone only) */}
                  {(selectedProviderForSimCards?.provider_type === 'truphone' || selectedProviderForSimCards?.provider_type === '1nce') && (
                    <div className="space-y-3 pt-4 border-t">
                      <h3 className="font-semibold text-lg">
                        <RefreshCw className="w-4 h-4 inline mr-2" />
                        Update Expiry & Data Limit
                      </h3>
                      <div className="flex items-end gap-3 p-3 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200">
                        <div className="space-y-1">
                          <Label className="text-xs">New Expiry Date</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full h-9 justify-start text-left font-normal">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {newExpiryDate ? formatDateOnly(newExpiryDate) : 'Pick a date'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 z-[10000]" align="start">
                              <Calendar
                                mode="single"
                                selected={newExpiryDate}
                                onSelect={setNewExpiryDate}
                                disabled={(date) => date < new Date()}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">New Data Limit (MB)</Label>
                          <Input
                            type="number"
                            value={newDataLimit}
                            onChange={(e) => setNewDataLimit(e.target.value)}
                            placeholder={String(simDetailData?.data_limit_mb || '500')}
                            className="h-9 w-32"
                          />
                        </div>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 h-9"
                          onClick={handleUpdateLimits}
                          disabled={(!newExpiryDate && !newDataLimit) || saving}
                        >
                          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update'}
                        </Button>
                        {simDetailData?.manual_limits && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-9 text-amber-600 border-amber-300 hover:bg-amber-50"
                            onClick={handleResetLimits}
                            disabled={saving}
                          >
                            Reset
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Update the expiry date and/or data limit after a recharge. Only changes local records.
                        {simDetailData?.manual_limits && <span className="text-amber-600 ml-1">(Manual override active — click Reset to restore API values)</span>}
                      </p>
                    </div>
                  )}
                  
                  {/* 1NCE Specific Features */}
                  {selectedProviderForSimCards?.provider_type === '1nce' && (
                    <div className="space-y-3 pt-4 border-t">
                      <h3 className="font-semibold text-lg">
                        <CreditCard className="w-4 h-4 inline mr-2" />
                        1NCE Management
                      </h3>
                      
                      {/* Top-Up */}
                      <div className="flex items-center justify-between p-3 rounded-lg border bg-green-50 dark:bg-green-950 border-green-200">
                        <div>
                          <p className="font-medium text-sm">Top-Up Data</p>
                          <p className="text-xs text-muted-foreground">Add more data/SMS to this SIM (charges your saved payment method)</p>
                        </div>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={async () => {
                            setPendingGlobalAction({
                              title: 'Top-Up SIM',
                              message: 'Top-up this SIM? This will charge your 1NCE payment method.',
                              confirmLabel: 'Top-Up',
                              variant: 'default',
                              action: async () => {
                                try {
                                  const r = await api.post(`/admin/sim-cards/${simDetailData.id}/1nce/topup`, { payment_method: 'creditcard' });
                                  toast.success(r.data.message);
                                  refreshSimDetail();
                                } catch (e) { toast.error(getApiErrorMessage(e) || 'Top-up failed'); }
                              },
                            });
                          }}
                          disabled={saving}
                        >
                          Top-Up
                        </Button>
                      </div>
                      
                      {/* Auto Top-Up Toggle */}
                      <div className="flex items-center justify-between p-3 rounded-lg border">
                        <div>
                          <p className="font-medium text-sm">Auto Top-Up</p>
                          <p className="text-xs text-muted-foreground">Automatically recharge when data drops below 20%</p>
                        </div>
                        <Switch
                          checked={simDetailData?.auto_topup === true}
                          onCheckedChange={async (checked) => {
                            try {
                              await api.post(`/admin/sim-cards/${simDetailData.id}/1nce/autotopup`, { enabled: checked });
                              toast.success(`Auto top-up ${checked ? 'enabled' : 'disabled'}`);
                              setSimDetailData(prev => ({ ...prev, auto_topup: checked }));
                            } catch (e) { toast.error(getApiErrorMessage(e) || 'Failed'); }
                          }}
                        />
                      </div>
                      
                      {/* SIM Extension */}
                      <div className="flex items-center justify-between p-3 rounded-lg border bg-blue-50 dark:bg-blue-950 border-blue-200">
                        <div>
                          <p className="font-medium text-sm">Extend SIM</p>
                          <p className="text-xs text-muted-foreground">Renew activation period + data quota (generates invoice)</p>
                        </div>
                        <Button
                          size="sm"
                          className="bg-[#2563eb] hover:bg-[#1d4ed8]"
                          onClick={async () => {
                            setPendingGlobalAction({
                              title: 'Extend SIM',
                              message: 'Extend this SIM? This will renew the quota and generate an invoice.',
                              confirmLabel: 'Extend',
                              variant: 'default',
                              action: async () => {
                                try {
                                  const r = await api.post(`/admin/sim-cards/${simDetailData.id}/1nce/extend`, { payment_method: 'creditcard' });
                                  toast.success(r.data.message);
                                  refreshSimDetail();
                                } catch (e) { toast.error(getApiErrorMessage(e) || 'Extension failed'); }
                              },
                            });
                          }}
                          disabled={saving}
                        >
                          Extend
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Danger Zone (existing) */}
                  <div className="space-y-3 pt-4 border-t">
                    <h3 className="font-semibold text-lg text-red-600">Danger Zone</h3>
                    <div className="p-3 bg-red-50 dark:bg-red-950 rounded-lg border border-red-200">
                      <p className="text-sm mb-2">
                        <strong>Delete SIM:</strong> This only removes the SIM from your local database. 
                        It does NOT delete the SIM from your {
                          selectedProviderForSimCards?.provider_type === 'truphone' ? 'Truphone' :
                          selectedProviderForSimCards?.provider_type === '1nce' ? '1NCE' : 'M2M One/Jasper'
                        } account.
                      </p>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          const providerName = selectedProviderForSimCards?.provider_type === 'truphone' ? 'Truphone' :
                            selectedProviderForSimCards?.provider_type === '1nce' ? '1NCE' : 'M2M One';
                          setPendingGlobalAction({
                            title: 'Remove SIM from Database',
                            message: `Remove this SIM from local database? This will NOT delete the SIM from your ${providerName} account.`,
                            confirmLabel: 'Remove',
                            variant: 'destructive',
                            action: async () => {
                              try {
                                await api.delete(`/admin/sim-cards/${simDetailData.id}`);
                                toast.success('SIM card removed');
                                fetchSimCards(selectedProviderForSimCards.id);
                                setIsSimDetailOpen(false);
                              } catch (error) {
                                toast.error('Failed to remove SIM card');
                              }
                            },
                          });
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remove from Database
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No data available
                </div>
              )}
              
              <DialogFooter className="flex justify-between">
                <div className="flex gap-2">
                  <Button size="sm" 
                    variant="outline" 
                    onClick={refreshSimDetail}
                    disabled={simDetailLoading}
                    data-testid="refresh-from-api-btn"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${simDetailLoading ? 'animate-spin' : ''}`} />
                    Refresh from API
                  </Button>
                  {simDetailData?.updated_at && (
                    <span className="text-xs text-muted-foreground self-center">
                      Last synced: {formatDT(simDetailData.updated_at)}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleSimDetailClose()}>
                    Close
                  </Button>
                  {selectedProviderForSimCards?.provider_type !== 'manual' && (
                    <Button size="sm" 
                      onClick={handleSaveStatusChange}
                      disabled={updatingStatus || !simDetailDirty}
                      className="bg-[#2563eb] hover:bg-[#1d4ed8]"
                    >
                      {updatingStatus ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                      ) : (
                        <><Save className="w-4 h-4 mr-2" />Save Changes</>
                      )}
                    </Button>
                  )}
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          {/* Import ICCIDs Dialog */}
          <Dialog open={isImportICCIDsOpen} onOpenChange={(open) => { if (!open && importStep !== 'importing') { setIsImportICCIDsOpen(false); } }}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Import ICCID List</DialogTitle>
                <DialogDescription>
                  Paste or upload a list of ICCIDs to sync missing SIM cards from M2M One.
                </DialogDescription>
              </DialogHeader>
              
              {importStep === 'input' && (
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label>ICCID List (one per line)</Label>
                    <Textarea
                      placeholder="89610185002468134429&#10;89610185002468134596&#10;89610185002363360730&#10;..."
                      value={importICCIDText}
                      onChange={(e) => setImportICCIDText(e.target.value)}
                      rows={10}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      {parseICCIDs(importICCIDText).length} valid ICCIDs detected (18-22 digit numbers, duplicates removed)
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Label htmlFor="iccid-file-upload" className="cursor-pointer">
                      <div className="flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-muted text-sm">
                        <Upload className="w-4 h-4" />
                        Upload .txt File
                      </div>
                    </Label>
                    <input
                      id="iccid-file-upload"
                      type="file"
                      accept=".txt,.csv"
                      onChange={handleICCIDFileUpload}
                      className="hidden"
                    />
                    <span className="text-xs text-muted-foreground">Supports .txt and .csv files. You can upload multiple files — content is appended.</span>
                  </div>
                </div>
              )}

              {importStep === 'preview' && importStats && (
                <div className="space-y-4 py-2">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950 border border-blue-200 text-center">
                      <p className="text-2xl font-bold text-blue-600">{importStats.total_submitted}</p>
                      <p className="text-xs text-muted-foreground">Total Submitted</p>
                    </div>
                    <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 text-center">
                      <p className="text-2xl font-bold text-green-600">{importStats.already_synced}</p>
                      <p className="text-xs text-muted-foreground">Already Synced</p>
                    </div>
                    <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-200 text-center">
                      <p className="text-2xl font-bold text-amber-600">{importStats.missing}</p>
                      <p className="text-xs text-muted-foreground">Missing (To Import)</p>
                    </div>
                  </div>
                  {importStats.missing === 0 && (
                    <div className="p-3 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200">
                      <p className="text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" /> All ICCIDs are already synced. Nothing to import.
                      </p>
                    </div>
                  )}
                  {importStats.missing > 0 && (
                    <div className="p-3 bg-amber-50 dark:bg-amber-950 rounded-lg border border-amber-200">
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        <strong>{importStats.missing}</strong> SIM cards will be fetched from M2M One and added to the database. 
                        This may take approximately <strong>{Math.ceil(importStats.missing * 0.4 / 60)} minute(s)</strong>.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {importStep === 'importing' && (
                <div className="space-y-4 py-4">
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                    <p className="font-medium">Importing SIM cards from M2M One...</p>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3">
                    <div
                      className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${importProgress.total > 0 ? (importProgress.current / importProgress.total) * 100 : 0}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>{importProgress.current} / {importProgress.total} processed</span>
                    <span>{importProgress.synced} synced, {importProgress.failed} failed</span>
                  </div>
                </div>
              )}

              {importStep === 'done' && (
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950 border border-blue-200 text-center">
                      <p className="text-2xl font-bold text-blue-600">{importProgress.total}</p>
                      <p className="text-xs text-muted-foreground">Total Processed</p>
                    </div>
                    <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 text-center">
                      <p className="text-2xl font-bold text-green-600">{importProgress.synced}</p>
                      <p className="text-xs text-muted-foreground">Successfully Synced</p>
                    </div>
                    <div className="p-4 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 text-center">
                      <p className="text-2xl font-bold text-red-600">{importProgress.failed}</p>
                      <p className="text-xs text-muted-foreground">Failed</p>
                    </div>
                  </div>
                  <div className="p-3 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200">
                    <p className="text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" /> Import complete! The SIM card list has been refreshed.
                    </p>
                  </div>
                </div>
              )}

              <DialogFooter>
                {importStep === 'input' && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setIsImportICCIDsOpen(false)}>Cancel</Button>
                    <Button size="sm" 
                      className="bg-[#2563eb] hover:bg-[#1d4ed8]"
                      onClick={handleCheckMissingICCIDs}
                      disabled={saving || parseICCIDs(importICCIDText).length === 0}
                    >
                      {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking...</> : 'Check & Preview'}
                    </Button>
                  </>
                )}
                {importStep === 'preview' && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setImportStep('input')}>Back</Button>
                    {importStats?.missing > 0 && (
                      <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8]" onClick={handleStartImport}>
                        Start Import ({importStats.missing} SIMs)
                      </Button>
                    )}
                  </>
                )}
                {importStep === 'done' && (
                  <Button size="sm" variant="outline" onClick={() => setIsImportICCIDsOpen(false)}>Close</Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Add SIM Card Dialog */}
          <Dialog open={isAddSimCardOpen} onOpenChange={(open) => {
            if (!open && simCardFormHasChanges()) { setShowSimCardUnsavedWarning(true); return; }
            setIsAddSimCardOpen(open);
            if (!open) { setEditingSimCard(null); }
          }}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingSimCard ? 'Edit' : 'Add'} SIM Card {editingSimCard ? '' : `to ${selectedProviderForSimCards?.name}`}</DialogTitle>
                <DialogDescription>
                  Enter the SIM card details to add it to this provider&apos;s inventory.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>ICCID *</Label>
                    <Input
                      value={simCardForm.iccid}
                      onChange={(e) => setSimCardForm({...simCardForm, iccid: e.target.value})}
                      placeholder="89XXXXXXXXXXXXXXXXXX"
                      maxLength={22}
                    />
                    <p className="text-xs text-muted-foreground">Integrated Circuit Card Identifier (19-22 digits)</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Mobile Number</Label>
                    <Input
                      value={simCardForm.mobile_number}
                      onChange={(e) => setSimCardForm({...simCardForm, mobile_number: e.target.value})}
                      placeholder="+61 4XX XXX XXX"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>IMSI</Label>
                    <Input
                      value={simCardForm.imsi}
                      onChange={(e) => setSimCardForm({...simCardForm, imsi: e.target.value})}
                      placeholder="505XXXXXXXXXXXX"
                      maxLength={15}
                    />
                    <p className="text-xs text-muted-foreground">International Mobile Subscriber Identity</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>IMEI No.</Label>
                    <Input
                      value={simCardForm.imei || ''}
                      onChange={(e) => setSimCardForm({...simCardForm, imei: e.target.value})}
                      placeholder="861234567890123"
                      className="font-mono"
                      maxLength={16}
                    />
                    <p className="text-xs text-muted-foreground">Device IMEI linked to this SIM</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select 
                      value={simCardForm.status} 
                      onValueChange={(v) => { setSimCardForm({...simCardForm, status: v}); }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="activated">Activated</SelectItem>
                        <SelectItem value="deactivated">Deactivated</SelectItem>
                        <SelectItem value="pre-active">Pre-Active</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                        <SelectItem value="terminated">Terminated</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Data Plan</Label>
                    <Input
                      value={simCardForm.plan_name}
                      onChange={(e) => setSimCardForm({...simCardForm, plan_name: e.target.value})}
                      placeholder="e.g., 5GB IoT Plan"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Plan Type</Label>
                    <Select
                      value={simCardForm.plan_type || 'postpaid'}
                      onValueChange={(v) => { setSimCardForm({...simCardForm, plan_type: v}); }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="prepaid">Prepaid</SelectItem>
                        <SelectItem value="postpaid">Postpaid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Billing Cycle</Label>
                    <Select
                      value={simCardForm.billing_cycle || 'monthly'}
                      onValueChange={(v) => { setSimCardForm({...simCardForm, billing_cycle: v}); }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Cost per {simCardForm.billing_cycle === 'yearly' ? 'Year' : 'Month'} ($)</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={simCardForm.cost || ''}
                      onChange={(e) => setSimCardForm({...simCardForm, cost: e.target.value.replace(/[^0-9.]/g, '')})}
                      placeholder="9.99"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data Limit ({simCardForm.data_unit || 'MB'}) — {simCardForm.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'}</Label>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={simCardForm.data_limit_mb || ''}
                        onChange={(e) => setSimCardForm({...simCardForm, data_limit_mb: e.target.value.replace(/[^0-9]/g, '')})}
                        placeholder="5120"
                        className="flex-1"
                      />
                      <Select
                        value={simCardForm.data_unit || 'MB'}
                        onValueChange={(v) => { setSimCardForm({...simCardForm, data_unit: v}); }}
                      >
                        <SelectTrigger className="w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="MB">MB</SelectItem>
                          <SelectItem value="GB">GB</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={simCardForm.notes}
                    onChange={(e) => setSimCardForm({...simCardForm, notes: e.target.value})}
                    placeholder="Additional notes about this SIM card..."
                    rows={3}
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button size="sm" variant="outline" onClick={() => {
                  if (simCardFormHasChanges()) { setShowSimCardUnsavedWarning(true); return; }
                  setIsAddSimCardOpen(false); setEditingSimCard(null);
                }}>Cancel</Button>
                <Button size="sm" onClick={handleAddSimCard} disabled={saving} className="bg-[#2563eb] hover:bg-[#1d4ed8]">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : editingSimCard ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  {editingSimCard ? 'Save Changes' : 'Add SIM Card'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* ==================== ACCOUNTING TAB ==================== */}
      {activeTab === 'accounting' && (
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Accounting Integration
            </h2>
            <p className="text-sm text-muted-foreground mt-1">Connect to accounting software for automated invoicing</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Accounting Software</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Provider</Label>
                  <Select
                    value={accountingConfig.provider}
                    onValueChange={(v) => setAccountingConfig(prev => ({ ...prev, provider: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="xero">Xero</SelectItem>
                      <SelectItem value="quickbooks">QuickBooks</SelectItem>
                      <SelectItem value="myob">MYOB</SelectItem>
                      <SelectItem value="sage">Sage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>API Key / OAuth Token</Label>
                  <Input
                    type="password"
                    value={accountingConfig.api_key}
                    onChange={(e) => setAccountingConfig(prev => ({ ...prev, api_key: e.target.value }))}
                    placeholder="Enter credentials"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div>
                  <p className="font-medium text-sm">Enable Automatic Sync</p>
                  <p className="text-xs text-muted-foreground">Sync invoices and charges automatically</p>
                </div>
                <Switch
                  checked={accountingConfig.sync_enabled}
                  onCheckedChange={(v) => setAccountingConfig(prev => ({ ...prev, sync_enabled: v }))}
                />
              </div>

              <div className="flex justify-end">
                <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8]">
                  <Save className="w-4 h-4 mr-2" />
                  Save Accounting Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ==================== MAINTENANCE TAB ==================== */}
      {activeTab === 'maintenance' && (
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Wrench className="w-5 h-5" />
              Platform Maintenance
            </h2>
            <p className="text-sm text-muted-foreground mt-1">Control system maintenance mode and monitoring</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Maintenance Mode</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border-2 border-amber-200 bg-amber-50 dark:bg-amber-950/20">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                  <div>
                    <p className="font-semibold text-amber-900 dark:text-amber-400">Enable Maintenance Mode</p>
                    <p className="text-sm text-amber-700 dark:text-amber-500">Platform will be unavailable to all users except admins</p>
                  </div>
                </div>
                <Switch
                  checked={maintenanceMode.enabled}
                  onCheckedChange={(v) => setMaintenanceMode(prev => ({ ...prev, enabled: v }))}
                />
              </div>

              {maintenanceMode.enabled && (
                <>
                  <div className="space-y-2">
                    <Label>Maintenance Message</Label>
                    <Textarea
                      value={maintenanceMode.message}
                      onChange={(e) => setMaintenanceMode(prev => ({ ...prev, message: e.target.value }))}
                      placeholder="System is under maintenance. We'll be back soon."
                      rows={3}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <div>
                      <p className="font-medium text-sm">Allow Admin Access</p>
                      <p className="text-xs text-muted-foreground">Admins can access platform during maintenance</p>
                    </div>
                    <Switch
                      checked={maintenanceMode.allow_admin_access}
                      onCheckedChange={(v) => setMaintenanceMode(prev => ({ ...prev, allow_admin_access: v }))}
                    />
                  </div>
                </>
              )}

              <div className="flex justify-end">
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700">
                  <Save className="w-4 h-4 mr-2" />
                  {maintenanceMode.enabled ? 'Update Maintenance Mode' : 'Save Settings'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Live Tracking Polling Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <RefreshCw className="w-5 h-5" />
                Live Tracking Polling
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${pollingConfig.enabled ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-slate-200 dark:bg-slate-700'}`}>
                    <RefreshCw className={`w-5 h-5 ${pollingConfig.enabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">HTTP Polling Fallback</p>
                    <p className="text-xs text-muted-foreground">
                      {pollingConfig.enabled
                        ? `Fallback active — polls every ${pollingConfig.interval_seconds}s only when WebSocket is disconnected`
                        : 'Disabled — Live Tracking relies entirely on WebSocket for real-time updates'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={pollingConfig.enabled}
                  onCheckedChange={(v) => setPollingConfig(prev => ({ ...prev, enabled: v }))}
                  data-testid="polling-enabled-toggle"
                />
              </div>

              {pollingConfig.enabled && (
                <div className="space-y-2 pl-1">
                  <Label>Polling Interval (seconds)</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      min={10}
                      max={300}
                      value={pollingConfig.interval_seconds}
                      onChange={(e) => setPollingConfig(prev => ({ ...prev, interval_seconds: parseInt(e.target.value) || 60 }))}
                      className="w-28"
                      data-testid="polling-interval-input"
                    />
                    <span className="text-xs text-muted-foreground">Min: 10s &middot; Max: 300s</span>
                  </div>
                </div>
              )}

              <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-300">
                <strong>Note:</strong> WebSocket is the primary real-time data channel. When enabled, polling only activates as a fallback 
                when WebSocket disconnects — it stops automatically once WebSocket reconnects. No extra server load while WebSocket is healthy.
              </div>

              <div className="flex justify-end">
                <Button size="sm"
                  onClick={handleSavePollingConfig}
                  disabled={savingPolling}
                  className="bg-[#2563eb] hover:bg-[#1d4ed8]"
                  data-testid="save-polling-btn"
                >
                  {savingPolling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Polling Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Platform Health — Live Data */}
          <SystemHealth />
        </div>
      )}

      {/* ==================== DATA RETENTION TAB ==================== */}
      {activeTab === 'retention' && (
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Database className="w-5 h-5" />
              Data Retention & Backup
            </h2>
            <p className="text-sm text-muted-foreground mt-1">Configure data retention policies and backup settings</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Data Retention Policies</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Operational History (Days)</Label>
                  <NumberInput
                    value={retentionPolicies.operational_history_days}
                    onValueChange={(n) => setRetentionPolicies(prev => ({ ...prev, operational_history_days: n }))}
                    data-testid="retention-operational-history-input"
                  />
                  <p className="text-xs text-muted-foreground">Applies to GPS History, Events & Alerts, and Command Logs stored by the platform.</p>
                  {renderPurgeNote('operational')}
                </div>

                <div className="space-y-2">
                  <Label>Video Metadata (Days)</Label>
                  <NumberInput
                    value={retentionPolicies.video_metadata_days}
                    onValueChange={(n) => setRetentionPolicies(prev => ({ ...prev, video_metadata_days: n }))}
                  />
                  {renderPurgeNote('video')}
                </div>

                <div className="space-y-2">
                  <Label>Chat Messages (Days)</Label>
                  <NumberInput
                    value={retentionPolicies.chat_messages_days}
                    onValueChange={(n) => setRetentionPolicies(prev => ({ ...prev, chat_messages_days: n }))}
                    data-testid="retention-chat-messages-input"
                  />
                  <p className="text-xs text-muted-foreground">Managed by the storage lifecycle policy.</p>
                </div>

                <div className="space-y-2">
                  <Label>Audit Logs (Days)</Label>
                  <NumberInput
                    value={retentionPolicies.audit_logs_days}
                    onValueChange={(n) => setRetentionPolicies(prev => ({ ...prev, audit_logs_days: n }))}
                  />
                  {renderPurgeNote('audit')}
                </div>

                <div className="space-y-2">
                  <Label>Webhook Delivery Logs (Days)</Label>
                  <NumberInput
                    min="1"
                    value={retentionPolicies.webhook_logs_days}
                    onValueChange={(n) => setRetentionPolicies(prev => ({ ...prev, webhook_logs_days: n }))}
                    emptyValue={1}
                    data-testid="retention-webhook-logs-input"
                  />
                  <p className="text-xs text-muted-foreground">Event &amp; Alert webhook delivery history is purged after this many days. Applied immediately on save.</p>
                  {renderPurgeNote('webhook', 'logs')}
                </div>

                <div className="space-y-2">
                  <Label>Idle Session Expiry (Days)</Label>
                  <NumberInput
                    min="1"
                    value={retentionPolicies.idle_session_days}
                    onValueChange={(n) => setRetentionPolicies(prev => ({ ...prev, idle_session_days: n }))}
                    emptyValue={30}
                    data-testid="retention-idle-session-input"
                  />
                  <p className="text-xs text-muted-foreground">Signed-in device sessions with no activity for this many days are automatically signed out by a daily cleanup sweep.</p>
                  {renderPurgeNote('sessions', 'sessions')}
                </div>
              </div>

              <div className="flex justify-between items-center">
                <Button size="sm" variant="outline" onClick={openPurgeHistory} data-testid="retention-history-button">
                  <History className="w-4 h-4 mr-2" />
                  View cleanup history
                </Button>
                <Button size="sm" onClick={handleSaveRetentionPolicies} disabled={saving} className="bg-[#2563eb] hover:bg-[#1d4ed8]">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Retention Policies
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Session Audit — when a silent session counts as ended */}
          <Card data-testid="session-audit-settings-card">
            <CardHeader>
              <CardTitle className="text-base">Session Audit</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                <div className="space-y-2">
                  <Label>Inactivity Window (Minutes)</Label>
                  <NumberInput
                    min="15"
                    max="120"
                    value={sessionAudit.inactivity_minutes}
                    onValueChange={(n) => setSessionAudit((p) => ({ ...p, inactivity_minutes: n }))}
                    emptyValue={30}
                    data-testid="session-audit-window-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    A session silent for this long is audited as &ldquo;Session Ended (Inactive)&rdquo; with its
                    last-active time. 15&ndash;120 minutes, default 30. The app pings every 3 minutes while open.
                  </p>
                </div>
                <div className="flex justify-end">
                  <Button size="sm" onClick={handleSaveSessionAudit} disabled={sessionAuditSaving} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="session-audit-save-button">
                    {sessionAuditSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Session Audit Settings
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Audit Alerts — instant security alerts for sensitive account actions */}
          <Card data-testid="audit-alerts-settings-card">
            <CardHeader>
              <CardTitle className="text-base">Audit Alerts</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label>Send security alerts</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Instantly notify Super Admins and Admins holding the &ldquo;Audit Alerts&rdquo; privilege when a
                    sensitive action happens. Bell alerts deep-link to the Audit Log. Email requires a configured SMTP provider.
                  </p>
                </div>
                <Switch
                  checked={auditAlerts.enabled}
                  onCheckedChange={(v) => setAuditAlerts((p) => ({ ...p, enabled: v }))}
                  data-testid="audit-alerts-enabled-switch"
                />
              </div>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label>Four-eyes mode</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Also alert the acting admin about their own actions. Off = self-exclusion
                    (you are not alerted about your own admin work).
                  </p>
                </div>
                <Switch
                  checked={auditAlerts.four_eyes}
                  onCheckedChange={(v) => setAuditAlerts((p) => ({ ...p, four_eyes: v }))}
                  disabled={!auditAlerts.enabled}
                  data-testid="audit-alerts-four-eyes-switch"
                />
              </div>
              <div className={auditAlerts.enabled ? 'overflow-x-auto' : 'overflow-x-auto opacity-50 pointer-events-none'}>
                <table className="w-full text-sm" data-testid="audit-alerts-matrix">
                  <thead>
                    <tr className="text-xs text-muted-foreground border-b">
                      <th className="text-left py-2 pr-2 font-medium">Alert type</th>
                      <th className="text-center py-2 px-2 font-medium">Bell</th>
                      <th className="text-center py-2 px-2 font-medium">Email</th>
                      <th className="text-center py-2 px-2 font-medium">Push</th>
                      <th className="text-center py-2 px-2 font-medium">Notify affected user</th>
                    </tr>
                  </thead>
                  <tbody>
                    {AUDIT_ALERT_ROWS.map((row) => (
                      <tr key={row.id} className="border-b last:border-0">
                        <td className="py-2.5 pr-2">
                          <p className="font-medium">{row.label}</p>
                          <p className="text-xs text-muted-foreground">{row.hint}</p>
                        </td>
                        {['bell', 'email', 'push', 'notify_affected'].map((ch) => (
                          <td key={ch} className="text-center py-2.5 px-2">
                            {ch === 'notify_affected' && row.noAffected ? (
                              <span className="text-xs text-muted-foreground" title="Not applicable — no affected user for this alert">—</span>
                            ) : (
                              <Switch
                                checked={!!auditAlerts.types?.[row.id]?.[ch]}
                                onCheckedChange={(v) => setAuditAlerts((p) => ({
                                  ...p,
                                  types: { ...p.types, [row.id]: { ...p.types?.[row.id], [ch]: v } },
                                }))}
                                data-testid={`audit-alert-${row.id}-${ch}`}
                              />
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end">
                <Button size="sm" onClick={handleSaveAuditAlerts} disabled={auditAlertsSaving} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="audit-alerts-save-button">
                  {auditAlertsSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Audit Alert Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Audit Archive — compliance exports that survive the audit purge */}
          <Card data-testid="audit-archive-settings-card">
            <CardHeader>
              <CardTitle className="text-base">Audit Archive (Compliance)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Archive audit logs monthly</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    On the 1st of each month the previous month&apos;s audit entries are written to a permanent archive file,
                    so compliance records survive the &ldquo;Audit Logs (Days)&rdquo; purge above. Archives are listed on the
                    Admin Panel &rarr; Audit Log page (requires the Audit Archives privilege).
                  </p>
                </div>
                <Switch
                  checked={auditArchive.enabled}
                  onCheckedChange={(v) => setAuditArchive((p) => ({ ...p, enabled: v }))}
                  data-testid="audit-archive-enabled-switch"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Archive Format</Label>
                  <Select
                    value={auditArchive.formats?.length === 1 ? auditArchive.formats[0] : 'both'}
                    onValueChange={(v) => setAuditArchive((p) => ({ ...p, formats: v === 'both' ? ['csv', 'json'] : [v] }))}
                  >
                    <SelectTrigger data-testid="audit-archive-format-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="both">CSV + JSON</SelectItem>
                      <SelectItem value="csv">CSV only</SelectItem>
                      <SelectItem value="json">JSON only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Keep Archives For (Years)</Label>
                  <NumberInput
                    min="0"
                    value={auditArchive.retention_years}
                    onValueChange={(n) => setAuditArchive((p) => ({ ...p, retention_years: n }))}
                    emptyValue={0}
                    data-testid="audit-archive-retention-input"
                  />
                  <p className="text-xs text-muted-foreground">0 = keep forever (recommended for compliance).</p>
                </div>
              </div>
              <div className="flex justify-end">
                <Button size="sm" onClick={handleSaveAuditArchive} disabled={auditArchiveSaving} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="audit-archive-save-button">
                  {auditArchiveSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Audit Archive Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          <Dialog open={purgeHistoryOpen} onOpenChange={setPurgeHistoryOpen}>
            <DialogContent className="max-w-2xl" data-testid="retention-history-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <History className="w-5 h-5" /> Retention Cleanup History
                </DialogTitle>
                <DialogDescription>
                  Past cleanup runs that removed data, newest first. Empty no-op cycles are not listed.
                </DialogDescription>
              </DialogHeader>
              <div className="max-h-[60vh] overflow-y-auto">
                {purgeHistoryLoading ? (
                  <div className="flex items-center justify-center py-10 text-muted-foreground">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                  </div>
                ) : purgeHistory.length === 0 ? (
                  <div className="py-10 text-center text-sm text-muted-foreground" data-testid="retention-history-empty">
                    No cleanup runs have removed data yet.
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Policy</TableHead>
                        <TableHead className="text-right">Removed</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {purgeHistory.map((row, i) => {
                        let when = row.at;
                        try { when = formatDT(row.at, { seconds: true }); } catch { /* raw */ }
                        return (
                          <TableRow key={row.id || i} data-testid={`retention-history-row-${i}`}>
                            <TableCell className="whitespace-nowrap">{when}</TableCell>
                            <TableCell>{PURGE_CATEGORY_LABELS[row.category] || row.category}</TableCell>
                            <TableCell className="text-right font-medium">{row.count.toLocaleString()}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Backup Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Backup & Disaster Recovery</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Database Backup Schedule</Label>
                  <Select defaultValue="daily">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hourly">Hourly</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Backup Retention (Days)</Label>
                  <Input type="number" defaultValue="30" />
                </div>

                <div className="space-y-2">
                  <Label>RPO (Recovery Point Objective)</Label>
                  <Input defaultValue="1 hour" disabled />
                </div>

                <div className="space-y-2">
                  <Label>RTO (Recovery Time Objective)</Label>
                  <Input defaultValue="4 hours" disabled />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm">
                  Test Restore
                </Button>
                <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8]">
                  <Save className="w-4 h-4 mr-2" />
                  Save Backup Configuration
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ==================== SECURITY TAB ==================== */}
      {activeTab === 'security' && (
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Security & Governance
            </h2>
            <p className="text-sm text-muted-foreground mt-1">Configure platform-wide security policies</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Authentication & Access</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div>
                  <p className="font-medium text-sm">Enforce MFA for Admins</p>
                  <p className="text-xs text-muted-foreground">Require multi-factor authentication</p>
                </div>
                <Switch
                  checked={securityConfig.mfa_enforced}
                  onCheckedChange={(v) => setSecurityConfig(prev => ({ ...prev, mfa_enforced: v }))}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Session Timeout (minutes)</Label>
                  <Input
                    type="number"
                    value={securityConfig.session_timeout}
                    onChange={(e) => setSecurityConfig(prev => ({ ...prev, session_timeout: parseInt(e.target.value) }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Minimum Password Length</Label>
                  <Input
                    type="number"
                    value={securityConfig.password_min_length}
                    onChange={(e) => setSecurityConfig(prev => ({ ...prev, password_min_length: parseInt(e.target.value) }))}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div>
                  <p className="font-medium text-sm">IP Whitelist</p>
                  <p className="text-xs text-muted-foreground">Restrict admin access to specific IPs</p>
                </div>
                <Switch
                  checked={securityConfig.ip_whitelist_enabled}
                  onCheckedChange={(v) => setSecurityConfig(prev => ({ ...prev, ip_whitelist_enabled: v }))}
                />
              </div>

              <div className="flex justify-end">
                <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8]">
                  <Save className="w-4 h-4 mr-2" />
                  Save Security Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* TCP Server Security - Blacklist & Whitelist Management */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">TCP Server Security</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">Manage IP blacklists and view auto-whitelisted device IPs</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchTcpSecurity}
                disabled={loadingTcpSecurity}
                data-testid="refresh-tcp-security"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${loadingTcpSecurity ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Protocol Stats Summary */}
              {tcpSecurityData && (
                <div data-testid="tcp-security-stats">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {Object.entries(tcpSecurityData).map(([protocol, stats]) => (
                      <div key={protocol} className="p-3 rounded-lg border bg-slate-50 dark:bg-slate-800/50">
                        <p className="font-medium text-sm capitalize mb-2">{protocol.replace('_', ' ')}</p>
                        <div className="grid grid-cols-2 gap-1 text-xs">
                          <span className="text-muted-foreground">Accepted:</span>
                          <span className="text-green-600 font-medium">{stats.total_accepted}</span>
                          <span className="text-muted-foreground">Rejected:</span>
                          <span className="text-red-600 font-medium">{stats.total_rejected}</span>
                          <span className="text-muted-foreground">Blacklisted:</span>
                          <span className="text-orange-600 font-medium">{stats.currently_blacklisted}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                    {trustedIpCount} trusted device IP{trustedIpCount !== 1 ? 's' : ''} (persistent, auto-cleaned after 30 days)
                  </div>
                </div>
              )}

              {/* Blacklisted IPs */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium flex items-center gap-1.5">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    Blacklisted IPs
                  </h4>
                  {Object.values(blacklistedIps).some(arr => arr.length > 0) && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleClearAllBlacklists}
                      data-testid="clear-all-blacklists"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      Clear All
                    </Button>
                  )}
                </div>
                {Object.values(blacklistedIps).every(arr => arr.length === 0) ? (
                  <p className="text-xs text-muted-foreground p-3 rounded-lg border text-center">No IPs currently blacklisted</p>
                ) : (
                  <div className="rounded-md border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">IP Address</TableHead>
                          <TableHead className="text-xs">Protocol</TableHead>
                          <TableHead className="text-xs">Failures</TableHead>
                          <TableHead className="text-xs">Remaining</TableHead>
                          <TableHead className="text-xs w-[80px]">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(blacklistedIps).flatMap(([protocol, ips]) =>
                          ips.map((item) => (
                            <TableRow key={`${protocol}-${item.ip}`}>
                              <TableCell className="text-xs font-mono">{item.ip}</TableCell>
                              <TableCell className="text-xs capitalize">{protocol.replace('_', ' ')}</TableCell>
                              <TableCell className="text-xs">{item.failure_count}</TableCell>
                              <TableCell className="text-xs">
                                <Badge variant="destructive" className="text-xs">
                                  {Math.ceil(item.remaining_seconds / 60)}m
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-xs text-red-600 hover:text-red-700"
                                  onClick={() => handleClearBlacklistIp(item.ip)}
                                  data-testid={`clear-blacklist-${item.ip}`}
                                >
                                  <X className="w-3.5 h-3.5 mr-1" />
                                  Clear
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              {/* Unregistered Devices */}
              {unregisteredDevices.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium flex items-center gap-1.5 mb-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    Unregistered Devices ({unregisteredDevices.length})
                  </h4>
                  <p className="text-xs text-muted-foreground mb-2">Devices that connected with a valid protocol handshake but their IMEI is not registered in the system.</p>
                  <div className="rounded-md border overflow-hidden max-h-[250px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">IMEI</TableHead>
                          <TableHead className="text-xs">Protocol</TableHead>
                          <TableHead className="text-xs">IP Address</TableHead>
                          <TableHead className="text-xs">Connections</TableHead>
                          <TableHead className="text-xs">First Seen</TableHead>
                          <TableHead className="text-xs">Last Seen</TableHead>
                          <TableHead className="text-xs w-[60px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {unregisteredDevices.map((dev) => (
                          <TableRow key={dev.imei}>
                            <TableCell className="text-xs font-mono font-medium">{dev.imei}</TableCell>
                            <TableCell className="text-xs"><Badge variant="outline" className="text-xs">{dev.protocol}</Badge></TableCell>
                            <TableCell className="text-xs font-mono">{dev.ip || '-'}</TableCell>
                            <TableCell className="text-xs">{dev.connection_count || 1}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{dev.first_seen ? formatDT(dev.first_seen) : '-'}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{dev.last_seen ? formatDT(dev.last_seen) : '-'}</TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={async () => {
                                try {
                                  await api.delete(`/parsing/security/unregistered-devices/${encodeURIComponent(dev.imei)}`);
                                  setUnregisteredDevices(prev => prev.filter(d => d.imei !== dev.imei));
                                  toast.success(`Removed ${dev.imei}`);
                                } catch { toast.error('Failed to remove'); }
                              }}><XCircle className="w-3.5 h-3.5" /></Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <Button variant="outline" size="sm" className="mt-2 text-xs" onClick={async () => {
                    try {
                      await api.delete('/parsing/security/unregistered-devices');
                      setUnregisteredDevices([]);
                      toast.success('All unregistered devices cleared');
                    } catch { toast.error('Failed'); }
                  }}>Clear All</Button>
                </div>
              )}

              {/* Recent Security Events */}
              {securityEvents.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium flex items-center gap-1.5 mb-2">
                    <Clock className="w-4 h-4 text-slate-500" />
                    Recent Security Events
                  </h4>
                  <div className="rounded-md border overflow-hidden max-h-[200px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Type</TableHead>
                          <TableHead className="text-xs">IP</TableHead>
                          <TableHead className="text-xs">Details</TableHead>
                          <TableHead className="text-xs">Time</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {securityEvents.map((evt, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="text-xs">
                              <Badge
                                variant={evt.type === 'ip_blacklisted' ? 'destructive' : 'outline'}
                                className="text-xs"
                              >
                                {evt.type?.replace(/_/g, ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs font-mono">{evt.ip || '-'}</TableCell>
                            <TableCell className="text-xs">
                              {evt.failure_count ? `${evt.failure_count} failures` : ''}
                              {evt.duration_seconds ? ` / ${Math.ceil(evt.duration_seconds / 60)}m ban` : ''}
                              {evt.cleared_protocols ? `Protocols: ${evt.cleared_protocols.join(', ')}` : ''}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {evt.timestamp ? formatDT(new Date(evt.timestamp * 1000).toISOString()) : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Trusted Device IPs (persistent, from MongoDB) */}
              <div>
                <h4 className="text-sm font-medium flex items-center gap-1.5 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Trusted Device IPs
                  <span className="text-xs text-muted-foreground font-normal">(persisted after successful IMEI authentication, auto-cleaned after 30 days)</span>
                </h4>

                {/* Manual Add IP Form */}
                <div className="flex items-end gap-2 mb-3 p-3 rounded-lg border bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">IP Address</Label>
                    <Input
                      value={manualTrustIp}
                      onChange={(e) => setManualTrustIp(e.target.value)}
                      placeholder="e.g. 203.45.67.89"
                      className="h-8 text-xs font-mono"
                      data-testid="manual-trust-ip-input"
                    />
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">Note (optional)</Label>
                    <Input
                      value={manualTrustNote}
                      onChange={(e) => setManualTrustNote(e.target.value)}
                      placeholder="e.g. Office gateway, SIM provider"
                      className="h-8 text-xs"
                    />
                  </div>
                  <Button
                    size="sm"
                    className="h-8 bg-green-600 hover:bg-green-700"
                    onClick={handleManualTrustIp}
                    data-testid="manual-trust-ip-btn"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" />
                    Add
                  </Button>
                </div>

                {trustedIps.length === 0 ? (
                  <p className="text-xs text-muted-foreground p-3 rounded-lg border text-center">No trusted IPs yet — IPs are added when devices authenticate</p>
                ) : (
                  <div>
                    <div className="rounded-md border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">IP Address</TableHead>
                            <TableHead className="text-xs">Source</TableHead>
                            <TableHead className="text-xs">Device IMEI / Note</TableHead>
                            <TableHead className="text-xs">First Seen</TableHead>
                            <TableHead className="text-xs">Last Seen</TableHead>
                            <TableHead className="text-xs w-[60px]">Action</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {trustedIps.map((item) => (
                          <TableRow key={item.ip}>
                            <TableCell className="text-xs font-mono">{item.ip}</TableCell>
                            <TableCell className="text-xs">
                              <Badge variant="outline" className={`text-xs ${item.source === 'manual' ? 'border-amber-300 text-amber-600' : 'border-green-200 text-green-600'}`}>
                                {item.source === 'manual' ? 'Manual' : (item.protocol || '-').replace('_', ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs font-mono">{item.source === 'manual' ? (item.note || '-') : (item.imei || '-')}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {item.first_seen ? formatDateOnly(item.first_seen) : '-'}
                            </TableCell>
                            <TableCell className="text-xs">
                              {item.last_seen ? formatDT(item.last_seen) : '-'}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => handleRemoveTrustedIp(item.ip)}
                                data-testid={`remove-trusted-${item.ip}`}
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-500" />
                              </Button>
                            </TableCell>
                          </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <PaginationFooter
                      page={trustedIpsPage}
                      setPage={setTrustedIpsPage}
                      totalItems={trustedIpsTotal}
                      itemsPerPage={trustedIpsPageSize}
                      setItemsPerPage={setTrustedIpsPageSize}
                      testIdPrefix="trusted-ips"
                    />
                  </div>
                )}
              </div>

            </CardContent>
          </Card>
        </div>
      )}

      {/* SIM Detail Unsaved Warning */}
      <Dialog open={showSimUnsavedWarning} onOpenChange={setShowSimUnsavedWarning}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" />Unsaved Changes</DialogTitle>
            <DialogDescription>You have unsaved changes. Are you sure you want to leave? Your changes will be lost.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowSimUnsavedWarning(false)}>Stay</Button>
            <Button size="sm" variant="destructive" onClick={() => handleSimDetailClose(true)}>Discard Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SIM Card Edit Unsaved Warning */}
      <Dialog open={showSimCardUnsavedWarning} onOpenChange={setShowSimCardUnsavedWarning}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" />Unsaved Changes</DialogTitle>
            <DialogDescription>You have unsaved changes. Are you sure you want to leave? Your changes will be lost.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowSimCardUnsavedWarning(false)}>Stay</Button>
            <Button size="sm" variant="destructive" onClick={() => { setShowSimCardUnsavedWarning(false); setIsAddSimCardOpen(false); setEditingSimCard(null); }}>Discard Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Global Action Confirmation Dialog */}
      <Dialog open={!!pendingGlobalAction} onOpenChange={(open) => { if (!open) setPendingGlobalAction(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className={`w-5 h-5 ${pendingGlobalAction?.variant === 'destructive' ? 'text-red-500' : 'text-amber-500'}`} />
              {pendingGlobalAction?.title}
            </DialogTitle>
            <DialogDescription>
              {pendingGlobalAction?.message}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button size="sm" variant="outline" onClick={() => setPendingGlobalAction(null)}>
              Cancel
            </Button>
            <Button size="sm"
              variant={pendingGlobalAction?.variant === 'destructive' ? 'destructive' : 'default'}
              className={pendingGlobalAction?.variant !== 'destructive' ? 'bg-[#2563eb] hover:bg-[#1d4ed8]' : ''}
              onClick={async () => { await pendingGlobalAction?.action(); setPendingGlobalAction(null); }}
            >
              {pendingGlobalAction?.confirmLabel || 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Email Preview Dialog */}
      <Dialog open={emailPreviewOpen} onOpenChange={setEmailPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Email Preview
            </DialogTitle>
            <DialogDescription>Preview of the test email that will be sent</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm border-b pb-3">
            <div className="flex gap-2">
              <span className="font-medium text-muted-foreground w-16">From:</span>
              <span>{emailPreviewFrom}</span>
            </div>
            <div className="flex gap-2">
              <span className="font-medium text-muted-foreground w-16">Subject:</span>
              <span>{emailPreviewSubject}</span>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto border rounded-lg bg-white">
            <div dangerouslySetInnerHTML={{ __html: emailPreviewHtml }} />
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setEmailPreviewOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
